import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  AudioLines,
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Blend,
  Captions,
  ClipboardCopy,
  ClipboardPaste,
  CopyPlus,
  Circle,
  Cloud,
  CloudUpload,
  Download,
  Expand,
  Focus,
  Grid3X3,
  ExternalLink,
  FileDown,
  FileVideo,
  Film,
  Bot,
  House,
  Image as ImageIcon,
  Info,
  Italic,
  Layers,
  Lock,
  Loader2,
  Magnet,
  Maximize2,
  Minimize2,
  MoveDown,
  MoveLeft,
  MoveRight,
  MoveUp,
  Pause,
  Play,
  Redo2,
  RotateCw,
  Save,
  Scissors,
  SlidersHorizontal,
  Sparkles,
  Sun,
  TextCursorInput,
  Trash2,
  Type,
  Underline,
  Undo2,
  Unlock,
  Upload,
  Volume2,
  VolumeX,
  Wand2,
  X,
  Zap,
  Eye,
  EyeOff,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Select } from '@/components/ui/Select';
import { Slider } from '@/components/ui/Slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Textarea } from '@/components/ui/Textarea';
import { Tooltip } from '@/components/ui/Tooltip';
import {
  studioApi,
  type StudioAsset,
  type StudioAvatarReference,
  type StudioAssetKind,
  type StudioLabMaterial,
  type StudioPanelKey,
  type StudioTimelineClip,
  type StudioTimelineLayer,
  type StudioTimelineTransition,
} from '@/lib/api';
import {
  DEFAULT_STUDIO_ASPECT_RATIO,
  STUDIO_ASPECT_RATIOS,
  normalizeStudioAspectRatio,
  studioAspectRatioNumber,
  studioAspectRatioToCss,
} from './studioAspectRatios';
import { selectStudioVideoPreloadUrls, type StudioVideoPreloadCandidate } from './studioPlayback';

const DEFAULT_LAYERS = [4, 3, 2, 1, 0, -1, -2, -3, -4];
const AUDIO_LAYERS = [-1, -2, -3, -4];
const MIN_LAYER = -4;
const MAX_LAYER = 24;
const DEFAULT_PX_PER_SECOND = 36;
const MIN_PX_PER_SECOND = 20;
const MAX_PX_PER_SECOND = 240;
const MAX_STUDIO_TEXT_DURATION_MS = 24 * 60 * 60 * 1000;
const TIMELINE_LABEL_WIDTH = 92;
const TIMELINE_LANE_HEIGHT = 54;
const BASE_TIMELINE_LANE_HEIGHT = 64;
const DEFAULT_CLIP_DURATION_MS = 5000;
const BASE_TIMELINE_DURATION_MS = 60000;
const SNAP_THRESHOLD_MS = 150;
const TIMELINE_COLLISION_TOLERANCE_MS = 2;
const CANVAS_SNAP_THRESHOLD_PX = 10;
const CROP_GUIDE_RATIOS = [
  { value: '1:1', label: '1:1' },
  { value: '4:5', label: '4:5' },
  { value: '3:4', label: '3:4' },
] as const;
type CropGuideRatio = (typeof CROP_GUIDE_RATIOS)[number]['value'];
const MIN_PLAYBACK_SPEED = 0.25;
const MAX_PLAYBACK_SPEED = 4;
const MIN_PLAYBACK_SPEED_MAGNITUDE = 0.25;
const SILENCE_MINIMUM_CUT_MS = 60;
type SilenceSensitivity = 'safe' | 'balanced' | 'strong';
const SILENCE_PRESETS: Record<SilenceSensitivity, {
  minimumPauseMs: number;
  thresholdRatio: number;
  minimumThreshold: number;
  maximumThreshold: number;
  guardAfterSpeechMs: number;
  guardBeforeSpeechMs: number;
  dilationBuckets: number;
}> = {
  safe: { minimumPauseMs: 260, thresholdRatio: 0.14, minimumThreshold: 0.014, maximumThreshold: 0.16, guardAfterSpeechMs: 140, guardBeforeSpeechMs: 280, dilationBuckets: 2 },
  balanced: { minimumPauseMs: 220, thresholdRatio: 0.18, minimumThreshold: 0.018, maximumThreshold: 0.2, guardAfterSpeechMs: 100, guardBeforeSpeechMs: 220, dilationBuckets: 1 },
  strong: { minimumPauseMs: 180, thresholdRatio: 0.22, minimumThreshold: 0.022, maximumThreshold: 0.24, guardAfterSpeechMs: 80, guardBeforeSpeechMs: 160, dilationBuckets: 1 },
};
const STUDIO_LAYOUT_SHORT_EDGE = 720;
const STUDIO_EXPORT_QUALITIES = [
  { value: '360p', label: '360p (testowa)', shortEdge: 360, videoBitsPerSecond: 2_000_000 },
  { value: '720p', label: '720p', shortEdge: 720, videoBitsPerSecond: 8_000_000 },
  { value: '1080p', label: '1080p (Full HD)', shortEdge: 1080, videoBitsPerSecond: 16_000_000 },
  { value: '2160p', label: '2160p (4K)', shortEdge: 2160, videoBitsPerSecond: 45_000_000 },
] as const;
type StudioExportQuality = typeof STUDIO_EXPORT_QUALITIES[number]['value'];
const DEFAULT_STUDIO_SETTINGS = {
  aspectRatio: DEFAULT_STUDIO_ASPECT_RATIO,
  snapEnabled: true,
  mainTrackMagnetEnabled: false,
};

const STUDIO_VIDEO_WARMUP_LIMIT = 12;
const studioVideoWarmupCache = new Map<string, {
  video: HTMLVideoElement;
  promise: Promise<boolean>;
  lastUsedAt: number;
}>();

function warmStudioVideo(url: string, timeoutMs = 8000): Promise<boolean> {
  const cached = studioVideoWarmupCache.get(url);
  if (cached) {
    cached.lastUsedAt = Date.now();
    return cached.promise;
  }
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.src = url;

  const promise = new Promise<boolean>((resolve) => {
    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('error', onError);
      if (!ready) studioVideoWarmupCache.delete(url);
      resolve(ready);
    };
    const onReady = () => finish(true);
    const onError = () => finish(false);
    const timer = window.setTimeout(() => finish(video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA), timeoutMs);
    video.addEventListener('canplay', onReady, { once: true });
    video.addEventListener('loadeddata', onReady, { once: true });
    video.addEventListener('error', onError, { once: true });
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) finish(true);
    else video.load();
  });

  studioVideoWarmupCache.set(url, { video, promise, lastUsedAt: Date.now() });
  if (studioVideoWarmupCache.size > STUDIO_VIDEO_WARMUP_LIMIT) {
    const oldest = [...studioVideoWarmupCache.entries()]
      .filter(([entryUrl]) => entryUrl !== url)
      .sort(([, a], [, b]) => a.lastUsedAt - b.lastUsedAt)[0];
    if (oldest) {
      oldest[1].video.pause();
      oldest[1].video.removeAttribute('src');
      studioVideoWarmupCache.delete(oldest[0]);
    }
  }
  return promise;
}

const STUDIO_GOOGLE_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Montserrat', 'Poppins', 'Lato', 'Oswald', 'Raleway',
  'Nunito', 'Merriweather', 'Playfair Display', 'Bebas Neue', 'Anton', 'Pacifico', 'Lobster',
];

const TRANSCRIPTION_LANGUAGES = [
  { value: 'original', label: 'Język oryginalny' },
  { value: 'pl', label: 'Polski' },
  { value: 'en', label: 'Angielski' },
  { value: 'de', label: 'Niemiecki' },
  { value: 'es', label: 'Hiszpański' },
  { value: 'fr', label: 'Francuski' },
  { value: 'it', label: 'Włoski' },
  { value: 'cs', label: 'Czeski' },
  { value: 'sk', label: 'Słowacki' },
  { value: 'uk', label: 'Ukraiński' },
  { value: 'pt', label: 'Portugalski' },
  { value: 'nl', label: 'Niderlandzki' },
  { value: 'sv', label: 'Szwedzki' },
  { value: 'da', label: 'Duński' },
  { value: 'no', label: 'Norweski' },
  { value: 'fi', label: 'Fiński' },
  { value: 'ro', label: 'Rumuński' },
  { value: 'hu', label: 'Węgierski' },
  { value: 'tr', label: 'Turecki' },
  { value: 'ar', label: 'Arabski' },
  { value: 'ja', label: 'Japoński' },
  { value: 'ko', label: 'Koreański' },
  { value: 'zh', label: 'Chiński' },
];

const STUDIO_TRANSITIONS: Array<{ type: string; name: string; description: string; icon: typeof Film }> = [
  { type: 'crossfade', name: 'Przenikanie', description: 'Płynne przenikanie obu ujęć', icon: Blend },
  { type: 'fade-black', name: 'Do czerni', description: 'Wygaszenie przez czarne tło', icon: Circle },
  { type: 'fade-white', name: 'Do bieli', description: 'Rozjaśnienie przez białe tło', icon: Sun },
  { type: 'dissolve', name: 'Rozpuszczenie', description: 'Miękkie rozpuszczenie obrazu', icon: Sparkles },
  { type: 'slide-left', name: 'Wjazd w lewo', description: 'Nowy klip wjeżdża od prawej', icon: MoveLeft },
  { type: 'slide-right', name: 'Wjazd w prawo', description: 'Nowy klip wjeżdża od lewej', icon: MoveRight },
  { type: 'slide-up', name: 'Wjazd w górę', description: 'Nowy klip wjeżdża od dołu', icon: MoveUp },
  { type: 'slide-down', name: 'Wjazd w dół', description: 'Nowy klip wjeżdża od góry', icon: MoveDown },
  { type: 'wipe-left', name: 'Kurtyna w lewo', description: 'Odsłonięcie kadru w lewo', icon: ArrowLeft },
  { type: 'wipe-right', name: 'Kurtyna w prawo', description: 'Odsłonięcie kadru w prawo', icon: ArrowRight },
  { type: 'wipe-up', name: 'Kurtyna w górę', description: 'Odsłonięcie kadru w górę', icon: ArrowUp },
  { type: 'wipe-down', name: 'Kurtyna w dół', description: 'Odsłonięcie kadru w dół', icon: ArrowDown },
  { type: 'zoom-in', name: 'Zbliżenie', description: 'Dynamiczne wejście przez zoom', icon: Maximize2 },
  { type: 'zoom-out', name: 'Oddalenie', description: 'Dynamiczne wyjście przez zoom', icon: Minimize2 },
  { type: 'spin', name: 'Obrót', description: 'Przejście z obrotem kadru', icon: RotateCw },
  { type: 'blur', name: 'Rozmycie', description: 'Zmiana ujęcia przez rozmycie', icon: Focus },
  { type: 'flash', name: 'Błysk', description: 'Krótki fotograficzny błysk', icon: Zap },
  { type: 'push-left', name: 'Pchnięcie w lewo', description: 'Pierwszy klip wypycha drugi', icon: ArrowLeft },
  { type: 'push-right', name: 'Pchnięcie w prawo', description: 'Drugi klip wypycha pierwszy', icon: ArrowRight },
  { type: 'pulse', name: 'Puls', description: 'Szybkie przejście z pulsem skali', icon: AudioLines },
];

const PANEL_ITEMS: Array<{ key: StudioPanelKey; label: string; icon: typeof Film }> = [
  { key: 'media', label: 'Pliki', icon: Film },
  { key: 'audio', label: 'Dźwięk', icon: AudioLines },
  { key: 'text', label: 'Tekst', icon: Type },
  { key: 'transitions', label: 'Przejścia', icon: Wand2 },
  { key: 'ai', label: 'AI', icon: Bot },
];

interface StudioContextMenuItem {
  id: string;
  label: string;
  icon: ReactNode;
  disabled?: boolean;
  destructive?: boolean;
  separatorBefore?: boolean;
  onClick: () => void;
}

interface ClipUpdateOptions {
  recordHistory?: boolean;
  allowCollision?: boolean;
  ignoreLayerLock?: boolean;
}

interface ClipBatchUpdate {
  clipId: string;
  patch: Partial<StudioTimelineClip>;
}

type StudioLayerCategory = 'visual' | 'text' | 'audio';

interface StudioProjectSettings extends Record<string, unknown> {
  aspectRatio: string;
  snapEnabled: boolean;
  mainTrackMagnetEnabled: boolean;
}

interface TimelineHistorySnapshot {
  durationMs: number;
  clips: StudioTimelineClip[];
  layers: StudioTimelineLayer[];
  transitions: StudioTimelineTransition[];
}

interface TimelineLocalDraft {
  savedAt: number;
  timeline: TimelineHistorySnapshot;
}

function errorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { error?: string } } }).response;
    if (response?.data?.error) return response.data.error;
  }
  return error instanceof Error ? error.message : 'Wystąpił błąd.';
}

function formatTime(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function normalizePlaybackSpeed(value: number | null | undefined, fallback = 1) {
  const safeValue = typeof value === 'number' && Number.isFinite(value) ? Math.abs(value) : fallback;
  if (safeValue < MIN_PLAYBACK_SPEED_MAGNITUDE) return MIN_PLAYBACK_SPEED_MAGNITUDE;
  return clamp(safeValue, MIN_PLAYBACK_SPEED, MAX_PLAYBACK_SPEED);
}

function playbackSpeedMagnitude(value: number | null | undefined) {
  return Math.abs(normalizePlaybackSpeed(value));
}

function clipLayerCategory(kind: StudioAssetKind): StudioLayerCategory {
  if (kind === 'AUDIO') return 'audio';
  if (kind === 'TEXT' || kind === 'SCRIPT') return 'text';
  return 'visual';
}

function normalizeLayer(layer: number) {
  return clamp(Math.round(layer), MIN_LAYER, MAX_LAYER);
}

function newClipId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `clip-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function kindLabel(kind: StudioAssetKind) {
  switch (kind) {
    case 'VIDEO': return 'Wideo';
    case 'AUDIO': return 'Audio';
    case 'IMAGE': return 'Obraz';
    case 'SCRIPT': return 'Skrypt';
    default: return 'Tekst';
  }
}

function materialIcon(kind: StudioAssetKind) {
  if (kind === 'IMAGE') return ImageIcon;
  if (kind === 'AUDIO') return AudioLines;
  if (kind === 'SCRIPT' || kind === 'TEXT') return TextCursorInput;
  return Film;
}

function clipDurationForAsset(asset: StudioAsset) {
  return asset.durationMs || DEFAULT_CLIP_DURATION_MS;
}

const studioAssetDurationCache = new Map<string, Promise<number | null>>();

function readMediaDurationMs(url: string, kind: StudioAssetKind) {
  const cached = studioAssetDurationCache.get(url);
  if (cached) return cached;
  const pending = new Promise<number | null>((resolve) => {
    const media = document.createElement(kind === 'AUDIO' ? 'audio' : 'video');
    const cleanup = () => {
      media.onloadedmetadata = null;
      media.onerror = null;
      media.removeAttribute('src');
      media.load();
    };
    media.preload = 'metadata';
    media.onloadedmetadata = () => {
      const durationMs = Number.isFinite(media.duration) && media.duration > 0
        ? Math.ceil(media.duration * 1000)
        : null;
      cleanup();
      resolve(durationMs);
    };
    media.onerror = () => {
      cleanup();
      resolve(null);
    };
    media.src = url;
  });
  studioAssetDurationCache.set(url, pending);
  return pending;
}

function readFileDurationMs(file: File) {
  if (!file.type.startsWith('video/') && !file.type.startsWith('audio/')) return Promise.resolve<number | null>(null);
  const url = URL.createObjectURL(file);
  return readMediaDurationMs(url, file.type.startsWith('audio/') ? 'AUDIO' : 'VIDEO')
    .finally(() => {
      studioAssetDurationCache.delete(url);
      URL.revokeObjectURL(url);
    });
}

async function resolvedAssetDurationMs(asset: StudioAsset) {
  if ((asset.kind === 'VIDEO' || asset.kind === 'AUDIO') && asset.previewUrl) {
    return await readMediaDurationMs(asset.previewUrl, asset.kind) || clipDurationForAsset(asset);
  }
  return clipDurationForAsset(asset);
}

function createClipFromAsset(asset: StudioAsset, startMs: number, layer = 0): StudioTimelineClip {
  const durationMs = clipDurationForAsset(asset);
  return {
    id: newClipId(),
    assetId: asset.id,
    name: asset.name,
    kind: asset.kind,
    startMs,
    durationMs,
    sourceStartMs: 0,
    sourceDurationMs: durationMs,
    layer: asset.kind === 'AUDIO'
      ? clamp(Math.round(layer), MIN_LAYER, -1)
      : clamp(Math.round(layer), 0, MAX_LAYER),
    speed: 1,
    position: { x: 0, y: 0 },
    positionSpace: 'layout',
    scaleX: 1,
    scaleY: 1,
    rotation: 0,
    volumeDb: 0,
    fadeInMs: 0,
    fadeOutMs: 0,
    content: asset.content || null,
  };
}

function clipScale(clip: StudioTimelineClip | null) {
  return {
    x: clip?.scaleX && Number.isFinite(clip.scaleX) ? clip.scaleX : 1,
    y: clip?.scaleY && Number.isFinite(clip.scaleY) ? clip.scaleY : 1,
  };
}

function clipAudioSettings(clip: StudioTimelineClip) {
  return {
    volumeDb: typeof clip.volumeDb === 'number' ? clip.volumeDb : 0,
    fadeInMs: typeof clip.fadeInMs === 'number' ? clip.fadeInMs : 0,
    fadeOutMs: typeof clip.fadeOutMs === 'number' ? clip.fadeOutMs : 0,
  };
}

function clipHasAudioControls(clip: StudioTimelineClip) {
  return clip.kind === 'VIDEO' || clip.kind === 'AUDIO';
}

function clipAudioVolumeAt(clip: StudioTimelineClip, playheadMs: number) {
  const { volumeDb, fadeInMs, fadeOutMs } = clipAudioSettings(clip);
  const localMs = Math.max(0, playheadMs - clip.startMs);
  const fadeInFactor = fadeInMs > 0 ? clamp(localMs / fadeInMs, 0, 1) : 1;
  const fadeOutRemainingMs = Math.max(0, clip.durationMs - localMs);
  const fadeOutFactor = fadeOutMs > 0 ? clamp(fadeOutRemainingMs / fadeOutMs, 0, 1) : 1;
  return clamp(Math.pow(10, volumeDb / 20) * Math.min(fadeInFactor, fadeOutFactor), 0, 1);
}

function captionCuesFromSegment(segment: {
  startMs: number;
  endMs: number;
  text: string;
  words?: Array<{ startMs: number; endMs: number; text: string }>;
}) {
  const words = segment.words?.filter(word => word.text.trim() && word.endMs > word.startMs) || [];
  if (words.length === 0) {
    const textWords = segment.text.trim().split(/\s+/).filter(Boolean);
    if (textWords.length === 0) return [];
    const syntheticWords = textWords.map((text, index) => ({
      text,
      startMs: segment.startMs + ((segment.endMs - segment.startMs) * index) / textWords.length,
      endMs: segment.startMs + ((segment.endMs - segment.startMs) * (index + 1)) / textWords.length,
    }));
    return captionCuesFromSegment({ ...segment, words: syntheticWords });
  }
  const groups: Array<typeof words> = [];
  let group: typeof words = [];
  const flush = () => {
    if (group.length === 0) return;
    groups.push(group);
    group = [];
  };
  words.forEach(word => {
    group.push(word);
    const groupDuration = group[group.length - 1].endMs - group[0].startMs;
    const naturalBreak = /[.!?;:]$/.test(word.text) || (/[,:]$/.test(word.text) && group.length >= 3);
    if (naturalBreak || group.length >= 6 || (group.length >= 3 && groupDuration >= 1600)) flush();
  });
  flush();
  for (let index = groups.length - 1; index > 0; index -= 1) {
    const current = groups[index];
    const previous = groups[index - 1];
    if (current.length > 2 || previous.length + current.length > 6) continue;
    const previousLastWord = previous[previous.length - 1];
    const gapMs = current[0].startMs - previousLastWord.endMs;
    const beginsNewSentence = /[.!?]$/.test(previousLastWord.text)
      || (gapMs >= 140 && /^[A-ZĄĆĘŁŃÓŚŹŻ]/.test(current[0].text));
    if (beginsNewSentence) continue;
    previous.push(...current);
    groups.splice(index, 1);
  }
  return groups.map(cueWords => ({
    startOffsetMs: Math.max(0, cueWords[0].startMs - segment.startMs),
    endOffsetMs: Math.max(0, cueWords[cueWords.length - 1].endMs - segment.startMs),
    text: cueWords.map(word => word.text).join(' ').replace(/\s+([,.!?;:])/g, '$1'),
  }));
}

function studioClipTextAt(clip: StudioTimelineClip, timelineMs: number) {
  if (!clip.dynamicCaption || !clip.captionCues?.length) return clip.content || clip.name;
  const localMs = Math.max(0, timelineMs - clip.startMs);
  return clip.captionCues.find(cue => localMs >= cue.startOffsetMs && localMs < cue.endOffsetMs)?.text
    || clip.captionCues.find(cue => localMs < cue.startOffsetMs)?.text
    || clip.captionCues[clip.captionCues.length - 1].text;
}

function sortClips(clips: StudioTimelineClip[]) {
  return [...clips].sort((a, b) => a.startMs - b.startMs || b.layer - a.layer);
}

function clipEnd(clip: Pick<StudioTimelineClip, 'startMs' | 'durationMs'>) {
  return clip.startMs + clip.durationMs;
}

function rangesOverlap(startA: number, durationA: number, startB: number, durationB: number) {
  const overlapMs = Math.min(startA + durationA, startB + durationB) - Math.max(startA, startB);
  return overlapMs > TIMELINE_COLLISION_TOLERANCE_MS;
}

function hasLayerCollision(
  clips: StudioTimelineClip[],
  clipId: string | null,
  layer: number,
  startMs: number,
  durationMs: number,
) {
  return clips.some(clip => (
    clip.id !== clipId
    && clip.layer === layer
    && rangesOverlap(startMs, durationMs, clip.startMs, clip.durationMs)
  ));
}

function layerAcceptsKind(
  clips: StudioTimelineClip[],
  layer: number,
  kind: StudioAssetKind,
  excludedClipIds: ReadonlySet<string> = new Set(),
) {
  if (layer < 0) return kind === 'AUDIO';
  if (kind === 'AUDIO') return false;
  const category = clipLayerCategory(kind);
  return clips.every(clip => (
    clip.layer !== layer
    || excludedClipIds.has(clip.id)
    || clipLayerCategory(clip.kind) === category
  ));
}

function findOpenLayer(clips: StudioTimelineClip[], startMs: number, durationMs: number, kind: StudioAssetKind) {
  const highestLayer = Math.max(4, ...clips.filter(clip => clip.kind !== 'AUDIO').map(clip => clip.layer));
  const candidates = Array.from({ length: Math.min(MAX_LAYER, highestLayer + 1) + 1 }, (_, layer) => layer);
  return candidates.find(layer => (
    layerAcceptsKind(clips, layer, kind)
    && !hasLayerCollision(clips, null, layer, startMs, durationMs)
  ))
    ?? Math.min(MAX_LAYER, highestLayer + 1);
}

function findOpenAudioLayer(clips: StudioTimelineClip[], startMs: number, durationMs: number) {
  return AUDIO_LAYERS.find(layer => !hasLayerCollision(clips, null, layer, startMs, durationMs)) ?? -1;
}

function findDropLayer(
  clips: StudioTimelineClip[],
  layers: StudioTimelineLayer[],
  clipId: string,
  requestedLayer: number,
  startMs: number,
  durationMs: number,
  kind: StudioAssetKind,
) {
  const audioOnly = kind === 'AUDIO';
  const highestVisualLayer = Math.max(4, ...clips.filter(clip => clip.kind !== 'AUDIO').map(clip => clip.layer));
  const normalizedLayer = audioOnly
    ? clamp(Math.round(requestedLayer), MIN_LAYER, -1)
    : clamp(Math.round(requestedLayer), 0, Math.min(MAX_LAYER, highestVisualLayer + 1));
  const candidates = audioOnly
    ? [normalizedLayer, ...AUDIO_LAYERS.filter(layer => layer !== normalizedLayer)]
    : [
      ...Array.from({ length: Math.min(MAX_LAYER, highestVisualLayer + 1) - normalizedLayer + 1 }, (_, index) => normalizedLayer + index),
      ...Array.from({ length: normalizedLayer }, (_, index) => index),
    ];
  return candidates.find(layer => (
    !layerSetting(layers, layer).locked
    && layerAcceptsKind(clips, layer, kind, new Set([clipId]))
    && !hasLayerCollision(clips, clipId, layer, startMs, durationMs)
  )) ?? normalizedLayer;
}

function findGroupDropLayer(
  clips: StudioTimelineClip[],
  layers: StudioTimelineLayer[],
  group: Array<{ clip: StudioTimelineClip; startMs: number }>,
  requestedLayer: number,
) {
  const representative = group[0]?.clip;
  if (!representative) return requestedLayer;
  const audioOnly = representative.kind === 'AUDIO';
  const highestVisualLayer = Math.max(4, ...clips.filter(clip => clip.kind !== 'AUDIO').map(clip => clip.layer));
  const normalizedLayer = audioOnly
    ? clamp(Math.round(requestedLayer), MIN_LAYER, -1)
    : clamp(Math.round(requestedLayer), 0, Math.min(MAX_LAYER, highestVisualLayer + 1));
  const candidates = audioOnly
    ? [normalizedLayer, ...AUDIO_LAYERS.filter(layer => layer !== normalizedLayer)]
    : [
      ...Array.from({ length: Math.min(MAX_LAYER, highestVisualLayer + 1) - normalizedLayer + 1 }, (_, index) => normalizedLayer + index),
      ...Array.from({ length: normalizedLayer }, (_, index) => index),
    ];
  return candidates.find(layer => (
    !layerSetting(layers, layer).locked
    && layerAcceptsKind(clips, layer, representative.kind)
    && group.every(item => !hasLayerCollision(
      clips,
      null,
      layer,
      item.startMs,
      item.clip.durationMs,
    ))
  )) ?? normalizedLayer;
}

function nextNonOverlappingStart(clips: StudioTimelineClip[], layer: number) {
  const layerClips = clips.filter(clip => clip.layer === layer);
  return layerClips.length > 0 ? Math.max(...layerClips.map(clipEnd)) : 0;
}

function duplicateClip(clip: StudioTimelineClip, startMs: number, layer = clip.layer): StudioTimelineClip {
  return {
    ...clip,
    id: newClipId(),
    name: `${clip.name} kopia`,
    startMs,
    layer: normalizeLayer(layer),
  };
}

function snapClipStart(
  clips: StudioTimelineClip[],
  clipId: string,
  kind: StudioAssetKind,
  startMs: number,
  durationMs: number,
  playheadMs: number,
  thresholdMs: number,
) {
  let best = { startMs, distance: thresholdMs + 1, priority: 2 };
  const snapPoints = [{ value: playheadMs, priority: 1 }];
  clips.forEach(clip => {
    if (clip.id === clipId) return;
    const priority = clipLayerCategory(clip.kind) === clipLayerCategory(kind) ? 0 : 1;
    snapPoints.push({ value: clip.startMs, priority }, { value: clipEnd(clip), priority });
  });
  snapPoints.forEach(point => {
    const candidates = [point.value, point.value - durationMs];
    candidates.forEach(candidate => {
      const distance = Math.abs(candidate - startMs);
      if (
        distance <= thresholdMs
        && (point.priority < best.priority || (point.priority === best.priority && distance < best.distance))
      ) {
        best = { startMs: candidate, distance, priority: point.priority };
      }
    });
  });
  return Math.max(0, best.startMs);
}

function normalizeClipLayout(clips: StudioTimelineClip[]) {
  return sortClips(clips.map(clip => {
    const legalLayer = clip.kind === 'AUDIO'
      ? clamp(clip.layer, MIN_LAYER, -1)
      : clamp(clip.layer, 0, MAX_LAYER);
    return legalLayer === clip.layer ? clip : { ...clip, layer: legalLayer };
  }));
}

function compactPositiveLayers(clips: StudioTimelineClip[], layers: StudioTimelineLayer[]) {
  const usedOverlayLayers = Array.from(new Set(clips
    .filter(clip => clip.layer > 0)
    .map(clip => clip.layer)))
    .sort((a, b) => a - b);
  const layerMap = new Map(usedOverlayLayers.map((layer, index) => [layer, index + 1]));
  const nextClips = sortClips(clips.map(clip => (
    clip.layer > 0 && layerMap.has(clip.layer)
      ? { ...clip, layer: layerMap.get(clip.layer)! }
      : clip
  )));
  const requiredLayers = [
    ...Array.from({ length: Math.max(4, usedOverlayLayers.length) + 1 }, (_, layer) => layer),
    ...AUDIO_LAYERS,
  ];
  const originalSettings = new Map(layers.map(setting => [setting.layer, setting]));
  const settingsByLayer = new Map<number, StudioTimelineLayer>();
  [0, ...AUDIO_LAYERS].forEach(layer => {
    const setting = originalSettings.get(layer);
    if (setting) settingsByLayer.set(layer, setting);
  });
  usedOverlayLayers.forEach(oldLayer => {
    const setting = originalSettings.get(oldLayer);
    const newLayer = layerMap.get(oldLayer)!;
    settingsByLayer.set(newLayer, setting ? { ...setting, layer: newLayer } : { layer: newLayer, locked: false, hidden: false, muted: false });
  });
  const nextLayers = requiredLayers
    .map(layer => settingsByLayer.get(layer) || { layer, locked: false, hidden: false, muted: false })
    .sort((a, b) => b.layer - a.layer);
  return { clips: nextClips, layers: nextLayers };
}

function closeMainTrackAfterDeletion(clips: StudioTimelineClip[], deletedClips: StudioTimelineClip[]) {
  const deletedMainClips = deletedClips
    .filter(clip => clip.layer === 0 && clipLayerCategory(clip.kind) === 'visual')
    .sort((a, b) => a.startMs - b.startMs);
  if (deletedMainClips.length === 0) return clips;
  let nextClips = sortClips(clips);
  let cumulativeShiftMs = 0;
  deletedMainClips.forEach(deletedClip => {
    const adjustedStart = deletedClip.startMs - cumulativeShiftMs;
    const mainClips = nextClips
      .filter(clip => clip.layer === 0 && clipLayerCategory(clip.kind) === 'visual')
      .sort((a, b) => a.startMs - b.startMs);
    const previousClip = [...mainClips]
      .reverse()
      .find(clip => clipEnd(clip) <= adjustedStart + 4);
    const followingClip = mainClips.find(clip => (
      clip.id !== previousClip?.id && clip.startMs >= adjustedStart - 4
    ));
    if (!followingClip) return;
    const targetStart = previousClip ? clipEnd(previousClip) : 0;
    const shiftMs = Math.max(0, followingClip.startMs - targetStart);
    if (shiftMs === 0) return;
    nextClips = sortClips(nextClips.map(clip => (
      clip.layer === 0
      && clipLayerCategory(clip.kind) === 'visual'
      && clip.startMs >= followingClip.startMs
        ? { ...clip, startMs: clip.startMs - shiftMs }
        : clip
    )));
    cumulativeShiftMs += shiftMs;
  });
  return nextClips;
}

function closeMainTrackAfterReplacement(
  clips: StudioTimelineClip[],
  replacements: Array<{ sourceClip: StudioTimelineClip; pieces: StudioTimelineClip[] }>,
) {
  let nextClips = sortClips(clips);
  replacements
    .filter(change => change.sourceClip.layer === 0 && clipLayerCategory(change.sourceClip.kind) === 'visual')
    .sort((a, b) => a.sourceClip.startMs - b.sourceClip.startMs)
    .forEach(change => {
      const pieceIds = new Set(change.pieces.map(piece => piece.id));
      const currentPieces = nextClips.filter(clip => pieceIds.has(clip.id));
      if (currentPieces.length === 0) return;
      const replacementEnd = Math.max(...currentPieces.map(clipEnd));
      const followingClip = nextClips
        .filter(clip => (
          !pieceIds.has(clip.id)
          && clip.layer === 0
          && clipLayerCategory(clip.kind) === 'visual'
          && clip.startMs >= replacementEnd - 4
        ))
        .sort((a, b) => a.startMs - b.startMs)[0];
      if (!followingClip) return;
      const shiftMs = Math.max(0, followingClip.startMs - replacementEnd);
      if (shiftMs === 0) return;
      nextClips = sortClips(nextClips.map(clip => (
        clip.layer === 0
        && clipLayerCategory(clip.kind) === 'visual'
        && clip.startMs >= followingClip.startMs
          ? { ...clip, startMs: clip.startMs - shiftMs }
          : clip
      )));
    });
  return nextClips;
}

function timelineKey(durationMs: number, clips: StudioTimelineClip[], layers: StudioTimelineLayer[], transitions: StudioTimelineTransition[]) {
  return JSON.stringify({ durationMs, clips, layers, transitions });
}

function cloneTimelineSnapshot(snapshot: TimelineHistorySnapshot): TimelineHistorySnapshot {
  return {
    durationMs: snapshot.durationMs,
    clips: snapshot.clips.map(clip => ({
      ...clip,
      position: { ...clip.position },
    })),
    layers: snapshot.layers.map(layer => ({ ...layer })),
    transitions: snapshot.transitions.map(transition => ({ ...transition })),
  };
}

function timelineDraftKey(projectId: string) {
  return `ecomflow:studio:timeline-draft:${projectId}`;
}

function readTimelineDraft(projectId: string): TimelineLocalDraft | null {
  try {
    const raw = window.localStorage.getItem(timelineDraftKey(projectId));
    if (!raw) return null;
    const draft = JSON.parse(raw) as Partial<TimelineLocalDraft>;
    if (
      typeof draft.savedAt !== 'number'
      || !draft.timeline
      || typeof draft.timeline.durationMs !== 'number'
      || !Array.isArray(draft.timeline.clips)
      || !Array.isArray(draft.timeline.layers)
      || !Array.isArray(draft.timeline.transitions)
    ) return null;
    return { savedAt: draft.savedAt, timeline: cloneTimelineSnapshot(draft.timeline) };
  } catch {
    return null;
  }
}

function writeTimelineDraft(projectId: string, timeline: TimelineHistorySnapshot) {
  try {
    window.localStorage.setItem(timelineDraftKey(projectId), JSON.stringify({
      savedAt: Date.now(),
      timeline,
    } satisfies TimelineLocalDraft));
  } catch {
    // Zapis serwerowy pozostaje głównym mechanizmem, localStorage jest wyłącznie kopią awaryjną.
  }
}

function removeTimelineDraft(projectId: string) {
  try {
    window.localStorage.removeItem(timelineDraftKey(projectId));
  } catch {
    // Brak dostępu do localStorage nie może zablokować edycji projektu.
  }
}

function transitionsFrom(value: unknown, clips: StudioTimelineClip[]): StudioTimelineTransition[] {
  const transitions = typeof value === 'object' && value !== null && 'transitions' in value
    ? (value as { transitions?: unknown }).transitions
    : [];
  if (!Array.isArray(transitions)) return [];
  const clipIds = new Set(clips.map(clip => clip.id));
  return transitions.flatMap(item => {
    if (!item || typeof item !== 'object') return [];
    const candidate = item as Partial<StudioTimelineTransition>;
    if (
      typeof candidate.id !== 'string'
      || typeof candidate.type !== 'string'
      || typeof candidate.fromClipId !== 'string'
      || typeof candidate.toClipId !== 'string'
      || !clipIds.has(candidate.fromClipId)
      || !clipIds.has(candidate.toClipId)
    ) return [];
    const definition = STUDIO_TRANSITIONS.find(transition => transition.type === candidate.type);
    return [{
      id: candidate.id,
      type: candidate.type,
      name: typeof candidate.name === 'string' ? candidate.name : definition?.name || 'Przejście',
      fromClipId: candidate.fromClipId,
      toClipId: candidate.toClipId,
      boundaryMs: Math.max(0, Number(candidate.boundaryMs) || 0),
      durationMs: clamp(Number(candidate.durationMs) || 500, 100, 2000),
    }];
  });
}

function reconcileTransitions(items: StudioTimelineTransition[], clips: StudioTimelineClip[]) {
  const byId = new Map(clips.map(clip => [clip.id, clip]));
  return items.flatMap(transition => {
    const fromClip = byId.get(transition.fromClipId);
    const toClip = byId.get(transition.toClipId);
    if (
      !fromClip
      || !toClip
      || fromClip.layer !== toClip.layer
      || Math.abs(clipEnd(fromClip) - toClip.startMs) > 80
    ) return [];
    return [{ ...transition, boundaryMs: clipEnd(fromClip) }];
  });
}

function layerSettingsFrom(value: unknown): StudioTimelineLayer[] {
  const layers = typeof value === 'object' && value !== null && 'layers' in value
    ? (value as { layers?: unknown }).layers
    : [];
  const byLayer = new Map<number, StudioTimelineLayer>();
  if (Array.isArray(layers)) {
    layers.forEach(item => {
      if (!item || typeof item !== 'object') return;
      const candidate = item as Partial<StudioTimelineLayer>;
      if (typeof candidate.layer !== 'number') return;
      const layer = normalizeLayer(candidate.layer);
      byLayer.set(layer, {
        layer,
        locked: Boolean(candidate.locked),
        hidden: Boolean(candidate.hidden),
        muted: Boolean(candidate.muted),
      });
    });
  }
  const layerNumbers = Array.from(new Set([...DEFAULT_LAYERS, ...byLayer.keys()])).sort((a, b) => b - a);
  return layerNumbers.map(layer => byLayer.get(layer) || { layer, locked: false, hidden: false, muted: false });
}

function layerSetting(layers: StudioTimelineLayer[], layer: number) {
  return layers.find(item => item.layer === layer) || { layer, locked: false, hidden: false, muted: false };
}

function clipSourceDurationMs(clip: StudioTimelineClip) {
  return Math.max(500, clip.sourceDurationMs || clip.durationMs * playbackSpeedMagnitude(clip.speed));
}

function clipSourceTimeMsAt(clip: StudioTimelineClip, timelineMs: number) {
  const speed = normalizePlaybackSpeed(clip.speed);
  const sourceStartMs = clip.sourceStartMs || 0;
  const sourceDurationMs = clipSourceDurationMs(clip);
  const localSourceMs = clamp((timelineMs - clip.startMs) * Math.abs(speed), 0, sourceDurationMs);
  return speed < 0
    ? sourceStartMs + Math.max(0, sourceDurationMs - localSourceMs)
    : sourceStartMs + localSourceMs;
}

function syncMediaToTimeline(
  media: HTMLMediaElement,
  clip: StudioTimelineClip,
  playheadMs: number,
  isPlaying: boolean,
  hardSeekThresholdSeconds = 0.45,
) {
  const targetSeconds = clipSourceTimeMsAt(clip, playheadMs) / 1000;
  if (!Number.isFinite(targetSeconds)) return;
  const baseRate = normalizePlaybackSpeed(clip.speed);
  const driftSeconds = targetSeconds - media.currentTime;
  const seekThreshold = isPlaying ? hardSeekThresholdSeconds : 0.04;

  if (Math.abs(media.playbackRate - baseRate) > 0.001) {
    media.playbackRate = baseRate;
  }
  media.preservesPitch = true;
  if (Math.abs(driftSeconds) > seekThreshold) {
    media.currentTime = targetSeconds;
  }
}

function slugifyFilename(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'studio-export';
}

function evenNumber(value: number) {
  const rounded = Math.max(2, Math.round(value));
  return rounded % 2 === 0 ? rounded : rounded + 1;
}

function exportVideoSize(aspectRatio: string, shortEdge = STUDIO_LAYOUT_SHORT_EDGE) {
  const ratio = studioAspectRatioNumber(aspectRatio);
  if (ratio >= 1) {
    return {
      width: evenNumber(shortEdge * ratio),
      height: evenNumber(shortEdge),
    };
  }
  return {
    width: evenNumber(shortEdge),
    height: evenNumber(shortEdge / ratio),
  };
}

function studioLayoutScale(width: number, height: number) {
  return Math.min(width, height) / STUDIO_LAYOUT_SHORT_EDGE;
}

function studioClipPosition(clip: StudioTimelineClip, width: number, height: number) {
  const scale = studioLayoutScale(width, height);
  return {
    x: clip.position.x * scale,
    y: clip.position.y * scale,
  };
}

function bestMp4MimeType() {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = [
    'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
    'video/mp4;codecs=h264',
    'video/mp4',
  ];
  return candidates.find(candidate => MediaRecorder.isTypeSupported(candidate)) || null;
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName.toLowerCase();
  return tagName === 'input' || tagName === 'textarea' || tagName === 'select' || target.isContentEditable;
}

function studioSettingsFrom(value: unknown): StudioProjectSettings {
  const record = typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  return {
    ...record,
    aspectRatio: normalizeStudioAspectRatio(record.aspectRatio),
    snapEnabled: typeof record.snapEnabled === 'boolean' ? record.snapEnabled : DEFAULT_STUDIO_SETTINGS.snapEnabled,
    mainTrackMagnetEnabled: typeof record.mainTrackMagnetEnabled === 'boolean'
      ? record.mainTrackMagnetEnabled
      : DEFAULT_STUDIO_SETTINGS.mainTrackMagnetEnabled,
  };
}

function buildTimelineTicks(durationMs: number, pxPerSecond: number) {
  const minorCandidates = [100, 200, 500, 1000, 2000, 5000, 10000, 15000, 30000];
  const majorCandidates = [1000, 2000, 5000, 10000, 15000, 30000, 60000];
  let minorMs = minorCandidates.find(value => (value / 1000) * pxPerSecond >= 8) ?? 30000;
  while (durationMs / minorMs > 2500) minorMs *= 2;
  let majorMs = majorCandidates.find(value => value >= minorMs && (value / 1000) * pxPerSecond >= 72) ?? minorMs * 5;
  while (majorMs % minorMs !== 0) majorMs += minorMs;
  const minorCount = Math.floor(durationMs / minorMs) + 1;
  const ticks = Array.from({ length: minorCount }, (_, index) => {
    const value = index * minorMs;
    return {
      value,
      major: value % majorMs === 0,
    };
  });
  return { ticks, minorMs, majorMs };
}

function StudioLogoMark() {
  return (
    <Link
      to="/studio"
      className="w-full h-full flex items-center justify-center text-text-primary hover:bg-surface-hover transition-colors"
      aria-label="Wróć do Studio"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="570 478 70 70" className="w-8 h-8" aria-hidden="true">
        <path d="M592.76 526.57c-.06.55.07 1.11.39 1.58l11.33 16.84 10.7-18.42z" style={{ fill: '#259950' }} />
        <path d="M574.85 500.7c-.06.55.07 1.11.39 1.58l11.33 16.84 10.7-18.42z" style={{ fill: '#006aee' }} />
        <path d="m574.84 500.76 8.88-18.25c.4-.82 1.23-1.34 2.14-1.34h49.36l-7.36 15.13a7.95 7.95 0 0 1-7.14 4.47h-45.88Z" style={{ fill: '#44a6ff' }} />
        <path d="m592.75 526.63 8.88-18.25c.4-.82 1.23-1.34 2.14-1.34h29.73l-7.36 15.13a7.95 7.95 0 0 1-7.14 4.47h-26.26Z" style={{ fill: '#67e64c' }} />
      </svg>
    </Link>
  );
}

function StudioEmptyPanel({ icon: Icon, title, description }: { icon: typeof Film; title: string; description: string }) {
  return (
    <div className="h-full rounded-card border border-border bg-surface-raised">
      <EmptyState icon={Icon} title={title} description={description} size="sm" fillHeight />
    </div>
  );
}

function SourceAssetPreview({ asset, onClose }: { asset: StudioAsset; onClose: () => void }) {
  return (
    <div className="relative flex h-full min-h-0 items-center justify-center bg-black/85 p-5">
      <div data-studio-source-preview-content className="absolute left-3 top-3 z-10 flex items-center gap-2 rounded-lg border border-white/15 bg-black/75 px-2 py-1.5 text-xs text-white shadow-lg">
        <Film className="h-3.5 w-3.5 text-accent-primary" />
        <span className="max-w-72 truncate">Podgląd pliku: {asset.name}</span>
        <button type="button" onClick={onClose} className="ml-1 rounded px-2 py-1 text-[10px] text-white/75 hover:bg-white/10 hover:text-white">
          Wróć do projektu
        </button>
      </div>
      {asset.kind === 'VIDEO' && asset.previewUrl ? (
        <video data-studio-source-preview-content key={asset.id} src={asset.previewUrl} controls autoPlay playsInline preload="auto" className="max-h-full max-w-full rounded-lg object-contain shadow-2xl" />
      ) : asset.kind === 'IMAGE' && asset.previewUrl ? (
        <img data-studio-source-preview-content src={asset.previewUrl} alt={asset.name} className="max-h-full max-w-full rounded-lg object-contain shadow-2xl" />
      ) : asset.kind === 'AUDIO' && asset.previewUrl ? (
        <div data-studio-source-preview-content className="w-full max-w-xl rounded-xl border border-white/10 bg-surface-raised p-6">
          <AudioLines className="mx-auto mb-4 h-10 w-10 text-accent-primary" />
          <p className="mb-4 truncate text-center text-sm text-text-primary">{asset.name}</p>
          <audio key={asset.id} src={asset.previewUrl} controls autoPlay preload="auto" className="w-full" />
        </div>
      ) : (
        <div data-studio-source-preview-content className="max-w-xl rounded-xl border border-white/10 bg-surface-raised p-6 text-sm text-text-primary whitespace-pre-wrap">
          {asset.content || 'Ten plik nie ma dostępnego podglądu.'}
        </div>
      )}
    </div>
  );
}

function StudioIconButton({
  label,
  icon,
  active,
  disabled,
  onClick,
  side = 'top',
  compact = false,
}: {
  label: string;
  icon: ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  side?: 'top' | 'right' | 'bottom' | 'left';
  compact?: boolean;
}) {
  return (
    <Tooltip content={label} side={side}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={active}
        aria-disabled={disabled}
        title={label}
        onClick={() => {
          if (!disabled) onClick();
        }}
        className={[
          compact ? 'w-4 h-4 inline-flex items-center justify-center rounded transition-colors [&>svg]:w-2.5 [&>svg]:h-2.5' : 'w-7 h-7 inline-flex items-center justify-center rounded-md transition-colors [&>svg]:w-4 [&>svg]:h-4',
          disabled
            ? 'text-text-disabled cursor-not-allowed'
            : active
              ? 'bg-accent-primary/15 text-accent-primary hover:bg-accent-primary/20'
              : 'text-text-muted hover:text-text-primary hover:bg-surface-hover/70',
        ].join(' ')}
      >
        {icon}
      </button>
    </Tooltip>
  );
}

function AssetListItem({
  asset,
  onAdd,
  onPreview,
  previewing = false,
}: {
  asset: StudioAsset;
  onAdd: (asset: StudioAsset) => void;
  onPreview?: (asset: StudioAsset) => void;
  previewing?: boolean;
}) {
  const Icon = materialIcon(asset.kind);
  return (
    <div
      data-studio-asset-preview-trigger={onPreview ? true : undefined}
      role={onPreview ? 'button' : undefined}
      tabIndex={onPreview ? 0 : -1}
      onClick={() => onPreview?.(asset)}
      onKeyDown={event => {
        if (onPreview && (event.key === 'Enter' || event.key === ' ')) onPreview(asset);
      }}
      className={`group relative rounded-lg border p-1.5 transition-colors ${previewing ? 'border-accent-primary bg-accent-primary/10' : 'border-border-subtle bg-surface-hover/30 hover:border-accent-primary/35'}`}
    >
      <div className="flex items-center gap-2">
        <div className="relative w-16 h-11 rounded-md bg-surface-base border border-border-subtle flex items-center justify-center shrink-0 overflow-hidden">
          {asset.previewUrl && asset.kind === 'IMAGE' ? (
            <img src={asset.previewUrl} alt="" className="w-full h-full object-cover" />
          ) : asset.previewUrl && asset.kind === 'VIDEO' ? (
            <video src={`${asset.previewUrl}#t=0.05`} muted preload="metadata" playsInline className="w-full h-full object-cover" />
          ) : (
            <Icon className="w-4 h-4 text-accent-primary" />
          )}
          {asset.durationMs ? (
            <span className="absolute right-0.5 bottom-0.5 rounded bg-black/75 px-1 text-[8px] text-white">{formatTime(asset.durationMs)}</span>
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium text-text-primary truncate" title={asset.name}>{asset.name}</p>
          <p className="text-[9px] text-text-muted truncate">{kindLabel(asset.kind)} - {asset.source === 'LAB' ? 'Lab' : 'Upload'}</p>
        </div>
        <span onClick={event => event.stopPropagation()}>
          <StudioIconButton label="Dodaj na oś czasu" icon={<Layers />} onClick={() => onAdd(asset)} side="left" />
        </span>
      </div>
    </div>
  );
}

function LabMaterialItem({
  material,
  onAdd,
  adding,
}: {
  material: StudioLabMaterial;
  onAdd: (material: StudioLabMaterial) => void;
  adding: boolean;
}) {
  const Icon = materialIcon(material.kind);
  return (
    <div className="rounded-lg border border-border-subtle bg-surface-hover/30 p-1.5">
      <div className="flex items-center gap-2">
        <div className="w-16 h-11 rounded-md bg-surface-base border border-border-subtle flex items-center justify-center shrink-0 overflow-hidden">
          {material.previewUrl && material.kind === 'IMAGE' ? (
            <img src={material.previewUrl} alt="" className="w-full h-full object-cover" />
          ) : material.previewUrl && material.kind === 'VIDEO' ? (
            <video src={`${material.previewUrl}#t=0.05`} muted preload="metadata" playsInline className="w-full h-full object-cover" />
          ) : (
            <Icon className="w-4 h-4 text-accent-primary" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium text-text-primary truncate" title={material.name}>{material.name}</p>
          <p className="text-[9px] text-accent-primary truncate">{material.label}</p>
        </div>
        <StudioIconButton
          label="Dodaj na oś czasu"
          icon={adding ? <Loader2 className="animate-spin" /> : <Layers />}
          disabled={adding}
          onClick={() => onAdd(material)}
          side="left"
        />
      </div>
    </div>
  );
}

function MediaPanel({
  projectId,
  assets,
  labProductId,
  onLabProductChange,
  onAddAsset,
  onPreviewAsset,
  previewAssetId,
  onUploadDone,
}: {
  projectId: string;
  assets: StudioAsset[];
  labProductId: string;
  onLabProductChange: (value: string) => void;
  onAddAsset: (asset: StudioAsset) => void;
  onPreviewAsset: (asset: StudioAsset) => void;
  previewAssetId: string | null;
  onUploadDone: () => void;
}) {
  const [tab, setTab] = useState('project');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const projectMediaAssets = assets.filter(asset => asset.kind === 'IMAGE' || asset.kind === 'VIDEO' || asset.kind === 'AUDIO');

  const productsQuery = useQuery({
    queryKey: ['studio', 'lab-products'],
    queryFn: async () => (await studioApi.listLabProducts()).data.data,
  });

  const materialsQuery = useQuery({
    queryKey: ['studio', 'lab-materials', labProductId],
    queryFn: async () => (await studioApi.listLabMaterials(labProductId)).data.data.materials,
    enabled: Boolean(labProductId),
  });
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const durationMs = await readFileDurationMs(file);
      return (await studioApi.uploadAsset(projectId, file, durationMs || undefined)).data.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['studio', 'project', projectId] });
      onUploadDone();
    },
  });

  const addLabMutation = useMutation({
    mutationFn: async (material: StudioLabMaterial) => (await studioApi.createAssetFromLab(projectId, {
      materialType: material.materialType,
      materialId: material.materialId,
      labProductId: material.labProductId,
    })).data.data,
    onSuccess: async (asset) => {
      await queryClient.invalidateQueries({ queryKey: ['studio', 'project', projectId] });
      onAddAsset(asset);
    },
  });

  return (
    <div className="h-full flex flex-col min-h-0">
      <Tabs value={tab} onValueChange={setTab} className="min-h-0 flex flex-col">
        <TabsList fullWidth className="mb-2 gap-0.5 p-0.5 rounded-lg">
          <TabsTrigger value="project" size="sm" className="flex-1">Projekt</TabsTrigger>
          <TabsTrigger value="lab" size="sm" className="flex-1">Laboratorium</TabsTrigger>
        </TabsList>

        <TabsContent value="project" className="mt-0 min-h-0 flex-1">
          <div className="space-y-3 h-full flex flex-col min-h-0">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="video/*,audio/*,image/*"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) uploadMutation.mutate(file);
                event.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              loading={uploadMutation.isPending}
              leftIcon={<Upload />}
              fullWidth
            >
              Upload assetu
            </Button>
            {uploadMutation.isError && (
              <p className="text-xs text-danger">{errorMessage(uploadMutation.error)}</p>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto pr-1 space-y-1.5">
              {projectMediaAssets.length === 0 ? (
                <div className="rounded-card border border-border bg-surface-raised">
                  <EmptyState
                    icon={Upload}
                    title="Brak assetów projektu"
                    description="Wgraj plik albo dodaj materiał z Laboratorium."
                    size="sm"
                  />
                </div>
              ) : projectMediaAssets.map(asset => (
                <AssetListItem
                  key={asset.id}
                  asset={asset}
                  onAdd={onAddAsset}
                  onPreview={onPreviewAsset}
                  previewing={previewAssetId === asset.id}
                />
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="lab" className="mt-0 min-h-0 flex-1">
          <div className="space-y-3 h-full flex flex-col min-h-0">
            <FormField label="Produkt">
              <Select
                value={labProductId}
                onChange={(event) => onLabProductChange(event.target.value)}
                placeholder={productsQuery.isLoading ? 'Ładowanie...' : 'Wybierz produkt'}
                disabled={productsQuery.isLoading}
                className="h-8 px-2 py-1 text-xs"
              >
                <option value="">Wybierz produkt</option>
                {(productsQuery.data || []).map(product => (
                  <option key={product.id} value={product.id}>{product.name}</option>
                ))}
              </Select>
            </FormField>

            <div className="min-h-0 flex-1 overflow-y-auto pr-1 space-y-1.5">
              {!labProductId ? (
                <StudioEmptyPanel
                  icon={Sparkles}
                  title="Wybierz produkt"
                  description="Po wyborze produktu pokażę pliki graficzne i wideo z Laboratorium."
                />
              ) : materialsQuery.isLoading ? (
                <div className="h-40 rounded-card bg-surface-raised border border-border flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-accent-primary" />
                </div>
              ) : (materialsQuery.data || []).length === 0 ? (
                <StudioEmptyPanel
                  icon={Sparkles}
                  title="Brak plików"
                  description="Ten produkt nie ma jeszcze plików graficznych ani wideo do użycia na osi."
                />
              ) : (
                (materialsQuery.data || []).map(material => (
                  <LabMaterialItem
                    key={material.id}
                    material={material}
                    adding={addLabMutation.isPending && addLabMutation.variables?.id === material.id}
                    onAdd={(item) => addLabMutation.mutate(item)}
                  />
                ))
              )}
            </div>
            {addLabMutation.isError && (
              <p className="text-xs text-danger">{errorMessage(addLabMutation.error)}</p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ensureGoogleFont(fontFamily: string) {
  if (!STUDIO_GOOGLE_FONTS.includes(fontFamily) || typeof document === 'undefined') return;
  const id = `studio-font-${fontFamily.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily).replace(/%20/g, '+')}:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap`;
  document.head.appendChild(link);
}

function AudioPanel({ assets, onAddAsset }: { assets: StudioAsset[]; onAddAsset: (asset: StudioAsset) => void }) {
  const audioAssets = assets.filter(asset => asset.kind === 'AUDIO');
  const music = audioAssets.filter(asset => (
    asset.metadata?.audioCategory === 'MUSIC' || (!asset.metadata?.audioCategory && (asset.durationMs || 0) >= 30_000)
  ));
  const sounds = audioAssets.filter(asset => !music.some(item => item.id === asset.id));
  const list = (items: StudioAsset[], title: string, description: string) => items.length > 0 ? (
    <div className="space-y-1.5">
      {items.map(asset => <AssetListItem key={asset.id} asset={asset} onAdd={onAddAsset} />)}
    </div>
  ) : (
    <StudioEmptyPanel icon={AudioLines} title={title} description={description} />
  );
  return (
    <Tabs defaultValue="music" className="flex h-full min-h-0 flex-col">
      <TabsList fullWidth className="mb-2 gap-0.5 rounded-lg p-0.5">
        <TabsTrigger value="music" size="sm" className="flex-1">Muzyka</TabsTrigger>
        <TabsTrigger value="sounds" size="sm" className="flex-1">Dźwięki</TabsTrigger>
      </TabsList>
      <TabsContent value="music" className="mt-0 min-h-0 flex-1 overflow-y-auto">
        <p className="mb-2 text-xs text-text-muted">Dłuższe pliki audio gotowe do użycia jako podkład.</p>
        {list(music, 'Brak muzyki', 'Wgraj plik audio w Plikach. Nagrania dłuższe niż 30 sekund pojawią się tutaj.')}
      </TabsContent>
      <TabsContent value="sounds" className="mt-0 min-h-0 flex-1 overflow-y-auto">
        <p className="mb-2 text-xs text-text-muted">Krótkie efekty dźwiękowe i nagrania.</p>
        {list(sounds, 'Brak dźwięków', 'Wgraj krótki plik audio w Plikach albo wydziel ścieżkę z wideo.')}
      </TabsContent>
    </Tabs>
  );
}

function AiPanel({
  projectId,
  assets,
  labProductId,
  onLabProductChange,
}: {
  projectId: string;
  assets: StudioAsset[];
  labProductId: string;
  onLabProductChange: (value: string) => void;
}) {
  const queryClient = useQueryClient();
  const uploadRef = useRef<HTMLInputElement>(null);
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState<'1:1' | '16:9' | '9:16'>('9:16');
  const [referenceAssetIds, setReferenceAssetIds] = useState<string[]>([]);
  const productsQuery = useQuery({
    queryKey: ['studio', 'lab-products'],
    queryFn: async () => (await studioApi.listLabProducts()).data.data,
  });
  const materialsQuery = useQuery({
    queryKey: ['studio', 'lab-materials', labProductId],
    queryFn: async () => (await studioApi.listLabMaterials(labProductId)).data.data.materials,
    enabled: Boolean(labProductId),
  });
  const avatarsQuery = useQuery({
    queryKey: ['studio', 'avatar-references'],
    queryFn: async () => (await studioApi.listAvatarReferences()).data.data,
  });
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => (await studioApi.uploadAsset(projectId, file)).data.data,
    onSuccess: async asset => {
      setReferenceAssetIds(previous => Array.from(new Set([...previous, asset.id])).slice(0, 5));
      await queryClient.invalidateQueries({ queryKey: ['studio', 'project', projectId] });
    },
  });
  const importReferenceMutation = useMutation({
    mutationFn: async (material: StudioLabMaterial) => (await studioApi.createAssetFromLab(projectId, {
      materialType: material.materialType,
      materialId: material.materialId,
      labProductId: material.labProductId,
    })).data.data,
    onSuccess: async asset => {
      setReferenceAssetIds(previous => Array.from(new Set([...previous, asset.id])).slice(0, 5));
      await queryClient.invalidateQueries({ queryKey: ['studio', 'project', projectId] });
    },
  });
  const importAvatarMutation = useMutation({
    mutationFn: async (avatar: StudioAvatarReference) => (await studioApi.createAssetFromAvatar(projectId, avatar.id)).data.data,
    onSuccess: async asset => {
      setReferenceAssetIds(previous => Array.from(new Set([...previous, asset.id])).slice(0, 5));
      await queryClient.invalidateQueries({ queryKey: ['studio', 'project', projectId] });
    },
  });
  const generateMutation = useMutation({
    mutationFn: async () => (await studioApi.generateImage(projectId, { prompt, aspectRatio, referenceAssetIds })).data.data,
    onSuccess: async () => {
      setPrompt('');
      await queryClient.invalidateQueries({ queryKey: ['studio', 'project', projectId] });
    },
  });
  const projectImages = assets.filter(asset => asset.kind === 'IMAGE' && asset.driveFileId);
  const labReferences = (materialsQuery.data || []).filter(material => (
    material.kind === 'IMAGE' && (material.materialType === 'productImage' || material.materialType === 'referenceImage')
  ));
  const avatars = (avatarsQuery.data || []).filter(avatar => avatar.status === 'READY');
  const selectedReferences = referenceAssetIds
    .map(assetId => projectImages.find(asset => asset.id === assetId))
    .filter((asset): asset is StudioAsset => Boolean(asset));
  const toggleReference = (assetId: string) => {
    setReferenceAssetIds(previous => previous.includes(assetId)
      ? previous.filter(id => id !== assetId)
      : previous.length < 5 ? [...previous, assetId] : previous);
  };
  const importedMaterialAsset = (material: StudioLabMaterial) => projectImages.find(asset => (
    asset.metadata?.materialType === material.materialType && asset.metadata?.materialId === material.materialId
  ));
  const importedAvatarAsset = (avatar: StudioAvatarReference) => projectImages.find(asset => asset.metadata?.avatarId === avatar.id);
  const chooseMaterial = (material: StudioLabMaterial) => {
    const imported = importedMaterialAsset(material);
    if (imported) toggleReference(imported.id);
    else importReferenceMutation.mutate(material);
  };
  const chooseAvatar = (avatar: StudioAvatarReference) => {
    const imported = importedAvatarAsset(avatar);
    if (imported) toggleReference(imported.id);
    else importAvatarMutation.mutate(avatar);
  };
  return (
    <Tabs defaultValue="image" className="flex h-full min-h-0 flex-col">
      <TabsList fullWidth className="mb-2 gap-0.5 rounded-lg p-0.5">
        <TabsTrigger value="image" size="sm" className="flex-1">Obraz</TabsTrigger>
        <TabsTrigger value="video" size="sm" className="flex-1">Wideo</TabsTrigger>
      </TabsList>
      <TabsContent value="image" className="mt-0 min-h-0 flex-1 overflow-y-auto pr-1">
        <div className="space-y-3">
          <FormField label="Prompt">
            <Textarea value={prompt} rows={5} maxLength={4000} placeholder="Opisz obraz, który chcesz wygenerować..." onChange={event => setPrompt(event.target.value)} />
          </FormField>
          <FormField label="Format">
            <Select value={aspectRatio} onChange={event => setAspectRatio(event.target.value as typeof aspectRatio)}>
              <option value="1:1">Kwadrat 1:1</option>
              <option value="16:9">Poziomy 16:9</option>
              <option value="9:16">Pionowy 9:16</option>
            </Select>
          </FormField>
          <input
            ref={uploadRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={event => {
              const file = event.target.files?.[0];
              if (file) uploadMutation.mutate(file);
              event.target.value = '';
            }}
          />
          <Button variant="secondary" size="sm" fullWidth leftIcon={<Upload />} loading={uploadMutation.isPending} onClick={() => uploadRef.current?.click()}>
            Dodaj referencję z komputera
          </Button>
          {selectedReferences.length > 0 && (
            <div className="space-y-2 rounded-lg border border-border-subtle p-2">
              <p className="text-xs font-semibold text-text-primary">Wybrane referencje ({referenceAssetIds.length}/5)</p>
              {selectedReferences.map(asset => (
                <div key={asset.id} className="flex items-center gap-2 rounded-md bg-surface-base/70 p-1.5">
                  {asset.previewUrl ? <img src={asset.previewUrl} alt="" className="h-9 w-9 rounded object-cover" /> : <ImageIcon className="h-4 w-4" />}
                  <span className="min-w-0 flex-1 truncate text-xs">{asset.name}</span>
                  <Tooltip content="Usuń z referencji">
                    <button type="button" className="rounded p-1 text-text-muted hover:bg-surface-hover hover:text-text-primary" onClick={() => toggleReference(asset.id)} aria-label={`Usuń referencję ${asset.name}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </Tooltip>
                </div>
              ))}
            </div>
          )}
          <Tabs defaultValue="project-references" className="rounded-lg border border-border-subtle p-1.5">
            <TabsList fullWidth className="mb-1.5 gap-0.5 rounded-lg p-0.5">
              <TabsTrigger value="project-references" size="sm" className="min-w-0 flex-1 whitespace-nowrap leading-none">Projekt</TabsTrigger>
              <TabsTrigger value="lab-references" size="sm" className="min-w-0 flex-1 whitespace-nowrap leading-none">Referencje</TabsTrigger>
              <TabsTrigger value="avatars" size="sm" className="min-w-0 flex-1 whitespace-nowrap leading-none">Avatary</TabsTrigger>
            </TabsList>
            <TabsContent value="project-references" className="mt-0 space-y-2">
              {projectImages.length > 0 ? projectImages.map(asset => (
                <div key={asset.id} className="min-w-0">
                  <Checkbox
                    size="sm"
                    className="w-full items-center rounded-md border border-border-subtle p-1.5 hover:border-accent-primary/40"
                    checked={referenceAssetIds.includes(asset.id)}
                    onChange={() => toggleReference(asset.id)}
                    label={(
                      <span className="flex min-w-0 items-center gap-2">
                        {asset.previewUrl ? (
                          <img src={asset.previewUrl} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
                        ) : (
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-surface-base">
                            <ImageIcon className="h-3.5 w-3.5 text-text-muted" />
                          </span>
                        )}
                        <span className="min-w-0 truncate">{asset.name}</span>
                      </span>
                    )}
                  />
                </div>
              )) : <p className="text-xs text-text-muted">Brak obrazów w projekcie.</p>}
            </TabsContent>
            <TabsContent value="lab-references" className="mt-0 space-y-2">
              <Select value={labProductId} disabled={productsQuery.isLoading} onChange={event => onLabProductChange(event.target.value)}>
                <option value="">Wybierz produkt</option>
                {(productsQuery.data || []).map(product => <option key={product.id} value={product.id}>{product.name}</option>)}
              </Select>
              {labReferences.map(material => {
                const imported = importedMaterialAsset(material);
                const selected = Boolean(imported && referenceAssetIds.includes(imported.id));
                return (
                  <button
                    key={material.id}
                    type="button"
                    disabled={importReferenceMutation.isPending || (!imported && referenceAssetIds.length >= 5)}
                    onClick={() => chooseMaterial(material)}
                    className="flex w-full items-center gap-2 rounded-md border border-border-subtle p-1.5 text-left hover:border-accent-primary/40 disabled:opacity-50"
                  >
                    {material.previewUrl ? <img src={material.previewUrl} alt="" className="h-9 w-12 rounded object-cover" /> : <ImageIcon className="h-4 w-4" />}
                    <span className="min-w-0 flex-1 truncate text-xs">{material.name}</span>
                    <span className="text-[10px] text-accent-primary">{selected ? 'Cofnij' : 'Wybierz'}</span>
                  </button>
                );
              })}
              {labProductId && !materialsQuery.isLoading && labReferences.length === 0 && <p className="text-xs text-text-muted">Brak zdjęć referencyjnych dla produktu.</p>}
            </TabsContent>
            <TabsContent value="avatars" className="mt-0 space-y-2">
              {avatars.map(avatar => {
                const imported = importedAvatarAsset(avatar);
                const selected = Boolean(imported && referenceAssetIds.includes(imported.id));
                return (
                  <button
                    key={avatar.id}
                    type="button"
                    disabled={importAvatarMutation.isPending || (!imported && referenceAssetIds.length >= 5)}
                    onClick={() => chooseAvatar(avatar)}
                    className="flex w-full items-center gap-2 rounded-md border border-border-subtle p-1.5 text-left hover:border-accent-primary/40 disabled:opacity-50"
                  >
                    <img src={avatar.previewUrl || `/api/lab/avatars/${encodeURIComponent(avatar.id)}/image`} alt="" className="h-10 w-10 rounded object-cover" />
                    <span className="min-w-0 flex-1 truncate text-xs">{avatar.name}</span>
                    <span className="text-[10px] text-accent-primary">{selected ? 'Cofnij' : 'Wybierz'}</span>
                  </button>
                );
              })}
              {!avatarsQuery.isLoading && avatars.length === 0 && <p className="text-xs text-text-muted">Brak gotowych avatarów w bibliotece.</p>}
            </TabsContent>
          </Tabs>
          <Button fullWidth leftIcon={<Sparkles />} loading={generateMutation.isPending} disabled={prompt.trim().length < 3} onClick={() => generateMutation.mutate()}>
            Generuj obraz
          </Button>
          {(uploadMutation.isError || importReferenceMutation.isError || importAvatarMutation.isError || generateMutation.isError) && (
            <p className="text-xs text-danger">{errorMessage(uploadMutation.error || importReferenceMutation.error || importAvatarMutation.error || generateMutation.error)}</p>
          )}
          {generateMutation.isSuccess && <p className="text-xs text-success">Obraz zapisano w Plikach i folderze projektu na Dysku Google.</p>}
        </div>
      </TabsContent>
      <TabsContent value="video" className="mt-0 min-h-0 flex-1 overflow-y-auto">
        <StudioEmptyPanel
          icon={Film}
          title="Generowanie wideo w Laboratorium"
          description="HeyGen wymaga avatara, głosu i scen, a Higgsfield działa jako asynchroniczny job. Skonfiguruj produkcję przy produkcie, a gotowe klipy zaimportuj do Studio z zakładki Pliki."
        />
        <Link to={labProductId ? `/marketing/laboratorium/produkt/${labProductId}` : '/marketing/laboratorium'} className="mt-3 flex h-9 items-center justify-center rounded-lg bg-accent-primary px-3 text-xs font-semibold text-white hover:opacity-90">
          Otwórz Laboratorium
        </Link>
      </TabsContent>
    </Tabs>
  );
}

function TextPanel({
  projectId,
  clips,
  layers,
  selectedClip,
  onAddText,
  onUpdateClip,
  onAddTranscriptions,
}: {
  projectId: string;
  clips: StudioTimelineClip[];
  layers: StudioTimelineLayer[];
  selectedClip: StudioTimelineClip | null;
  onAddText: () => void;
  onUpdateClip: (clipId: string, patch: Partial<StudioTimelineClip>) => void;
  onAddTranscriptions: (results: Array<{
    sourceClip: StudioTimelineClip;
    segments: Array<{ startMs: number; endMs: number; text: string; words?: Array<{ startMs: number; endMs: number; text: string }> }>;
    dynamic: boolean;
    fillToClip: boolean;
  }>) => void;
}) {
  const textClip = selectedClip?.kind === 'TEXT' ? selectedClip : null;
  const videoClips = useMemo(() => clips.filter(clip => {
    if (clip.kind !== 'VIDEO' || layerSetting(layers, clip.layer).hidden) return false;
    if (!clip.audioDetached) {
      return !layerSetting(layers, clip.layer).muted && clipAudioSettings(clip).volumeDb > -60;
    }
    return clips.some(audioClip => (
      audioClip.kind === 'AUDIO'
      && audioClip.detachedFromClipId === clip.id
      && !layerSetting(layers, audioClip.layer).hidden
      && !layerSetting(layers, audioClip.layer).muted
      && clipAudioSettings(audioClip).volumeDb > -60
    ));
  }), [clips, layers]);
  const [sourceClipId, setSourceClipId] = useState('all');
  const [targetLanguage, setTargetLanguage] = useState('original');
  const [dynamicCaptions, setDynamicCaptions] = useState(false);
  const [fillCaptionsToClip, setFillCaptionsToClip] = useState(false);
  const [showTranscriptionOptions, setShowTranscriptionOptions] = useState(false);
  const transcriptionMutation = useMutation({
    mutationFn: async () => {
      const sourceClips = sourceClipId === 'all'
        ? videoClips
        : videoClips.filter(clip => clip.id === sourceClipId);
      if (sourceClips.length === 0) throw new Error('Brak widocznych klipów wideo z aktywnym dźwiękiem.');
      const results = [];
      for (const sourceClip of sourceClips) {
        const result = await studioApi.transcribeAsset(projectId, sourceClip.assetId, {
          targetLanguage: targetLanguage === 'original' ? undefined : targetLanguage,
        });
        results.push({
          sourceClip,
          segments: result.data.data.segments,
          dynamic: dynamicCaptions,
          fillToClip: fillCaptionsToClip,
        });
      }
      return results;
    },
    onSuccess: onAddTranscriptions,
  });

  useEffect(() => {
    if (textClip?.fontFamily) ensureGoogleFont(textClip.fontFamily);
  }, [textClip?.fontFamily]);

  useEffect(() => {
    if (sourceClipId !== 'all' && !videoClips.some(clip => clip.id === sourceClipId)) {
      setSourceClipId('all');
    }
  }, [sourceClipId, videoClips]);

  const update = (patch: Partial<StudioTimelineClip>) => {
    if (textClip) onUpdateClip(textClip.id, patch);
  };

  return (
    <div className="h-full min-h-0 overflow-y-auto pr-1 space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Button size="sm" onClick={onAddText} leftIcon={<Type />}>Dodaj tekst</Button>
        <Button
          size="sm"
          variant={showTranscriptionOptions ? 'primary' : 'secondary'}
          disabled={videoClips.length === 0}
          onClick={() => setShowTranscriptionOptions(value => !value)}
          leftIcon={<Captions />}
        >
          Napisy AI
        </Button>
      </div>

      {showTranscriptionOptions && (
        <div className="rounded-lg border border-accent-primary/30 bg-accent-primary/5 p-2 space-y-2">
          <p className="text-xs font-semibold text-text-primary">Automatyczna transkrypcja</p>
          <Select value={sourceClipId} onChange={event => setSourceClipId(event.target.value)} className="h-8 text-xs">
            <option value="all">Wszystkie widoczne klipy z aktywnym dźwiękiem</option>
            {videoClips.map(clip => <option key={clip.id} value={clip.id}>{clip.name}</option>)}
          </Select>
          <Select value={targetLanguage} onChange={event => setTargetLanguage(event.target.value)} className="h-8 text-xs">
            {TRANSCRIPTION_LANGUAGES.map(language => <option key={language.value} value={language.value}>{language.label}</option>)}
          </Select>
          <div className="flex items-center gap-1.5">
            <Checkbox
              checked={dynamicCaptions}
              onChange={event => setDynamicCaptions(event.target.checked)}
              label="Dynamiczne napisy"
            />
            <Tooltip content="Krótkie frazy zsynchronizowane ze słowami.">
              <button type="button" className="rounded text-text-muted hover:text-text-primary" aria-label="Jak działają dynamiczne napisy">
                <Info className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
          <div className="flex items-center gap-1.5">
            <Checkbox
              checked={fillCaptionsToClip}
              onChange={event => setFillCaptionsToClip(event.target.checked)}
              label="Dociągnij do klipu"
            />
            <Tooltip content="Wypełnia przerwy napisami. Pierwszy napis zaczyna się przy krawędzi klipu, kolejne trwają do następnego napisu, a ostatni do końca klipu." side="right">
              <button type="button" className="rounded text-text-muted hover:text-text-primary" aria-label="Jak działa dociąganie napisów do klipu">
                <Info className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
          <Button
            size="sm"
            fullWidth
            loading={transcriptionMutation.isPending}
            onClick={() => transcriptionMutation.mutate()}
            leftIcon={<Sparkles />}
          >
            Generuj napisy
          </Button>
          {transcriptionMutation.isError && <p className="text-[10px] leading-relaxed text-danger">{errorMessage(transcriptionMutation.error)}</p>}
        </div>
      )}

      <div className="hidden" aria-hidden="true">{!textClip ? (
        <StudioEmptyPanel icon={Type} title="Dodaj lub zaznacz tekst" description="Po zaznaczeniu tekstu pojawią się tutaj wszystkie opcje wyglądu." />
      ) : (
        <div className="space-y-3">
          <FormField label="Treść">
            <Textarea value={textClip.content || ''} rows={4} onChange={event => update({ content: event.target.value })} />
          </FormField>
          <FormField label="Font Google">
            <Select value={textClip.fontFamily || 'Inter'} onChange={event => update({ fontFamily: event.target.value })}>
              {STUDIO_GOOGLE_FONTS.map(font => <option key={font} value={font}>{font}</option>)}
            </Select>
          </FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Wielkość">
              <Input type="number" min={8} max={240} value={textClip.fontSize || 48} onChange={event => update({ fontSize: Number(event.target.value) || 48 })} />
            </FormField>
            <FormField label="Grubość">
              <Select value={String(textClip.fontWeight || 700)} onChange={event => update({ fontWeight: Number(event.target.value) })}>
                {[300, 400, 500, 600, 700, 800, 900].map(weight => <option key={weight} value={weight}>{weight}</option>)}
              </Select>
            </FormField>
          </div>
          <div className="flex items-center gap-1">
            <StudioIconButton label="Pogrubienie" icon={<Bold />} active={(textClip.fontWeight || 700) >= 700} onClick={() => update({ fontWeight: (textClip.fontWeight || 700) >= 700 ? 400 : 700 })} />
            <StudioIconButton label="Kursywa" icon={<Italic />} active={textClip.fontStyle === 'italic'} onClick={() => update({ fontStyle: textClip.fontStyle === 'italic' ? 'normal' : 'italic' })} />
            <StudioIconButton label="Podkreślenie" icon={<Underline />} active={textClip.textDecoration === 'underline'} onClick={() => update({ textDecoration: textClip.textDecoration === 'underline' ? 'none' : 'underline' })} />
            <span className="w-px h-5 bg-border-subtle mx-1" />
            <StudioIconButton label="Do lewej" icon={<AlignLeft />} active={textClip.textAlign === 'left'} onClick={() => update({ textAlign: 'left' })} />
            <StudioIconButton label="Wyśrodkuj" icon={<AlignCenter />} active={!textClip.textAlign || textClip.textAlign === 'center'} onClick={() => update({ textAlign: 'center' })} />
            <StudioIconButton label="Do prawej" icon={<AlignRight />} active={textClip.textAlign === 'right'} onClick={() => update({ textAlign: 'right' })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Kolor tekstu"><Input type="color" value={textClip.textColor || '#ffffff'} onChange={event => update({ textColor: event.target.value })} className="h-9 p-1" /></FormField>
            <FormField label="Kolor tła"><Input type="color" value={textClip.textBackgroundColor || '#000000'} onChange={event => update({ textBackgroundColor: event.target.value })} className="h-9 p-1" /></FormField>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Obrys"><Input type="number" min={0} max={12} step={0.5} value={textClip.textStrokeWidth || 0} onChange={event => update({ textStrokeWidth: Number(event.target.value) || 0 })} /></FormField>
            <FormField label="Kolor obrysu"><Input type="color" value={textClip.textStrokeColor || '#000000'} onChange={event => update({ textStrokeColor: event.target.value })} className="h-9 p-1" /></FormField>
          </div>
          <FormField label="Krycie tła">
            <Slider value={textClip.textBackgroundOpacity ?? 70} min={0} max={100} step={1} unit="%" onChange={textBackgroundOpacity => update({ textBackgroundOpacity })} />
          </FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Zaokrąglenie"><Input type="number" min={0} max={100} value={textClip.textBorderRadius ?? 16} onChange={event => update({ textBorderRadius: Number(event.target.value) || 0 })} /></FormField>
            <FormField label="Margines wewn."><Input type="number" min={0} max={100} value={textClip.textPadding ?? 16} onChange={event => update({ textPadding: Number(event.target.value) || 0 })} /></FormField>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Odstęp liter"><Input type="number" min={-5} max={30} step={0.5} value={textClip.letterSpacing || 0} onChange={event => update({ letterSpacing: Number(event.target.value) || 0 })} /></FormField>
            <FormField label="Interlinia"><Input type="number" min={0.7} max={3} step={0.1} value={textClip.lineHeight || 1.15} onChange={event => update({ lineHeight: Number(event.target.value) || 1.15 })} /></FormField>
          </div>
          <label className="flex items-center gap-2 text-xs text-text-secondary">
            <input type="checkbox" checked={Boolean(textClip.textShadow)} onChange={event => update({ textShadow: event.target.checked })} />
            Cień tekstu
          </label>
        </div>
      )}</div>
    </div>
  );
}

function TransitionsPanel() {
  return (
    <div className="h-full min-h-0 overflow-y-auto pr-1">
      <div className="mb-3">
        <p className="text-xs font-semibold text-text-primary">Przejścia wideo</p>
        <p className="mt-1 text-[10px] leading-relaxed text-text-muted">
          Przeciągnij efekt na miejsce styku dwóch klipów na tej samej warstwie.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {STUDIO_TRANSITIONS.map(transition => {
          const Icon = transition.icon;
          return (
            <div
              key={transition.type}
              draggable
              onDragStart={event => {
                event.dataTransfer.effectAllowed = 'copy';
                event.dataTransfer.setData('application/x-studio-transition', transition.type);
                event.dataTransfer.setData('text/plain', transition.type);
              }}
              title={transition.description}
              className="group cursor-grab active:cursor-grabbing rounded-lg border border-border-subtle bg-surface-hover/30 p-2 hover:border-violet-400/60 hover:bg-violet-500/10 transition-colors"
            >
              <div className="mb-1 flex h-7 items-center justify-center rounded-md bg-violet-500/10 text-violet-300 group-hover:bg-violet-500/20">
                <Icon className="h-4 w-4" />
              </div>
              <p className="truncate text-center text-[10px] font-medium text-text-secondary group-hover:text-text-primary">
                {transition.name}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function transitionVisualStyles(type: string, progress: number): {
  from: CSSProperties;
  to: CSSProperties;
  overlay?: CSSProperties;
} {
  const inverse = 1 - progress;
  const baseFrom: CSSProperties = { opacity: inverse };
  const baseTo: CSSProperties = { opacity: progress };
  switch (type) {
    case 'fade-black':
      return { from: { opacity: Math.max(0, 1 - progress * 2) }, to: { opacity: Math.max(0, progress * 2 - 1) }, overlay: { backgroundColor: '#000', opacity: 1 - Math.abs(progress - 0.5) * 2 } };
    case 'fade-white':
      return { from: { opacity: Math.max(0, 1 - progress * 2) }, to: { opacity: Math.max(0, progress * 2 - 1) }, overlay: { backgroundColor: '#fff', opacity: 1 - Math.abs(progress - 0.5) * 2 } };
    case 'dissolve':
      return { from: { opacity: inverse, filter: `contrast(${1 + progress * 0.5})` }, to: { opacity: progress, filter: `contrast(${1.5 - progress * 0.5})` } };
    case 'slide-left':
      return { from: { transform: `translateX(${-progress * 100}%)` }, to: { transform: `translateX(${inverse * 100}%)` } };
    case 'slide-right':
      return { from: { transform: `translateX(${progress * 100}%)` }, to: { transform: `translateX(${-inverse * 100}%)` } };
    case 'slide-up':
      return { from: { transform: `translateY(${-progress * 100}%)` }, to: { transform: `translateY(${inverse * 100}%)` } };
    case 'slide-down':
      return { from: { transform: `translateY(${progress * 100}%)` }, to: { transform: `translateY(${-inverse * 100}%)` } };
    case 'wipe-left':
      return { from: {}, to: { clipPath: `inset(0 ${inverse * 100}% 0 0)` } };
    case 'wipe-right':
      return { from: {}, to: { clipPath: `inset(0 0 0 ${inverse * 100}%)` } };
    case 'wipe-up':
      return { from: {}, to: { clipPath: `inset(0 0 ${inverse * 100}% 0)` } };
    case 'wipe-down':
      return { from: {}, to: { clipPath: `inset(${inverse * 100}% 0 0 0)` } };
    case 'zoom-in':
      return { from: { opacity: inverse, transform: `scale(${1 + progress * 0.35})` }, to: { opacity: progress, transform: `scale(${0.7 + progress * 0.3})` } };
    case 'zoom-out':
      return { from: { opacity: inverse, transform: `scale(${1 - progress * 0.3})` }, to: { opacity: progress, transform: `scale(${1.35 - progress * 0.35})` } };
    case 'spin':
      return { from: { opacity: inverse, transform: `rotate(${progress * 35}deg) scale(${1 - progress * 0.15})` }, to: { opacity: progress, transform: `rotate(${(progress - 1) * 35}deg) scale(${0.85 + progress * 0.15})` } };
    case 'blur':
      return { from: { opacity: inverse, filter: `blur(${progress * 18}px)` }, to: { opacity: progress, filter: `blur(${inverse * 18}px)` } };
    case 'flash':
      return { from: baseFrom, to: baseTo, overlay: { backgroundColor: '#fff', opacity: Math.max(0, 1 - Math.abs(progress - 0.5) * 7) } };
    case 'push-left':
      return { from: { transform: `translateX(${-progress * 55}%)` }, to: { transform: `translateX(${inverse * 100}%)` } };
    case 'push-right':
      return { from: { transform: `translateX(${progress * 55}%)` }, to: { transform: `translateX(${-inverse * 100}%)` } };
    case 'pulse': {
      const pulse = Math.sin(progress * Math.PI);
      return { from: { opacity: inverse, transform: `scale(${1 + pulse * 0.12})` }, to: { opacity: progress, transform: `scale(${1.12 - pulse * 0.12})` } };
    }
    default:
      return { from: baseFrom, to: baseTo };
  }
}

function TransitionPreview({
  transition,
  fromClip,
  toClip,
  fromAsset,
  toAsset,
  playheadMs,
  isPlaying,
}: {
  transition: StudioTimelineTransition;
  fromClip: StudioTimelineClip;
  toClip: StudioTimelineClip;
  fromAsset: StudioAsset;
  toAsset: StudioAsset;
  playheadMs: number;
  isPlaying: boolean;
}) {
  const fromVideoRef = useRef<HTMLVideoElement>(null);
  const toVideoRef = useRef<HTMLVideoElement>(null);
  const [fromReady, setFromReady] = useState(fromClip.kind !== 'VIDEO');
  const [toReady, setToReady] = useState(toClip.kind !== 'VIDEO');
  const startMs = transition.boundaryMs - transition.durationMs / 2;
  const progress = clamp((playheadMs - startMs) / transition.durationMs, 0, 1);
  const styles = transitionVisualStyles(transition.type, progress);

  useEffect(() => {
    setFromReady(fromClip.kind !== 'VIDEO');
    setToReady(toClip.kind !== 'VIDEO');
  }, [fromAsset.id, fromClip.kind, toAsset.id, toClip.kind]);

  useEffect(() => {
    const syncVideo = (video: HTMLVideoElement | null, clip: StudioTimelineClip) => {
      if (!video) return;
      syncMediaToTimeline(video, clip, playheadMs, isPlaying, 0.35);
      video.muted = true;
      if (isPlaying) {
        if (video.paused && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) void video.play().catch(() => {});
      } else {
        video.pause();
      }
    };
    syncVideo(fromVideoRef.current, fromClip);
    syncVideo(toVideoRef.current, toClip);
  }, [fromClip, fromReady, isPlaying, playheadMs, toClip, toReady]);

  const media = (clip: StudioTimelineClip, asset: StudioAsset, ref: React.RefObject<HTMLVideoElement>, onReady: () => void) => clip.kind === 'VIDEO' ? (
    <video ref={ref} src={asset.previewUrl || undefined} muted playsInline preload="auto" onLoadedData={onReady} onCanPlay={onReady} className="h-full w-full object-contain" />
  ) : (
    <img src={asset.previewUrl || undefined} alt="" draggable={false} className="h-full w-full object-contain" />
  );

  return (
    <div className="absolute inset-0 z-40 overflow-hidden bg-black pointer-events-none">
      <div className="absolute inset-0 will-change-transform" style={toReady ? styles.from : { opacity: fromReady ? 1 : 0 }}>{media(fromClip, fromAsset, fromVideoRef, () => setFromReady(true))}</div>
      <div className="absolute inset-0 will-change-transform" style={toReady ? styles.to : { opacity: 0 }}>{media(toClip, toAsset, toVideoRef, () => setToReady(true))}</div>
      {toReady && styles.overlay && <div className="absolute inset-0" style={styles.overlay} />}
    </div>
  );
}

function PreviewPanel({
  clip,
  asset,
  playheadMs,
  isPlaying,
  aspectRatio,
  onAspectRatioChange,
  onSelectClip,
  onClearSelection,
  onBeginEdit,
  selectedClipId,
  onUpdateClip,
  onTogglePlay,
  muted,
  backgroundClip,
  backgroundAsset,
  activeTransition,
  transitionFromClip,
  transitionToClip,
  transitionFromAsset,
  transitionToAsset,
  playbackBuffering,
  onPlaybackBufferingChange,
}: {
  clip: StudioTimelineClip | null;
  asset: StudioAsset | null;
  playheadMs: number;
  isPlaying: boolean;
  aspectRatio: string;
  onAspectRatioChange: (value: string) => void;
  onSelectClip: (clipId: string) => void;
  onClearSelection: () => void;
  onBeginEdit: () => void;
  selectedClipId: string | null;
  onUpdateClip: (clipId: string, patch: Partial<StudioTimelineClip>, options?: ClipUpdateOptions) => void;
  onTogglePlay: () => void;
  muted: boolean;
  backgroundClip: StudioTimelineClip | null;
  backgroundAsset: StudioAsset | null;
  activeTransition: StudioTimelineTransition | null;
  transitionFromClip: StudioTimelineClip | null;
  transitionToClip: StudioTimelineClip | null;
  transitionFromAsset: StudioAsset | null;
  transitionToAsset: StudioAsset | null;
  playbackBuffering: boolean;
  onPlaybackBufferingChange: (buffering: boolean) => void;
}) {
  const previewRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const objectRef = useRef<HTMLElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playbackClip = clip?.kind === 'VIDEO' ? clip : backgroundClip?.kind === 'VIDEO' ? backgroundClip : null;
  const [previewSize, setPreviewSize] = useState({ width: 0, height: 0 });
  const [snapGuides, setSnapGuides] = useState<{ vertical: number[]; horizontal: number[] }>({
    vertical: [],
    horizontal: [],
  });
  const [objectBox, setObjectBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const [cropGuideRatio, setCropGuideRatio] = useState<CropGuideRatio>('1:1');
  const [cropGuideEnabled, setCropGuideEnabled] = useState(false);

  useEffect(() => {
    const node = previewRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      const rect = entry?.contentRect;
      if (!rect) return;
      setPreviewSize({ width: rect.width, height: rect.height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!videoRef.current || !playbackClip) return;
    syncMediaToTimeline(videoRef.current, playbackClip, playheadMs, isPlaying);
    videoRef.current.volume = muted || playbackClip.audioDetached ? 0 : clipAudioVolumeAt(playbackClip, playheadMs);
  }, [isPlaying, muted, playbackClip, playheadMs]);

  useEffect(() => {
    if (!videoRef.current) return;
    if (playbackClip) {
      videoRef.current.playbackRate = normalizePlaybackSpeed(playbackClip.speed);
      videoRef.current.preservesPitch = true;
      videoRef.current.volume = muted || playbackClip.audioDetached ? 0 : clipAudioVolumeAt(playbackClip, playheadMs);
    }
    videoRef.current.muted = muted || Boolean(playbackClip?.audioDetached);
    if (isPlaying) {
      videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
    }
  }, [isPlaying, asset?.previewUrl, backgroundAsset?.previewUrl, muted, playbackClip]);

  useEffect(() => {
    if (!isPlaying || !playbackClip) onPlaybackBufferingChange(false);
  }, [isPlaying, onPlaybackBufferingChange, playbackClip]);

  const handleVideoWaiting = () => {
    if (isPlaying) onPlaybackBufferingChange(true);
  };
  const handleVideoReady = () => {
    onPlaybackBufferingChange(false);
  };

  const updateObjectBox = useCallback(() => {
    if (!objectRef.current || !canvasRef.current) {
      setObjectBox(null);
      return;
    }
    const objectRect = objectRef.current.getBoundingClientRect();
    const canvasRect = canvasRef.current.getBoundingClientRect();
    setObjectBox({
      left: objectRect.left - canvasRect.left,
      top: objectRect.top - canvasRect.top,
      width: objectRect.width,
      height: objectRect.height,
    });
  }, []);

  useEffect(() => {
    const frame = requestAnimationFrame(updateObjectBox);
    return () => cancelAnimationFrame(frame);
  }, [clip, asset?.previewUrl, aspectRatio, previewSize.width, previewSize.height, updateObjectBox]);

  const requestFullscreen = () => {
    void previewRef.current?.requestFullscreen?.();
  };

  const ratio = studioAspectRatioNumber(aspectRatio);
  const canvasStyle = useMemo(() => {
    const maxWidth = Math.max(0, previewSize.width - 32);
    const maxHeight = Math.max(0, previewSize.height - 32);
    if (!maxWidth || !maxHeight) {
      return {
        aspectRatio: studioAspectRatioToCss(aspectRatio),
        width: '100%',
        height: 'auto',
      };
    }
    const availableRatio = maxWidth / maxHeight;
    if (availableRatio > ratio) {
      const height = maxHeight;
      return {
        width: height * ratio,
        height,
      };
    }
    const width = maxWidth;
    return {
      width,
      height: width / ratio,
    };
  }, [aspectRatio, previewSize.height, previewSize.width, ratio]);
  const cropGuideStyle = useMemo<CSSProperties>(() => {
    const guideRatio = studioAspectRatioNumber(cropGuideRatio);
    if (guideRatio > ratio) {
      return {
        width: '100%',
        height: `${(ratio / guideRatio) * 100}%`,
      };
    }
    return {
      width: `${(guideRatio / ratio) * 100}%`,
      height: '100%',
    };
  }, [cropGuideRatio, ratio]);

  const startObjectDrag = (event: React.PointerEvent<HTMLElement>) => {
    if (!clip || !canvasRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    onSelectClip(clip.id);
    onBeginEdit();
    const startX = event.clientX;
    const startY = event.clientY;
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const layoutScale = studioLayoutScale(canvasRect.width, canvasRect.height);
    const initialPosition = studioClipPosition(clip, canvasRect.width, canvasRect.height);
    const targetRect = event.currentTarget.getBoundingClientRect();
    const objectWidth = targetRect.width;
    const objectHeight = targetRect.height;
    const maxX = canvasRect.width;
    const maxY = canvasRect.height;

    const snapAxis = (
      rawValue: number,
      candidates: Array<{ value: number; guide: number }>,
      min: number,
      max: number,
    ) => {
      let nextValue = rawValue;
      let guide: number | null = null;
      let bestDistance = CANVAS_SNAP_THRESHOLD_PX + 1;
      candidates.forEach(candidate => {
        const distance = Math.abs(candidate.value - rawValue);
        if (distance <= CANVAS_SNAP_THRESHOLD_PX && distance < bestDistance) {
          nextValue = candidate.value;
          guide = candidate.guide;
          bestDistance = distance;
        }
      });
      return {
        value: clamp(nextValue, min, max),
        guide,
      };
    };

    const move = (moveEvent: PointerEvent) => {
      const rawX = initialPosition.x + moveEvent.clientX - startX;
      const rawY = initialPosition.y + moveEvent.clientY - startY;
      const xCandidates = [
        { value: 0, guide: canvasRect.width / 2 },
        { value: -canvasRect.width / 2 + objectWidth / 2, guide: 0 },
        { value: canvasRect.width / 2 - objectWidth / 2, guide: canvasRect.width },
      ];
      const yCandidates = [
        { value: 0, guide: canvasRect.height / 2 },
        { value: -canvasRect.height / 2 + objectHeight / 2, guide: 0 },
        { value: canvasRect.height / 2 - objectHeight / 2, guide: canvasRect.height },
      ];
      const snappedX = snapAxis(rawX, xCandidates, -maxX, maxX);
      const snappedY = snapAxis(rawY, yCandidates, -maxY, maxY);
      setSnapGuides({
        vertical: snappedX.guide !== null ? [snappedX.guide] : [],
        horizontal: snappedY.guide !== null ? [snappedY.guide] : [],
      });
      onUpdateClip(clip.id, {
        position: {
          x: Math.round(snappedX.value / layoutScale),
          y: Math.round(snappedY.value / layoutScale),
        },
        positionSpace: 'layout',
      }, { recordHistory: false });
      requestAnimationFrame(updateObjectBox);
    };

    const up = () => {
      setSnapGuides({ vertical: [], horizontal: [] });
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const startObjectResize = (event: React.PointerEvent<HTMLButtonElement>, corner: 'nw' | 'ne' | 'sw' | 'se') => {
    if (!clip || !objectBox) return;
    event.preventDefault();
    event.stopPropagation();
    onSelectClip(clip.id);
    onBeginEdit();
    const startX = event.clientX;
    const startY = event.clientY;
    const initialWidth = Math.max(1, objectBox.width);
    const initialHeight = Math.max(1, objectBox.height);
    const initialScale = clipScale(clip);
    const xSign = corner.includes('e') ? 1 : -1;
    const ySign = corner.includes('s') ? 1 : -1;

    const move = (moveEvent: PointerEvent) => {
      const nextWidth = Math.max(24, initialWidth + (moveEvent.clientX - startX) * xSign);
      const nextHeight = Math.max(24, initialHeight + (moveEvent.clientY - startY) * ySign);
      const xFactor = nextWidth / initialWidth;
      const yFactor = nextHeight / initialHeight;
      const uniformFactor = Math.max(xFactor, yFactor);
      const nextScaleX = moveEvent.shiftKey ? initialScale.x * xFactor : initialScale.x * uniformFactor;
      const nextScaleY = moveEvent.shiftKey ? initialScale.y * yFactor : initialScale.y * uniformFactor;
      onUpdateClip(clip.id, {
        scaleX: Number(clamp(nextScaleX, 0.1, 8).toFixed(3)),
        scaleY: Number(clamp(nextScaleY, 0.1, 8).toFixed(3)),
      }, { recordHistory: false });
      requestAnimationFrame(updateObjectBox);
    };

    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const canvasWidth = typeof canvasStyle.width === 'number' ? canvasStyle.width : previewSize.width || STUDIO_LAYOUT_SHORT_EDGE;
  const canvasHeight = typeof canvasStyle.height === 'number' ? canvasStyle.height : previewSize.height || STUDIO_LAYOUT_SHORT_EDGE;
  const layoutScale = studioLayoutScale(canvasWidth, canvasHeight);
  const clipPosition = clip
    ? studioClipPosition(clip, canvasWidth, canvasHeight)
    : { x: 0, y: 0 };
  const scale = clipScale(clip);
  const clipTransform = clip
    ? `translate3d(${clipPosition.x}px, ${clipPosition.y}px, 0) rotate(${clip.rotation || 0}deg) scale(${scale.x}, ${scale.y})`
    : undefined;
  const backgroundPosition = backgroundClip
    ? studioClipPosition(backgroundClip, canvasWidth, canvasHeight)
    : { x: 0, y: 0 };
  const backgroundScale = clipScale(backgroundClip);
  const backgroundTransform = backgroundClip
    ? `translate3d(${backgroundPosition.x}px, ${backgroundPosition.y}px, 0) rotate(${backgroundClip.rotation || 0}deg) scale(${backgroundScale.x}, ${backgroundScale.y})`
    : undefined;
  const showTransformHandles = Boolean(!activeTransition && clip && selectedClipId === clip.id && objectBox);
  const handlePreviewPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const target = event.target as Node;
    if (objectRef.current?.contains(target)) return;
    onClearSelection();
  };

  return (
    <div className="h-full min-h-0 flex flex-col">
      <div
        ref={previewRef}
        className="relative flex-1 min-h-0 border border-border bg-[#090909] overflow-hidden flex items-center justify-center p-4"
        onPointerDown={handlePreviewPointerDown}
      >
        <div
          className="absolute right-3 top-3 z-30 flex items-center gap-1.5"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <div className="w-24">
            <Select
              value={aspectRatio}
              onChange={(event) => onAspectRatioChange(event.target.value)}
              className="h-8 bg-surface-raised/90 px-2 py-1 text-xs"
            >
              {STUDIO_ASPECT_RATIOS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Select>
          </div>
          <Tooltip content={cropGuideEnabled ? `Ukryj podgląd kadru ${cropGuideRatio}` : `Pokaż podgląd kadru ${cropGuideRatio}`} side="bottom">
            <button
              type="button"
              aria-label={cropGuideEnabled ? `Ukryj podgląd kadru ${cropGuideRatio}` : `Pokaż podgląd kadru ${cropGuideRatio}`}
              aria-pressed={cropGuideEnabled}
              onClick={() => setCropGuideEnabled(enabled => !enabled)}
              className={[
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40',
                cropGuideEnabled
                  ? 'border-yellow-400/70 bg-yellow-400/10 text-yellow-300'
                  : 'border-border bg-surface-raised/90 text-text-secondary hover:border-border-strong hover:text-text-primary',
              ].join(' ')}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
          </Tooltip>
          <div className="w-20">
            <Select
              value={cropGuideRatio}
              onChange={(event) => {
                setCropGuideRatio(event.target.value as CropGuideRatio);
                setCropGuideEnabled(true);
              }}
              className="h-8 bg-surface-raised/90 px-2 py-1 text-xs"
            >
              {CROP_GUIDE_RATIOS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Select>
          </div>
        </div>
        {!clip || (!asset && clip.kind !== 'TEXT' && clip.kind !== 'SCRIPT') ? (
          <div className="text-center px-8">
            <Film className="w-14 h-14 text-text-disabled mx-auto mb-3" />
            <p className="text-sm text-text-muted">Przesuń czerwoną linię na klip, żeby zobaczyć podgląd.</p>
          </div>
        ) : (
          <div
            ref={canvasRef}
            className="relative bg-black overflow-hidden border border-white/10 shadow-lg flex items-center justify-center"
            style={canvasStyle}
          >
            {(clip.kind === 'TEXT' || clip.kind === 'SCRIPT') && backgroundClip?.kind === 'VIDEO' && backgroundAsset?.previewUrl && (
              <video
                ref={(node) => { videoRef.current = node; }}
                src={backgroundAsset.previewUrl}
                playsInline
                preload="auto"
                onLoadStart={handleVideoWaiting}
                onWaiting={handleVideoWaiting}
                onStalled={handleVideoWaiting}
                onPlaying={handleVideoReady}
                onCanPlay={handleVideoReady}
                className="absolute max-w-full max-h-full object-contain select-none"
                style={{ transform: backgroundTransform }}
              />
            )}
            {(clip.kind === 'TEXT' || clip.kind === 'SCRIPT') && backgroundClip?.kind === 'IMAGE' && backgroundAsset?.previewUrl && (
              <img
                src={backgroundAsset.previewUrl}
                alt=""
                draggable={false}
                className="absolute max-w-full max-h-full object-contain select-none"
                style={{ transform: backgroundTransform }}
              />
            )}
            {clip.kind === 'VIDEO' && asset?.previewUrl ? (
              <video
                ref={(node) => {
                  videoRef.current = node;
                  objectRef.current = node;
                }}
                src={asset.previewUrl}
                playsInline
                preload="auto"
                onLoadStart={handleVideoWaiting}
                onWaiting={handleVideoWaiting}
                onStalled={handleVideoWaiting}
                onPlaying={handleVideoReady}
                onCanPlay={handleVideoReady}
                onPointerDown={startObjectDrag}
                onLoadedMetadata={updateObjectBox}
                className="max-w-full max-h-full object-contain cursor-move touch-none select-none"
                style={{ transform: clipTransform }}
              />
            ) : clip.kind === 'IMAGE' && asset?.previewUrl ? (
              <img
                ref={(node) => { objectRef.current = node; }}
                src={asset.previewUrl}
                alt=""
                draggable={false}
                onPointerDown={startObjectDrag}
                onLoad={updateObjectBox}
                className="max-w-full max-h-full object-contain cursor-move touch-none select-none"
                style={{ transform: clipTransform }}
              />
            ) : (
              <div
                ref={(node) => { objectRef.current = node; }}
                onPointerDown={startObjectDrag}
                className="max-w-[86%] cursor-move touch-none select-none whitespace-pre-wrap"
                style={{
                  transform: clipTransform,
                  fontFamily: `'${clip.fontFamily || 'Inter'}', sans-serif`,
                  fontSize: `${(clip.fontSize || 48) * layoutScale}px`,
                  fontWeight: clip.fontWeight || 700,
                  fontStyle: clip.fontStyle || 'normal',
                  textDecoration: clip.textDecoration || 'none',
                  textAlign: clip.textAlign || 'center',
                  color: clip.textColor || '#ffffff',
                  backgroundColor: `${clip.textBackgroundColor || '#000000'}${Math.round(((clip.textBackgroundOpacity ?? 70) / 100) * 255).toString(16).padStart(2, '0')}`,
                  borderRadius: `${(clip.textBorderRadius ?? 16) * layoutScale}px`,
                  padding: `${(clip.textPadding ?? 16) * layoutScale}px`,
                  letterSpacing: `${(clip.letterSpacing || 0) * layoutScale}px`,
                  lineHeight: clip.lineHeight || 1.15,
                  textShadow: clip.textShadow ? `0 ${3 * layoutScale}px ${12 * layoutScale}px rgba(0,0,0,.85)` : undefined,
                  WebkitTextStroke: clip.textStrokeWidth ? `${clip.textStrokeWidth * layoutScale}px ${clip.textStrokeColor || '#000000'}` : undefined,
                }}
              >
                <p className="whitespace-pre-wrap line-clamp-[8]">
                  {clip.dynamicCaption ? studioClipTextAt(clip, playheadMs) : clip.content || asset?.content || clip.name}
                </p>
              </div>
            )}

            {clip.content && clip.kind !== 'TEXT' && clip.kind !== 'SCRIPT' && (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-6 max-w-[86%] rounded-lg bg-black/70 px-4 py-2 text-center text-sm text-white">
                {clip.content}
              </div>
            )}
            {activeTransition && transitionFromClip && transitionToClip && transitionFromAsset && transitionToAsset && (
              <TransitionPreview
                transition={activeTransition}
                fromClip={transitionFromClip}
                toClip={transitionToClip}
                fromAsset={transitionFromAsset}
                toAsset={transitionToAsset}
                playheadMs={playheadMs}
                isPlaying={isPlaying}
              />
            )}
            {playbackBuffering && playbackClip && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 pointer-events-none">
                <div className="flex items-center gap-2 rounded-lg bg-surface-raised/95 px-3 py-2 text-xs text-text-primary shadow-xl">
                  <Loader2 className="h-4 w-4 animate-spin text-accent-primary" />
                  Buforowanie klipu...
                </div>
              </div>
            )}
            {showTransformHandles && objectBox && (
              <div
                className="absolute border border-accent-primary/90 pointer-events-none"
                style={{
                  left: objectBox.left,
                  top: objectBox.top,
                  width: objectBox.width,
                  height: objectBox.height,
                }}
              >
                {(['nw', 'ne', 'sw', 'se'] as const).map(corner => (
                  <button
                    key={corner}
                    type="button"
                    aria-label="Zmień rozmiar elementu"
                    onPointerDown={(event) => startObjectResize(event, corner)}
                    className={[
                      'absolute w-3 h-3 rounded-sm bg-accent-primary border border-white pointer-events-auto',
                      corner === 'nw' ? '-left-1.5 -top-1.5 cursor-nwse-resize' : '',
                      corner === 'ne' ? '-right-1.5 -top-1.5 cursor-nesw-resize' : '',
                      corner === 'sw' ? '-left-1.5 -bottom-1.5 cursor-nesw-resize' : '',
                      corner === 'se' ? '-right-1.5 -bottom-1.5 cursor-nwse-resize' : '',
                    ].join(' ')}
                  />
                ))}
              </div>
            )}
            {snapGuides.vertical.map(guide => (
              <div
                key={`v-${guide}`}
                className="absolute top-0 bottom-0 w-px bg-accent-primary/85 shadow-[0_0_0_1px_rgba(96,165,250,0.25)] pointer-events-none"
                style={{ left: guide }}
              />
            ))}
            {snapGuides.horizontal.map(guide => (
              <div
                key={`h-${guide}`}
                className="absolute left-0 right-0 h-px bg-accent-primary/85 shadow-[0_0_0_1px_rgba(96,165,250,0.25)] pointer-events-none"
                style={{ top: guide }}
              />
            ))}
            {cropGuideEnabled && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 border border-dashed border-yellow-300/90 bg-black/15 shadow-[0_0_0_1px_rgba(0,0,0,0.18)]"
                style={cropGuideStyle}
              />
            )}
          </div>
        )}
      </div>
      <div className="h-12 shrink-0 flex items-center justify-center gap-3">
        <StudioIconButton
          label={isPlaying ? 'Pauza' : 'Play'}
          icon={isPlaying ? <Pause /> : <Play />}
          onClick={onTogglePlay}
        />
        <span className="text-xs font-mono text-text-secondary min-w-[42px] text-center">{formatTime(playheadMs)}</span>
        <StudioIconButton
          label="Tryb pełnoekranowy podglądu"
          icon={<Expand />}
          onClick={requestFullscreen}
        />
      </div>
    </div>
  );
}

function AudioPlaybackTrack({
  clip,
  asset,
  playheadMs,
  isPlaying,
  muted,
}: {
  clip: StudioTimelineClip;
  asset: StudioAsset;
  playheadMs: number;
  isPlaying: boolean;
  muted: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const active = playheadMs >= clip.startMs && playheadMs < clip.startMs + clip.durationMs;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    syncMediaToTimeline(audio, clip, playheadMs, isPlaying && active, 0.35);
    audio.volume = muted ? 0 : clipAudioVolumeAt(clip, playheadMs);
    if (!active || !isPlaying) {
      audio.pause();
      return;
    }
    if (audio.paused && audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      void audio.play().catch(() => {});
    }
  }, [active, clip, isPlaying, muted, playheadMs]);

  return <audio ref={audioRef} src={asset.previewUrl || undefined} preload={clip.kind === 'AUDIO' || active ? 'auto' : 'metadata'} aria-hidden="true" />;
}

function loadStudioImage(url: string) {
  return new Promise<HTMLImageElement | null>((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

function loadStudioVideo(url: string) {
  return new Promise<HTMLVideoElement | null>((resolve) => {
    const video = document.createElement('video');
    if (url.startsWith('http') && new URL(url, window.location.href).origin !== window.location.origin) {
      video.crossOrigin = 'anonymous';
    }
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    const done = (value: HTMLVideoElement | null) => {
      video.onloadedmetadata = null;
      video.onerror = null;
      resolve(value);
    };
    video.onloadedmetadata = () => done(video);
    video.onerror = () => done(null);
    video.src = url;
    video.load();
  });
}

function drawMissingAsset(ctx: CanvasRenderingContext2D, width: number, height: number, clip: StudioTimelineClip) {
  const clipScaleValue = clipScale(clip);
  const position = studioClipPosition(clip, width, height);
  ctx.save();
  ctx.translate(width / 2 + position.x, height / 2 + position.y);
  ctx.rotate((clip.rotation || 0) * Math.PI / 180);
  ctx.scale(clipScaleValue.x, clipScaleValue.y);
  ctx.fillStyle = 'rgba(30, 30, 30, 0.9)';
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
  ctx.lineWidth = 2;
  ctx.fillRect(-260, -70, 520, 140);
  ctx.strokeRect(-260, -70, 520, 140);
  ctx.fillStyle = '#f4f4f5';
  ctx.font = '600 28px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(clip.name, 0, 0, 460);
  ctx.restore();
}

function drawStudioMedia(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  width: number,
  height: number,
  clip: StudioTimelineClip,
) {
  const scale = Math.min(width / sourceWidth, height / sourceHeight);
  const clipScaleValue = clipScale(clip);
  const position = studioClipPosition(clip, width, height);
  const targetWidth = sourceWidth * scale * clipScaleValue.x;
  const targetHeight = sourceHeight * scale * clipScaleValue.y;
  ctx.save();
  ctx.translate(width / 2 + position.x, height / 2 + position.y);
  ctx.rotate((clip.rotation || 0) * Math.PI / 180);
  ctx.drawImage(source, -targetWidth / 2, -targetHeight / 2, targetWidth, targetHeight);
  ctx.restore();
}

function drawStudioText(ctx: CanvasRenderingContext2D, width: number, height: number, clip: StudioTimelineClip, timelineMs: number, asset?: StudioAsset) {
  const text = clip.dynamicCaption ? studioClipTextAt(clip, timelineMs) : clip.content || asset?.content || clip.name;
  const clipScaleValue = clipScale(clip);
  const layoutScale = studioLayoutScale(width, height);
  const position = studioClipPosition(clip, width, height);
  const fontSize = Math.max(8, (clip.fontSize || 48) * layoutScale);
  const padding = Math.max(0, (clip.textPadding ?? 16) * layoutScale);
  const maxTextWidth = width * 0.86;
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';
  ctx.font = `${clip.fontStyle || 'normal'} ${clip.fontWeight || 700} ${fontSize}px '${clip.fontFamily || 'Inter'}', sans-serif`;
  words.forEach(word => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (currentLine && ctx.measureText(candidate).width > maxTextWidth) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = candidate;
    }
  });
  if (currentLine) lines.push(currentLine);
  const lineHeight = fontSize * (clip.lineHeight || 1.15);
  const textWidth = Math.min(maxTextWidth, Math.max(...lines.map(line => ctx.measureText(line).width), fontSize));
  const boxWidth = textWidth + padding * 2;
  const boxHeight = lines.length * lineHeight + padding * 2;
  const backgroundHex = (clip.textBackgroundColor || '#000000').replace('#', '');
  const red = parseInt(backgroundHex.slice(0, 2), 16) || 0;
  const green = parseInt(backgroundHex.slice(2, 4), 16) || 0;
  const blue = parseInt(backgroundHex.slice(4, 6), 16) || 0;
  ctx.save();
  ctx.translate(width / 2 + position.x, height / 2 + position.y);
  ctx.rotate((clip.rotation || 0) * Math.PI / 180);
  ctx.scale(clipScaleValue.x, clipScaleValue.y);
  ctx.fillStyle = `rgba(${red}, ${green}, ${blue}, ${(clip.textBackgroundOpacity ?? 70) / 100})`;
  ctx.beginPath();
  ctx.roundRect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight, Math.max(0, (clip.textBorderRadius ?? 16) * layoutScale));
  ctx.fill();
  ctx.fillStyle = clip.textColor || '#ffffff';
  ctx.font = `${clip.fontStyle || 'normal'} ${clip.fontWeight || 700} ${fontSize}px '${clip.fontFamily || 'Inter'}', sans-serif`;
  ctx.textAlign = clip.textAlign || 'center';
  ctx.textBaseline = 'middle';
  if (clip.textShadow) {
    ctx.shadowColor = 'rgba(0,0,0,.85)';
    ctx.shadowBlur = 12 * layoutScale;
    ctx.shadowOffsetY = 3 * layoutScale;
  }
  const alignX = clip.textAlign === 'left' ? -textWidth / 2 : clip.textAlign === 'right' ? textWidth / 2 : 0;
  lines.forEach((line, index) => {
    const y = (index - (lines.length - 1) / 2) * lineHeight;
    if (clip.textStrokeWidth) {
      ctx.lineWidth = clip.textStrokeWidth * 2 * layoutScale;
      ctx.strokeStyle = clip.textStrokeColor || '#000000';
      ctx.strokeText(line, alignX, y, maxTextWidth);
    }
    ctx.fillText(line, alignX, y, maxTextWidth);
  });
  ctx.restore();
}

function drawStudioTransition(
  ctx: CanvasRenderingContext2D,
  fromCanvas: HTMLCanvasElement,
  toCanvas: HTMLCanvasElement,
  type: string,
  progress: number,
  width: number,
  height: number,
) {
  const inverse = 1 - progress;
  const drawAlpha = (source: HTMLCanvasElement, alpha: number) => {
    ctx.save();
    ctx.globalAlpha = clamp(alpha, 0, 1);
    ctx.drawImage(source, 0, 0);
    ctx.restore();
  };
  const drawTransformed = (source: HTMLCanvasElement, alpha: number, x: number, y: number, scale = 1, rotation = 0) => {
    ctx.save();
    ctx.globalAlpha = clamp(alpha, 0, 1);
    ctx.translate(width / 2 + x, height / 2 + y);
    ctx.rotate(rotation);
    ctx.scale(scale, scale);
    ctx.drawImage(source, -width / 2, -height / 2);
    ctx.restore();
  };

  if (type.startsWith('slide-') || type.startsWith('push-')) {
    const horizontal = type.endsWith('left') || type.endsWith('right');
    const direction = type.endsWith('left') || type.endsWith('up') ? -1 : 1;
    const distance = horizontal ? width : height;
    const fromDistance = type.startsWith('push-') ? distance * 0.55 : distance;
    drawTransformed(fromCanvas, 1, horizontal ? direction * progress * fromDistance : 0, horizontal ? 0 : direction * progress * fromDistance);
    drawTransformed(toCanvas, 1, horizontal ? -direction * inverse * distance : 0, horizontal ? 0 : -direction * inverse * distance);
    return;
  }
  if (type.startsWith('wipe-')) {
    ctx.drawImage(fromCanvas, 0, 0);
    if (type === 'wipe-left') ctx.drawImage(toCanvas, 0, 0, width * progress, height, 0, 0, width * progress, height);
    if (type === 'wipe-right') ctx.drawImage(toCanvas, width * inverse, 0, width * progress, height, width * inverse, 0, width * progress, height);
    if (type === 'wipe-up') ctx.drawImage(toCanvas, 0, 0, width, height * progress, 0, 0, width, height * progress);
    if (type === 'wipe-down') ctx.drawImage(toCanvas, 0, height * inverse, width, height * progress, 0, height * inverse, width, height * progress);
    return;
  }
  if (type === 'zoom-in' || type === 'zoom-out') {
    const zoomIn = type === 'zoom-in';
    drawTransformed(fromCanvas, inverse, 0, 0, zoomIn ? 1 + progress * 0.35 : 1 - progress * 0.3);
    drawTransformed(toCanvas, progress, 0, 0, zoomIn ? 0.7 + progress * 0.3 : 1.35 - progress * 0.35);
    return;
  }
  if (type === 'spin') {
    drawTransformed(fromCanvas, inverse, 0, 0, 1 - progress * 0.15, progress * 0.6);
    drawTransformed(toCanvas, progress, 0, 0, 0.85 + progress * 0.15, (progress - 1) * 0.6);
    return;
  }
  if (type === 'blur') {
    ctx.save();
    ctx.filter = `blur(${progress * 18}px)`;
    drawAlpha(fromCanvas, inverse);
    ctx.filter = `blur(${inverse * 18}px)`;
    drawAlpha(toCanvas, progress);
    ctx.restore();
    return;
  }
  if (type === 'pulse') {
    const pulse = Math.sin(progress * Math.PI) * 0.12;
    drawTransformed(fromCanvas, inverse, 0, 0, 1 + pulse);
    drawTransformed(toCanvas, progress, 0, 0, 1.12 - pulse);
    return;
  }

  drawAlpha(fromCanvas, inverse);
  drawAlpha(toCanvas, progress);
  if (type === 'fade-black' || type === 'fade-white' || type === 'flash') {
    const peak = type === 'flash'
      ? Math.max(0, 1 - Math.abs(progress - 0.5) * 7)
      : 1 - Math.abs(progress - 0.5) * 2;
    ctx.save();
    ctx.globalAlpha = peak;
    ctx.fillStyle = type === 'fade-black' ? '#000000' : '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }
}

async function renderStudioProjectToMp4({
  durationMs,
  clips,
  layers,
  transitions,
  assets,
  aspectRatio,
  quality,
  onProgress,
  signal,
}: {
  durationMs: number;
  clips: StudioTimelineClip[];
  layers: StudioTimelineLayer[];
  transitions: StudioTimelineTransition[];
  assets: StudioAsset[];
  aspectRatio: string;
  quality: StudioExportQuality;
  onProgress?: (progress: number, stage: string) => void;
  signal?: AbortSignal;
}) {
  if (signal?.aborted) throw new DOMException('Eksport anulowany.', 'AbortError');
  const mimeType = bestMp4MimeType();
  if (!mimeType) {
    throw new Error('Ta przeglądarka nie udostępnia kodera MP4. Do finalnego renderu MP4 potrzebny będzie renderer po stronie backendu.');
  }

  const qualitySettings = STUDIO_EXPORT_QUALITIES.find(item => item.value === quality) || STUDIO_EXPORT_QUALITIES[2];
  const { width, height } = exportVideoSize(aspectRatio, qualitySettings.shortEdge);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Nie udało się przygotować canvasu eksportu.');

  const assetMap = new Map(assets.map(asset => [asset.id, asset]));
  const visibleClips = clips.filter(clip => !layerSetting(layers, clip.layer).hidden);
  clips.filter(clip => clip.kind === 'TEXT' || clip.kind === 'SCRIPT').forEach(clip => ensureGoogleFont(clip.fontFamily || 'Inter'));
  await document.fonts?.ready;
  onProgress?.(3, 'Przygotowywanie zasobów');
  const imageMap = new Map<string, HTMLImageElement | null>();
  const videoMap = new Map<string, HTMLVideoElement | null>();
  await Promise.all(visibleClips.map(async (clip) => {
    const asset = assetMap.get(clip.assetId);
    if (!asset?.previewUrl) return;
    if (clip.kind === 'IMAGE') {
      imageMap.set(asset.id, await loadStudioImage(asset.previewUrl));
    }
    if (clip.kind === 'VIDEO') {
      videoMap.set(asset.id, await loadStudioVideo(asset.previewUrl));
    }
  }));
  if (signal?.aborted) throw new DOMException('Eksport anulowany.', 'AbortError');

  onProgress?.(8, 'Przygotowywanie dźwięku');
  const audioContext = new AudioContext();
  await audioContext.resume();
  const audioDestination = audioContext.createMediaStreamDestination();
  const audioTracks = await Promise.all(visibleClips
    .filter(clip => (
      (clip.kind === 'AUDIO' || (clip.kind === 'VIDEO' && !clip.audioDetached))
      && !layerSetting(layers, clip.layer).muted
      && clipAudioSettings(clip).volumeDb > -60
    ))
    .map(async clip => {
      const asset = assetMap.get(clip.assetId);
      if (!asset?.previewUrl) return null;
      const media = document.createElement(clip.kind === 'AUDIO' ? 'audio' : 'video');
      media.preload = 'auto';
      if (media instanceof HTMLVideoElement) media.playsInline = true;
      media.src = asset.previewUrl;
      media.playbackRate = normalizePlaybackSpeed(clip.speed);
      media.preservesPitch = true;
      await new Promise<void>(resolve => {
        const done = () => {
          media.removeEventListener('canplay', done);
          media.removeEventListener('error', done);
          resolve();
        };
        media.addEventListener('canplay', done, { once: true });
        media.addEventListener('error', done, { once: true });
        media.load();
      });
      try {
        const source = audioContext.createMediaElementSource(media);
        const gain = audioContext.createGain();
        source.connect(gain).connect(audioDestination);
        return { clip, media, gain };
      } catch {
        return null;
      }
    }));
  const usableAudioTracks = audioTracks.filter((track): track is NonNullable<typeof track> => Boolean(track));
  const canvasStream = canvas.captureStream(30);
  const stream = new MediaStream([
    ...canvasStream.getVideoTracks(),
    ...audioDestination.stream.getAudioTracks(),
  ]);
  const recorder = new MediaRecorder(stream, {
    mimeType,
    videoBitsPerSecond: qualitySettings.videoBitsPerSecond,
    audioBitsPerSecond: 192_000,
  });
  const chunks: BlobPart[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };

  const fromTransitionCanvas = document.createElement('canvas');
  fromTransitionCanvas.width = width;
  fromTransitionCanvas.height = height;
  const toTransitionCanvas = document.createElement('canvas');
  toTransitionCanvas.width = width;
  toTransitionCanvas.height = height;
  const fromTransitionContext = fromTransitionCanvas.getContext('2d');
  const toTransitionContext = toTransitionCanvas.getContext('2d');

  const drawClip = async (target: CanvasRenderingContext2D, clip: StudioTimelineClip, timelineMs: number) => {
    const asset = assetMap.get(clip.assetId);
    if (clip.kind === 'IMAGE' && asset) {
      const image = imageMap.get(asset.id);
      if (image) drawStudioMedia(target, image, image.naturalWidth, image.naturalHeight, width, height, clip);
      else drawMissingAsset(target, width, height, clip);
      return;
    }
    if (clip.kind === 'VIDEO' && asset) {
      const video = videoMap.get(asset.id);
      if (video) {
        syncMediaToTimeline(video, clip, timelineMs, true, 0.2);
        if (video.paused) void video.play().catch(() => {});
        drawStudioMedia(target, video, video.videoWidth || width, video.videoHeight || height, width, height, clip);
      } else {
        drawMissingAsset(target, width, height, clip);
      }
      return;
    }
    drawStudioText(target, width, height, clip, timelineMs, asset);
  };

  const drawFrame = async (timelineMs: number) => {
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);
    const activeClips = visibleClips
      .filter(clip => clip.kind !== 'AUDIO' && timelineMs >= clip.startMs && timelineMs < clip.startMs + clip.durationMs)
      .sort((a, b) => a.layer - b.layer);

    const activeTransition = transitions.find(transition => {
      const transitionClip = visibleClips.find(clip => clip.id === transition.fromClipId);
      return Boolean(transitionClip)
        && timelineMs >= transition.boundaryMs - transition.durationMs / 2
        && timelineMs <= transition.boundaryMs + transition.durationMs / 2;
    });
    const fromClip = activeTransition ? clips.find(clip => clip.id === activeTransition.fromClipId) : null;
    const toClip = activeTransition ? clips.find(clip => clip.id === activeTransition.toClipId) : null;
    const transitionLayer = fromClip?.layer;
    const layersToDraw = Array.from(new Set([
      ...activeClips.map(clip => clip.layer),
      ...(transitionLayer !== undefined ? [transitionLayer] : []),
    ])).sort((a, b) => a - b);

    for (const layer of layersToDraw) {
      if (activeTransition && fromClip && toClip && layer === transitionLayer && fromTransitionContext && toTransitionContext) {
        fromTransitionContext.clearRect(0, 0, width, height);
        toTransitionContext.clearRect(0, 0, width, height);
        await drawClip(fromTransitionContext, fromClip, timelineMs);
        await drawClip(toTransitionContext, toClip, timelineMs);
        const progress = clamp(
          (timelineMs - (activeTransition.boundaryMs - activeTransition.durationMs / 2)) / activeTransition.durationMs,
          0,
          1,
        );
        drawStudioTransition(ctx, fromTransitionCanvas, toTransitionCanvas, activeTransition.type, progress, width, height);
        continue;
      }
      for (const clip of activeClips.filter(item => item.layer === layer)) {
        await drawClip(ctx, clip, timelineMs);
      }
    }
  };

  return new Promise<Blob>((resolve, reject) => {
    let cleaned = false;
    let settled = false;
    let animationFrameId: number | null = null;
    let cancelExport: () => void = () => {};
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
      signal?.removeEventListener('abort', cancelExport);
      stream.getTracks().forEach(track => track.stop());
      usableAudioTracks.forEach(track => {
        track.media.pause();
        track.media.removeAttribute('src');
        track.media.load();
      });
      videoMap.forEach(video => {
        video?.pause();
        video?.removeAttribute('src');
        video?.load();
      });
      void audioContext.close();
    };
    const rejectOnce = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    cancelExport = () => {
      recorder.onstop = null;
      if (recorder.state !== 'inactive') recorder.stop();
      rejectOnce(new DOMException('Eksport anulowany.', 'AbortError'));
    };
    recorder.onerror = () => {
      rejectOnce(new Error('Nie udało się nagrać pliku MP4.'));
    };
    recorder.onstop = () => {
      if (settled) return;
      settled = true;
      cleanup();
      onProgress?.(99, 'Finalizowanie pliku');
      resolve(new Blob(chunks, { type: mimeType }));
    };
    signal?.addEventListener('abort', cancelExport, { once: true });
    if (signal?.aborted) {
      cancelExport();
      return;
    }
    recorder.start(500);
    const startedAt = performance.now();
    const tick = async (timestamp: number) => {
      try {
        if (signal?.aborted) {
          cancelExport();
          return;
        }
        const elapsed = Math.min(durationMs, timestamp - startedAt);
        usableAudioTracks.forEach(({ clip, media, gain }) => {
          const active = elapsed >= clip.startMs && elapsed < clipEnd(clip);
          if (!active) {
            if (!media.paused) media.pause();
            return;
          }
          const targetSeconds = clipSourceTimeMsAt(clip, elapsed) / 1000;
          media.playbackRate = normalizePlaybackSpeed(clip.speed);
          gain.gain.setValueAtTime(clipAudioVolumeAt(clip, elapsed), audioContext.currentTime);
          if (Math.abs(media.currentTime - targetSeconds) > 0.2) media.currentTime = targetSeconds;
          if (media.paused) void media.play().catch(() => {});
        });
        await drawFrame(elapsed);
        if (signal?.aborted) {
          cancelExport();
          return;
        }
        onProgress?.(10 + (elapsed / Math.max(1, durationMs)) * 88, 'Renderowanie wideo');
        if (elapsed >= durationMs) {
          onProgress?.(99, 'Finalizowanie pliku');
          recorder.stop();
          return;
        }
        animationFrameId = requestAnimationFrame(tick);
      } catch (error) {
        recorder.onstop = null;
        if (recorder.state !== 'inactive') recorder.stop();
        rejectOnce(error instanceof Error ? error : new Error('Nie udało się wyrenderować MP4.'));
      }
    };
    animationFrameId = requestAnimationFrame(tick);
  });
}

function isStudioExportAbort(error: unknown) {
  return (error instanceof DOMException && error.name === 'AbortError')
    || (typeof error === 'object' && error !== null && 'code' in error && error.code === 'ERR_CANCELED');
}

function ExportProjectModal({
  open,
  onClose,
  project,
  clips,
  layers,
  transitions,
  assets,
  aspectRatio,
}: {
  open: boolean;
  onClose: () => void;
  project: { id: string; name: string };
  clips: StudioTimelineClip[];
  layers: StudioTimelineLayer[];
  transitions: StudioTimelineTransition[];
  assets: StudioAsset[];
  aspectRatio: string;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [localExporting, setLocalExporting] = useState(false);
  const [quality, setQuality] = useState<StudioExportQuality>('1080p');
  const [progress, setProgress] = useState(0);
  const [progressStage, setProgressStage] = useState('');
  const exportAbortRef = useRef<AbortController | null>(null);
  const filename = `${slugifyFilename(project.name)}.mp4`;
  const exportDurationMs = Math.max(
    1000,
    ...clips
      .filter(clip => !layerSetting(layers, clip.layer).hidden)
      .map(clip => clipEnd(clip)),
  );
  const renderMp4 = (signal: AbortSignal) => renderStudioProjectToMp4({
    durationMs: exportDurationMs,
    clips,
    layers,
    transitions,
    assets,
    aspectRatio,
    quality,
    signal,
    onProgress: (value, stage) => {
      setProgress(value);
      setProgressStage(stage);
    },
  });
  const driveMutation = useMutation({
    mutationFn: async () => {
      const controller = new AbortController();
      exportAbortRef.current = controller;
      setProgress(0);
      setProgressStage('Przygotowywanie eksportu');
      setError(null);
      try {
        const blob = await renderMp4(controller.signal);
        const file = new File([blob], filename, { type: blob.type || 'video/mp4' });
        setProgressStage('Przesyłanie na Dysk Google');
        return (await studioApi.uploadAsset(project.id, file, exportDurationMs, uploadProgress => {
          setProgress(99 + uploadProgress * 0.01);
        }, controller.signal)).data.data;
      } finally {
        if (exportAbortRef.current === controller) exportAbortRef.current = null;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['studio', 'project', project.id] });
      setError(null);
      setProgress(100);
    },
    onError: (mutationError) => {
      setProgressStage(isStudioExportAbort(mutationError) ? 'Eksport anulowany' : 'Błąd eksportu');
      setError(isStudioExportAbort(mutationError) ? null : errorMessage(mutationError));
    },
  });
  const exporting = localExporting || driveMutation.isPending;

  const downloadLocal = async () => {
    try {
      const controller = new AbortController();
      exportAbortRef.current = controller;
      setLocalExporting(true);
      setProgress(0);
      setProgressStage('Przygotowywanie eksportu');
      setError(null);
      const blob = await renderMp4(controller.signal);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setProgress(100);
      setProgressStage('Gotowe');
      setError(null);
    } catch (renderError) {
      setProgressStage(isStudioExportAbort(renderError) ? 'Eksport anulowany' : 'Błąd eksportu');
      setError(isStudioExportAbort(renderError) ? null : errorMessage(renderError));
    } finally {
      exportAbortRef.current = null;
      setLocalExporting(false);
    }
  };

  const cancelExport = () => exportAbortRef.current?.abort();

  return (
    <Modal
      isOpen={open}
      onClose={exporting ? () => {} : onClose}
      title="Eksportuj"
      description="Renderuje aktualną oś czasu do pliku MP4."
      footer={(
        <div className="flex items-center gap-2">
          {exporting ? (
            <Button variant="secondary" onClick={cancelExport}>Zatrzymaj eksport</Button>
          ) : (
            <>
              <Button onClick={() => void downloadLocal()} leftIcon={<Download />}>Pobierz lokalnie</Button>
              <Button variant="secondary" onClick={() => driveMutation.mutate()} leftIcon={<CloudUpload />}>Zapisz na Dysku Google</Button>
            </>
          )}
        </div>
      )}
    >
      <div className="space-y-3">
        <div className="rounded-card border border-border bg-surface-base/60 p-4 flex items-start gap-3">
          <FileVideo className="w-5 h-5 text-accent-primary shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text-primary truncate">{filename}</p>
            <p className="text-xs text-text-muted mt-1">
              Render kończy się na ostatnim widocznym elemencie osi czasu.
            </p>
          </div>
        </div>
        <FormField label="Jakość eksportu">
          <Select
            value={quality}
            disabled={exporting}
            onChange={event => setQuality(event.target.value as StudioExportQuality)}
          >
            {STUDIO_EXPORT_QUALITIES.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Select>
        </FormField>
        {exporting && (
          <ProgressBar
            value={progress}
            label={progressStage || 'Eksportowanie'}
            showPercent
            aria-label="Postęp eksportu wideo"
          />
        )}
        {error && <p className="text-sm text-danger">{error}</p>}
        {driveMutation.isSuccess && (
          <p className="text-sm text-success">Plik MP4 został zapisany w folderze projektu na Dysku Google.</p>
        )}
      </div>
    </Modal>
  );
}

type StudioTextNumericField = 'fontSize' | 'textStrokeWidth' | 'textBackgroundOpacity' | 'textBorderRadius'
  | 'textPadding' | 'letterSpacing' | 'lineHeight';

function TextAssetOptions({
  clip,
  onUpdate,
}: {
  clip: StudioTimelineClip;
  onUpdate: (patch: Partial<StudioTimelineClip>, options?: { applyAll?: boolean; relativeFields?: StudioTextNumericField[] }) => void;
}) {
  const [applyAll, setApplyAll] = useState(false);
  useEffect(() => {
    if (clip.fontFamily) ensureGoogleFont(clip.fontFamily);
  }, [clip.fontFamily]);
  const update = (patch: Partial<StudioTimelineClip>, relativeFields?: StudioTextNumericField[]) => {
    onUpdate(patch, { applyAll, relativeFields });
  };
  return (
    <div className="space-y-3">
      <div>
        <p className="truncate text-sm font-semibold text-text-primary">{clip.name}</p>
        <p className="mt-1 text-xs text-text-muted">Tekst - warstwa {clip.layer}</p>
      </div>
      <div className="flex items-center gap-1.5">
        <Checkbox
          checked={applyAll}
          onChange={event => setApplyAll(event.target.checked)}
          label="Zastosuj do wszystkich"
        />
        <Tooltip content="Kolejne zmiany wyglądu obejmą wszystkie teksty. Wartości liczbowe zmienią się o tę samą różnicę." side="left">
          <button type="button" className="rounded text-text-muted hover:text-text-primary" aria-label="Jak działa zastosowanie do wszystkich">
            <Info className="h-3.5 w-3.5" />
          </button>
        </Tooltip>
      </div>
      {clip.captionCues?.length ? (
        <div className="flex items-center gap-1.5">
          <Checkbox
            checked={Boolean(clip.dynamicCaption)}
            onChange={event => update({ dynamicCaption: event.target.checked })}
            label="Dynamiczne napisy"
          />
          <Tooltip content="Pokazuje krótkie frazy dokładnie w czasie wypowiadanych słów." side="left">
            <button type="button" className="rounded text-text-muted hover:text-text-primary" aria-label="Jak działają dynamiczne napisy">
              <Info className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        </div>
      ) : null}
      <FormField label="Treść">
        <Textarea value={clip.content || ''} rows={4} onChange={event => onUpdate({ content: event.target.value })} />
      </FormField>
      <FormField label="Font Google">
        <Select value={clip.fontFamily || 'Inter'} onChange={event => update({ fontFamily: event.target.value })}>
          {STUDIO_GOOGLE_FONTS.map(font => <option key={font} value={font}>{font}</option>)}
        </Select>
      </FormField>
      <div className="grid grid-cols-2 gap-2">
        <FormField label="Wielkość">
          <Input type="number" min={8} max={240} value={clip.fontSize || 48} onChange={event => update({ fontSize: Number(event.target.value) || 48 }, ['fontSize'])} />
        </FormField>
        <FormField label="Grubość">
          <Select value={String(clip.fontWeight || 700)} onChange={event => update({ fontWeight: Number(event.target.value) })}>
            {[300, 400, 500, 600, 700, 800, 900].map(weight => <option key={weight} value={weight}>{weight}</option>)}
          </Select>
        </FormField>
      </div>
      <div className="flex items-center gap-1">
        <StudioIconButton label="Pogrubienie" icon={<Bold />} active={(clip.fontWeight || 700) >= 700} onClick={() => update({ fontWeight: (clip.fontWeight || 700) >= 700 ? 400 : 700 })} />
        <StudioIconButton label="Kursywa" icon={<Italic />} active={clip.fontStyle === 'italic'} onClick={() => update({ fontStyle: clip.fontStyle === 'italic' ? 'normal' : 'italic' })} />
        <StudioIconButton label="Podkreślenie" icon={<Underline />} active={clip.textDecoration === 'underline'} onClick={() => update({ textDecoration: clip.textDecoration === 'underline' ? 'none' : 'underline' })} />
        <span className="mx-1 h-5 w-px bg-border-subtle" />
        <StudioIconButton label="Do lewej" icon={<AlignLeft />} active={clip.textAlign === 'left'} onClick={() => update({ textAlign: 'left' })} />
        <StudioIconButton label="Wyśrodkuj" icon={<AlignCenter />} active={!clip.textAlign || clip.textAlign === 'center'} onClick={() => update({ textAlign: 'center' })} />
        <StudioIconButton label="Do prawej" icon={<AlignRight />} active={clip.textAlign === 'right'} onClick={() => update({ textAlign: 'right' })} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <FormField label="Kolor tekstu"><Input type="color" value={clip.textColor || '#ffffff'} onChange={event => update({ textColor: event.target.value })} className="h-9 p-1" /></FormField>
        <FormField label="Kolor tła"><Input type="color" value={clip.textBackgroundColor || '#000000'} onChange={event => update({ textBackgroundColor: event.target.value })} className="h-9 p-1" /></FormField>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <FormField label="Obrys"><Input type="number" min={0} max={12} step={0.5} value={clip.textStrokeWidth || 0} onChange={event => update({ textStrokeWidth: Number(event.target.value) || 0 }, ['textStrokeWidth'])} /></FormField>
        <FormField label="Kolor obrysu"><Input type="color" value={clip.textStrokeColor || '#000000'} onChange={event => update({ textStrokeColor: event.target.value })} className="h-9 p-1" /></FormField>
      </div>
      <FormField label="Krycie tła">
        <Slider value={clip.textBackgroundOpacity ?? 70} min={0} max={100} step={1} unit="%" onChange={value => update({ textBackgroundOpacity: value }, ['textBackgroundOpacity'])} />
      </FormField>
      <div className="grid grid-cols-2 gap-2">
        <FormField label="Zaokrąglenie"><Input type="number" min={0} max={100} value={clip.textBorderRadius ?? 16} onChange={event => update({ textBorderRadius: Number(event.target.value) || 0 }, ['textBorderRadius'])} /></FormField>
        <FormField label="Margines wewn."><Input type="number" min={0} max={100} value={clip.textPadding ?? 16} onChange={event => update({ textPadding: Number(event.target.value) || 0 }, ['textPadding'])} /></FormField>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <FormField label="Odstęp liter"><Input type="number" min={-5} max={30} step={0.5} value={clip.letterSpacing || 0} onChange={event => update({ letterSpacing: Number(event.target.value) || 0 }, ['letterSpacing'])} /></FormField>
        <FormField label="Interlinia"><Input type="number" min={0.7} max={3} step={0.1} value={clip.lineHeight || 1.15} onChange={event => update({ lineHeight: Number(event.target.value) || 1.15 }, ['lineHeight'])} /></FormField>
      </div>
      <Checkbox checked={Boolean(clip.textShadow)} onChange={event => update({ textShadow: event.target.checked })} label="Cień tekstu" />
    </div>
  );
}

function InspectorPanel({
  selectedClip,
  selectedClips,
  selectedTransition,
  onUpdateSelectedClips,
  onUpdateTransition,
  onDeleteTransition,
  onDetachAudio,
  onRemoveSilence,
  onUpdateTextClip,
}: {
  selectedClip: StudioTimelineClip | null;
  selectedClips: StudioTimelineClip[];
  selectedTransition: StudioTimelineTransition | null;
  onUpdateSelectedClips: (updater: (clip: StudioTimelineClip) => Partial<StudioTimelineClip>) => void;
  onUpdateTransition: (transitionId: string, patch: Partial<StudioTimelineTransition>) => void;
  onDeleteTransition: (transitionId: string) => void;
  onDetachAudio: (clipId: string) => void;
  onRemoveSilence: (clipIds: string[], sensitivity: SilenceSensitivity) => Promise<number>;
  onUpdateTextClip: (patch: Partial<StudioTimelineClip>, options?: { applyAll?: boolean; relativeFields?: StudioTextNumericField[] }) => void;
}) {
  const [removingSilence, setRemovingSilence] = useState(false);
  const [silenceResult, setSilenceResult] = useState<string | null>(null);
  const [silenceSensitivity, setSilenceSensitivity] = useState<SilenceSensitivity>('balanced');
  const commonValue = (getter: (clip: StudioTimelineClip) => number) => {
    if (selectedClips.length === 0) return null;
    const first = getter(selectedClips[0]);
    return selectedClips.every(clip => Math.abs(getter(clip) - first) < 0.0001) ? first : null;
  };
  const speedValue = commonValue(clip => normalizePlaybackSpeed(clip.speed));
  const positionX = commonValue(clip => clip.position.x);
  const positionY = commonValue(clip => clip.position.y);
  const rotation = commonValue(clip => clip.rotation);
  const audioClips = selectedClips.filter(clip => (
    clipHasAudioControls(clip) && !(clip.kind === 'VIDEO' && clip.audioDetached)
  ));
  const commonAudioValue = (getter: (clip: StudioTimelineClip) => number) => {
    if (audioClips.length === 0) return null;
    const first = getter(audioClips[0]);
    return audioClips.every(clip => Math.abs(getter(clip) - first) < 0.0001) ? first : null;
  };
  return (
    <div className="h-full rounded-card border border-border bg-surface-raised p-3 min-h-0 overflow-y-auto">
      <div className="flex items-center gap-2 mb-3">
        <SlidersHorizontal className="w-4 h-4 text-accent-primary" />
        <h2 className="text-sm font-semibold text-text-primary">Opcje zasobu</h2>
      </div>
      {selectedTransition ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-violet-400/30 bg-violet-500/10 p-3">
            <div className="flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-violet-300" />
              <p className="truncate text-sm font-semibold text-text-primary">{selectedTransition.name}</p>
            </div>
            <p className="mt-1 text-xs text-text-muted">Przejście pomiędzy dwoma stykającymi się klipami.</p>
          </div>
          <FormField label="Czas trwania przejścia">
            <Slider
              value={Number((selectedTransition.durationMs / 1000).toFixed(1))}
              min={0.1}
              max={2}
              step={0.1}
              unit="s"
              onChange={seconds => onUpdateTransition(selectedTransition.id, { durationMs: Math.round(seconds * 1000) })}
            />
          </FormField>
          <Button
            size="sm"
            variant="danger"
            fullWidth
            leftIcon={<Trash2 />}
            onClick={() => onDeleteTransition(selectedTransition.id)}
          >
            Usuń przejście
          </Button>
        </div>
      ) : !selectedClip ? (
        <EmptyState
          icon={Layers}
          title="Nic nie zaznaczono"
          description="Zaznacz klip na osi czasu, żeby edytować jego parametry."
          size="sm"
        />
      ) : selectedClip.kind === 'TEXT' || selectedClip.kind === 'SCRIPT' ? (
        <TextAssetOptions clip={selectedClip} onUpdate={onUpdateTextClip} />
      ) : (
        <Tabs defaultValue="film">
          <TabsList fullWidth className="mb-2 gap-0.5 p-0.5 rounded-lg">
            <TabsTrigger value="film" size="sm" className="flex-1">Film</TabsTrigger>
            <TabsTrigger value="audio" size="sm" className="flex-1">Dźwięk</TabsTrigger>
          </TabsList>
          <TabsContent value="film" className="mt-0 space-y-3">
            <div>
              <p className="text-sm font-semibold text-text-primary truncate">{selectedClips.length > 1 ? `${selectedClips.length} zaznaczone klipy` : selectedClip.name}</p>
              <p className="text-xs text-text-muted mt-1">{selectedClips.length > 1 ? 'Zmiana wartości obejmie całe zaznaczenie' : `${kindLabel(selectedClip.kind)} - warstwa ${selectedClip.layer}`}</p>
            </div>
            <FormField label="Prędkość odtwarzania">
              <Input
                type="number"
                value={speedValue ?? ''}
                placeholder="-"
                min={MIN_PLAYBACK_SPEED}
                max={MAX_PLAYBACK_SPEED}
                step={0.25}
                className="h-8 px-2 py-1 text-xs"
                onChange={(event) => {
                  if (event.target.value === '') return;
                  const requestedSpeed = Number(event.target.value);
                  onUpdateSelectedClips(clip => {
                    const currentSpeed = normalizePlaybackSpeed(clip.speed);
                    const nextSpeed = normalizePlaybackSpeed(requestedSpeed, currentSpeed);
                    const sourceWindowMs = clip.durationMs * Math.abs(currentSpeed);
                    return {
                    speed: nextSpeed,
                    durationMs: Math.max(500, Math.round(sourceWindowMs / Math.abs(nextSpeed))),
                    };
                  });
                }}
              />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Pozycja X">
                <Input
                  type="number"
                  value={positionX ?? ''}
                  placeholder="-"
                  className="h-8 px-2 py-1 text-xs"
                  onChange={(event) => event.target.value !== '' && onUpdateSelectedClips(clip => ({
                    position: { ...clip.position, x: Number(event.target.value) },
                  }))}
                />
              </FormField>
              <FormField label="Pozycja Y">
                <Input
                  type="number"
                  value={positionY ?? ''}
                  placeholder="-"
                  className="h-8 px-2 py-1 text-xs"
                  onChange={(event) => event.target.value !== '' && onUpdateSelectedClips(clip => ({
                    position: { ...clip.position, y: Number(event.target.value) },
                  }))}
                />
              </FormField>
            </div>
            <FormField label="Obracanie (stopnie)">
              <Input
                type="number"
                value={rotation ?? ''}
                placeholder="-"
                className="h-8 px-2 py-1 text-xs"
                onChange={(event) => event.target.value !== '' && onUpdateSelectedClips(() => ({ rotation: Number(event.target.value) }))}
              />
            </FormField>
          </TabsContent>
          <TabsContent value="audio" className="mt-0 space-y-3">
            {audioClips.length > 0 ? (
              <>
                <FormField label="Głośność">
                  <Input
                    type="number"
                    value={commonAudioValue(clip => clipAudioSettings(clip).volumeDb) ?? ''}
                    placeholder="-"
                    min={-60}
                    max={12}
                    step={0.5}
                    className="h-8 px-2 py-1 text-xs"
                    onChange={(event) => event.target.value !== '' && onUpdateSelectedClips(clip => clipHasAudioControls(clip) ? { volumeDb: Number(event.target.value) } : {})}
                  />
                </FormField>
                <FormField label="Stopniowe pojawianie się">
                  <Input
                    type="number"
                    value={commonAudioValue(clip => Number((clipAudioSettings(clip).fadeInMs / 1000).toFixed(1))) ?? ''}
                    placeholder="-"
                    min={0}
                    max={Math.max(1, Math.ceil(selectedClip.durationMs / 1000))}
                    step={0.1}
                    className="h-8 px-2 py-1 text-xs"
                    onChange={(event) => event.target.value !== '' && onUpdateSelectedClips(clip => clipHasAudioControls(clip) ? { fadeInMs: Math.round(Number(event.target.value) * 1000) } : {})}
                  />
                </FormField>
                <FormField label="Stopniowe zanikanie">
                  <Input
                    type="number"
                    value={commonAudioValue(clip => Number((clipAudioSettings(clip).fadeOutMs / 1000).toFixed(1))) ?? ''}
                    placeholder="-"
                    min={0}
                    max={Math.max(1, Math.ceil(selectedClip.durationMs / 1000))}
                    step={0.1}
                    className="h-8 px-2 py-1 text-xs"
                    onChange={(event) => event.target.value !== '' && onUpdateSelectedClips(clip => clipHasAudioControls(clip) ? { fadeOutMs: Math.round(Number(event.target.value) * 1000) } : {})}
                  />
                </FormField>
                {selectedClips.length === 1 && selectedClip.kind === 'VIDEO' && !selectedClip.audioDetached && (
                  <Button
                    size="sm"
                    variant="secondary"
                    fullWidth
                    leftIcon={<AudioLines />}
                    onClick={() => onDetachAudio(selectedClip.id)}
                  >
                    Wydziel audio z wideo
                  </Button>
                )}
                <div className="space-y-2 rounded-lg border border-border-subtle bg-surface-base/50 p-2">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-text-primary">
                    <Scissors className="h-3.5 w-3.5 text-accent-primary" />
                    Wykryj i wytnij pauzy
                  </div>
                  <FormField label="Czułość">
                    <Select
                      value={silenceSensitivity}
                      className="h-8 text-xs"
                      disabled={removingSilence}
                      onChange={event => setSilenceSensitivity(event.target.value as SilenceSensitivity)}
                    >
                      <option value="safe">Bezpieczna</option>
                      <option value="balanced">Zbalansowana</option>
                      <option value="strong">Mocna</option>
                    </Select>
                  </FormField>
                  <Button
                    size="sm"
                    variant="secondary"
                    fullWidth
                    loading={removingSilence}
                    onClick={async () => {
                      setRemovingSilence(true);
                      setSilenceResult(null);
                      try {
                        const changedClips = await onRemoveSilence(audioClips.map(clip => clip.id), silenceSensitivity);
                        setSilenceResult(changedClips > 0
                          ? `Wycięto pauzy w ${changedClips} ${changedClips === 1 ? 'klipie' : 'klipach'}.`
                          : 'Nie wykryto pauz odpowiednich dla wybranej czułości.');
                      } catch (error) {
                        setSilenceResult(errorMessage(error));
                      } finally {
                        setRemovingSilence(false);
                      }
                    }}
                  >
                    Wytnij pauzy
                  </Button>
                  {silenceResult && <p className="text-[10px] leading-relaxed text-text-muted">{silenceResult}</p>}
                </div>
              </>
            ) : (
              <EmptyState
                icon={AudioLines}
                title="Brak dźwięku"
                description="Ten typ klipu nie ma wbudowanego dźwięku do regulacji."
                size="sm"
              />
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

const studioWaveformSourceCache = new Map<string, Promise<Float32Array>>();
const studioWaveformCache = new Map<string, Promise<number[]>>();

function loadWaveformSource(url: string) {
  const cached = studioWaveformSourceCache.get(url);
  if (cached) return cached;
  const pending = fetch(url, { credentials: 'include' })
    .then(response => {
      if (!response.ok) throw new Error('Nie udało się pobrać dźwięku.');
      return response.arrayBuffer();
    })
    .then(async buffer => {
      const AudioContextClass = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextClass) throw new Error('Przeglądarka nie obsługuje analizy dźwięku.');
      const context = new AudioContextClass();
      try {
        const decoded = await context.decodeAudioData(buffer.slice(0));
        const sourceBucketCount = Math.min(24000, decoded.length);
        const sourceBucketSize = Math.max(1, Math.floor(decoded.length / sourceBucketCount));
        const channelData = decoded.getChannelData(0);
        const samples = new Float32Array(sourceBucketCount);
        for (let bucket = 0; bucket < sourceBucketCount; bucket += 1) {
          const start = bucket * sourceBucketSize;
          const end = Math.min(channelData.length, start + sourceBucketSize);
          const stride = Math.max(1, Math.floor(sourceBucketSize / 256));
          let sumSquares = 0;
          let sampleCount = 0;
          for (let index = start; index < end; index += stride) {
            const value = channelData[index] || 0;
            sumSquares += value * value;
            sampleCount += 1;
          }
          samples[bucket] = sampleCount > 0 ? Math.sqrt(sumSquares / sampleCount) : 0;
        }
        return samples;
      } finally {
        void context.close();
      }
    });
  studioWaveformSourceCache.set(url, pending);
  pending.catch(() => studioWaveformSourceCache.delete(url));
  return pending;
}

function loadWaveform(url: string, requestedBucketCount = 640) {
  const bucketCount = clamp(Math.round(requestedBucketCount), 64, 6000);
  const cacheKey = `${url}|${bucketCount}`;
  const cached = studioWaveformCache.get(cacheKey);
  if (cached) return cached;
  const pending = loadWaveformSource(url)
    .then(samples => {
        const effectiveBucketCount = Math.min(bucketCount, samples.length);
        const bucketSize = Math.max(1, Math.floor(samples.length / effectiveBucketCount));
        const rmsValues = Array.from({ length: effectiveBucketCount }, (_, bucket) => {
          const start = bucket * bucketSize;
          const end = Math.min(samples.length, start + bucketSize);
          let sumSquares = 0;
          let sampleCount = 0;
          for (let index = start; index < end; index += Math.max(1, Math.floor(bucketSize / 256))) {
            const value = samples[index] || 0;
            sumSquares += value * value;
            sampleCount += 1;
          }
          return sampleCount > 0 ? Math.sqrt(sumSquares / sampleCount) : 0;
        });
        const sorted = [...rmsValues].sort((a, b) => a - b);
        const ceiling = sorted[Math.floor(sorted.length * 0.95)] || Math.max(...rmsValues) || 1;
        return rmsValues.map(rms => {
          if (rms <= 0.00005) return 0;
          const db = 20 * Math.log10(Math.max(rms, 0.000001) / ceiling);
          return Math.pow(clamp((db + 48) / 48, 0, 1), 1.7);
        });
    });
  studioWaveformCache.set(cacheKey, pending);
  pending.catch(() => studioWaveformCache.delete(cacheKey));
  return pending;
}

function ClipWaveform({
  clip,
  asset,
  selected,
  pxPerSecond,
}: {
  clip: StudioTimelineClip;
  asset?: StudioAsset;
  selected: boolean;
  pxPerSecond: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [peaks, setPeaks] = useState<number[]>([]);

  useEffect(() => {
    if (!asset?.previewUrl || !clipHasAudioControls(clip)) return;
    let active = true;
    const requestedBucketCount = Math.ceil((clip.durationMs / 1000) * pxPerSecond / 3);
    loadWaveform(asset.previewUrl, requestedBucketCount).then(value => {
      if (active) setPeaks(value);
    }).catch(() => {});
    return () => { active = false; };
  }, [asset?.previewUrl, clip.durationMs, clip.kind, pxPerSecond]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || peaks.length === 0) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const width = Math.max(1, Math.round(rect.width * dpr));
    const height = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);
    const barWidth = Math.max(1, Math.round(2 * dpr));
    const gap = Math.max(1, Math.round(1 * dpr));
    const count = Math.max(1, Math.floor(width / (barWidth + gap)));
    const sourceDuration = Math.max(1, clipSourceDurationMs(clip));
    const sourceStartRatio = clamp((clip.sourceStartMs || 0) / sourceDuration, 0, 1);
    const sourceEndRatio = clamp(sourceStartRatio + (clip.durationMs * playbackSpeedMagnitude(clip.speed)) / sourceDuration, 0, 1);
    ctx.fillStyle = selected ? 'rgba(96,165,250,0.95)' : 'rgba(56,189,248,0.72)';
    for (let index = 0; index < count; index += 1) {
      const ratio = count <= 1 ? 0 : index / (count - 1);
      const peakIndex = Math.min(peaks.length - 1, Math.floor((sourceStartRatio + ratio * (sourceEndRatio - sourceStartRatio)) * peaks.length));
      const fadeIn = clip.fadeInMs ? clamp((ratio * clip.durationMs) / clip.fadeInMs, 0, 1) : 1;
      const fadeOut = clip.fadeOutMs ? clamp(((1 - ratio) * clip.durationMs) / clip.fadeOutMs, 0, 1) : 1;
      const amplitude = peaks[peakIndex] * Math.min(fadeIn, fadeOut);
      const barHeight = Math.max(1 * dpr, amplitude * height * 0.96);
      ctx.fillRect(index * (barWidth + gap), (height - barHeight) / 2, barWidth, barHeight);
    }
  }, [clip.durationMs, clip.fadeInMs, clip.fadeOutMs, clip.sourceStartMs, clip.sourceDurationMs, clip.speed, peaks, selected]);

  return (
    <canvas
      ref={canvasRef}
      className={clip.kind === 'VIDEO'
        ? 'absolute inset-x-0 bottom-0 h-4 w-full border-t border-sky-400/30 bg-sky-950/90 pointer-events-none opacity-100 z-[2]'
        : 'absolute left-2 right-2 bottom-1 h-[78%] w-[calc(100%_-_1rem)] pointer-events-none opacity-100 z-[2]'}
      aria-hidden="true"
    />
  );
}

const studioFilmstripCache = new Map<string, Promise<string[]>>();

function loadFilmstrip(url: string, sourceStartMs: number, sourceDurationMs: number, frameCount: number) {
  const key = `${url}|${Math.round(sourceStartMs / 100)}|${Math.round(sourceDurationMs / 100)}|${frameCount}`;
  const cached = studioFilmstripCache.get(key);
  if (cached) return cached;
  const pending = new Promise<string[]>((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    if (url.startsWith('http') && new URL(url, window.location.href).origin !== window.location.origin) {
      video.crossOrigin = 'anonymous';
    }
    video.src = url;
    const cleanup = () => {
      video.removeAttribute('src');
      video.load();
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('Nie udało się przygotować podglądu klipu.'));
    };
    video.onloadedmetadata = async () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 72;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Brak obsługi miniatur.');
        const durationSeconds = Number.isFinite(video.duration) ? video.duration : 0;
        const startSeconds = clamp(sourceStartMs / 1000, 0, Math.max(0, durationSeconds - 0.02));
        const availableSeconds = Math.max(0.02, Math.min(sourceDurationMs / 1000, durationSeconds - startSeconds));
        const frames: string[] = [];
        for (let index = 0; index < frameCount; index += 1) {
          const ratio = (index + 0.5) / frameCount;
          const seekTo = clamp(startSeconds + availableSeconds * ratio, 0, Math.max(0, durationSeconds - 0.02));
          await new Promise<void>((seekResolve) => {
            const onSeeked = () => seekResolve();
            video.addEventListener('seeked', onSeeked, { once: true });
            video.currentTime = seekTo;
          });
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          frames.push(canvas.toDataURL('image/jpeg', 0.68));
        }
        cleanup();
        resolve(frames);
      } catch (error) {
        cleanup();
        reject(error);
      }
    };
  });
  studioFilmstripCache.set(key, pending);
  pending.catch(() => studioFilmstripCache.delete(key));
  return pending;
}

function ClipThumbnail({ clip, asset }: { clip: StudioTimelineClip; asset?: StudioAsset }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [frameCount, setFrameCount] = useState(2);
  const [frames, setFrames] = useState<string[]>([]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || clip.kind !== 'VIDEO') return;
    const update = () => setFrameCount(clamp(Math.ceil(node.clientWidth / 72), 2, 18));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [clip.kind]);

  useEffect(() => {
    if (!asset?.previewUrl || clip.kind !== 'VIDEO') return;
    let active = true;
    setFrames([]);
    loadFilmstrip(
      asset.previewUrl,
      clip.sourceStartMs || 0,
      clip.durationMs * playbackSpeedMagnitude(clip.speed),
      frameCount,
    ).then(result => {
      if (active) setFrames(result);
    }).catch(() => {});
    return () => { active = false; };
  }, [asset?.previewUrl, clip.durationMs, clip.kind, clip.sourceStartMs, clip.speed, frameCount]);

  if (!asset?.previewUrl || (clip.kind !== 'VIDEO' && clip.kind !== 'IMAGE')) return null;
  return clip.kind === 'VIDEO' ? (
    <div ref={containerRef} className="absolute inset-x-0 top-0 flex h-[calc(100%_-_16px)] overflow-hidden pointer-events-none opacity-90" aria-hidden="true">
      {frames.length > 0 ? frames.map((frame, index) => (
        <img key={`${frame.slice(-16)}-${index}`} src={frame} alt="" className="h-full min-w-0 flex-1 border-r border-black/25 object-cover last:border-r-0" />
      )) : (
        <video src={`${asset.previewUrl}#t=0.05`} muted preload="metadata" playsInline className="h-full w-full object-cover" />
      )}
    </div>
  ) : (
    <img
      src={asset.previewUrl}
      alt=""
      draggable={false}
      className="absolute inset-0 h-full w-full object-cover pointer-events-none opacity-85"
      aria-hidden="true"
    />
  );
}

function TimelinePanel({
  clips,
  assets,
  layers,
  transitions,
  selectedTransitionId,
  selectedClipId,
  selectedClipIds,
  copiedClip,
  playheadMs,
  durationMs,
  pxPerSecond,
  snapEnabled,
  mainTrackMagnetEnabled,
  timelineSaveError,
  isPlaying,
  onSelectClip,
  onSelectClips,
  onUpdateClip,
  onUpdateClips,
  onSetPlayhead,
  onTogglePlay,
  onUndo,
  onRedo,
  onSplit,
  onZoomIn,
  onZoomOut,
  onCopyClip,
  onDuplicateClip,
  onDetachAudio,
  onPasteClip,
  onDeleteClip,
  onDeleteSelectedClips,
  onUpdateLayer,
  onInsertLayer,
  onSelectTransition,
  onAddTransition,
  onDragStateChange,
  onToggleSnap,
  onToggleMainTrackMagnet,
  onBeginEdit,
  canUndo,
  canRedo,
}: {
  clips: StudioTimelineClip[];
  assets: StudioAsset[];
  layers: StudioTimelineLayer[];
  transitions: StudioTimelineTransition[];
  selectedTransitionId: string | null;
  selectedClipId: string | null;
  selectedClipIds: string[];
  copiedClip: StudioTimelineClip | null;
  playheadMs: number;
  durationMs: number;
  pxPerSecond: number;
  snapEnabled: boolean;
  mainTrackMagnetEnabled: boolean;
  timelineSaveError: string | null;
  isPlaying: boolean;
  onSelectClip: (clipId: string | null) => void;
  onSelectClips: (clipIds: string[]) => void;
  onUpdateClip: (clipId: string, patch: Partial<StudioTimelineClip>, options?: ClipUpdateOptions) => void;
  onUpdateClips: (updates: ClipBatchUpdate[], options?: ClipUpdateOptions) => void;
  onSetPlayhead: (value: number) => void;
  onTogglePlay: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onSplit: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onCopyClip: (clipId: string) => void;
  onDuplicateClip: (clipId: string) => void;
  onDetachAudio: (clipId: string) => void;
  onPasteClip: (startMs: number, layer: number) => void;
  onDeleteClip: (clipId: string) => void;
  onDeleteSelectedClips: (clipIds: string[]) => void;
  onUpdateLayer: (layer: number, patch: Partial<StudioTimelineLayer>) => void;
  onInsertLayer: (layer: number) => void;
  onSelectTransition: (transitionId: string | null) => void;
  onAddTransition: (type: string, fromClipId: string, toClipId: string, boundaryMs: number) => void;
  onDragStateChange: (dragging: boolean) => void;
  onToggleSnap: () => void;
  onToggleMainTrackMagnet: () => void;
  onBeginEdit: () => void;
  canUndo: boolean;
  canRedo: boolean;
}) {
  const assetMap = useMemo(() => new Map(assets.map(asset => [asset.id, asset])), [assets]);
  const [layerInsertionPreview, setLayerInsertionPreview] = useState<{ targetLayer: number; movingIds: string[] } | null>(null);
  const layerNumbers = useMemo(() => {
    const previewTopLayer = layerInsertionPreview
      ? Math.max(0, ...clips.filter(clip => clip.kind !== 'AUDIO').map(clip => (
        clip.layer >= layerInsertionPreview.targetLayer ? clip.layer + 1 : clip.layer
      )))
      : null;
    return Array.from(new Set([
      ...layers.map(item => item.layer),
      ...(previewTopLayer !== null ? [previewTopLayer] : []),
    ])).sort((a, b) => b - a);
  }, [clips, layerInsertionPreview, layers]);
  const displayClips = useMemo(() => {
    if (!layerInsertionPreview) return clips;
    const movingIds = new Set(layerInsertionPreview.movingIds);
    return clips.map(clip => (
      !movingIds.has(clip.id) && clip.kind !== 'AUDIO' && clip.layer >= layerInsertionPreview.targetLayer
        ? { ...clip, layer: Math.min(MAX_LAYER, clip.layer + 1) }
        : clip
    ));
  }, [clips, layerInsertionPreview]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastWheelZoomAt = useRef(0);
  const zoomInRef = useRef(onZoomIn);
  const zoomOutRef = useRef(onZoomOut);
  const previousZoomRef = useRef(pxPerSecond);
  const lanesRef = useRef<HTMLDivElement>(null);
  const baseCenteredRef = useRef(false);
  const displayDurationMs = Math.max(durationMs, BASE_TIMELINE_DURATION_MS);
  const width = Math.max(960, Math.ceil(displayDurationMs / 1000) * pxPerSecond);
  const playheadLeft = (playheadMs / 1000) * pxPerSecond;
  const snapThresholdMs = Math.min(SNAP_THRESHOLD_MS, Math.max(20, (5 / pxPerSecond) * 1000));
  const { ticks } = useMemo(() => buildTimelineTicks(displayDurationMs, pxPerSecond), [displayDurationMs, pxPerSecond]);
  const laneMetrics = useMemo(() => {
    let top = 0;
    return layerNumbers.map(layer => {
      const height = layer === 0 ? BASE_TIMELINE_LANE_HEIGHT : TIMELINE_LANE_HEIGHT;
      const metric = { layer, top, height };
      top += height;
      return metric;
    });
  }, [layerNumbers]);
  const lanesHeight = laneMetrics.reduce((total, metric) => total + metric.height, 0);
  const touchingPairs = useMemo(() => layerNumbers.flatMap(layer => {
    const visualClips = clips
      .filter(clip => clip.layer === layer && (clip.kind === 'VIDEO' || clip.kind === 'IMAGE'))
      .sort((a, b) => a.startMs - b.startMs);
    return visualClips.slice(0, -1).flatMap((clip, index) => {
      const nextClip = visualClips[index + 1];
      const boundaryMs = clipEnd(clip);
      return Math.abs(boundaryMs - nextClip.startMs) <= 80
        ? [{ layer, fromClip: clip, toClip: nextClip, boundaryMs }]
        : [];
    });
  }), [clips, layerNumbers]);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    type: 'clip' | 'empty';
    clipId?: string;
    startMs: number;
    layer: number;
  } | null>(null);
  const [selectionRect, setSelectionRect] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const [snapGuideMs, setSnapGuideMs] = useState<number | null>(null);

  useEffect(() => {
    zoomInRef.current = onZoomIn;
    zoomOutRef.current = onZoomOut;
  }, [onZoomIn, onZoomOut]);

  useLayoutEffect(() => {
    const node = scrollRef.current;
    const previousZoom = previousZoomRef.current;
    if (!node || previousZoom === pxPerSecond) return;
    const previousPlayheadX = TIMELINE_LABEL_WIDTH + (playheadMs / 1000) * previousZoom;
    const playheadViewportX = previousPlayheadX - node.scrollLeft;
    const nextPlayheadX = TIMELINE_LABEL_WIDTH + (playheadMs / 1000) * pxPerSecond;
    node.scrollLeft = Math.max(0, nextPlayheadX - playheadViewportX);
    previousZoomRef.current = pxPerSecond;
  }, [playheadMs, pxPerSecond]);

  const snapTimelinePoint = (value: number, movingClip: StudioTimelineClip): { value: number; guide: number | null } => {
    if (!snapEnabled) return { value, guide: null as number | null };
    const points = [
      { value: playheadMs, priority: 1 },
      ...clips.filter(clip => clip.id !== movingClip.id).flatMap(clip => {
        const priority = clipLayerCategory(clip.kind) === clipLayerCategory(movingClip.kind) ? 0 : 1;
        return [{ value: clip.startMs, priority }, { value: clipEnd(clip), priority }];
      }),
    ];
    const best = points.reduce<{ value: number; guide: number | null; distance: number; priority: number }>((currentBest, point) => {
      const distance = Math.abs(point.value - value);
      return distance <= snapThresholdMs
        && (point.priority < currentBest.priority || (point.priority === currentBest.priority && distance < currentBest.distance))
        ? { value: point.value, guide: point.value, distance, priority: point.priority }
        : currentBest;
    }, { value, guide: null as number | null, distance: snapThresholdMs + 1, priority: 2 });
    return { value: best.value, guide: best.guide };
  };

  const transitionTypeFromTransfer = (dataTransfer: DataTransfer) => {
    const customType = dataTransfer.getData('application/x-studio-transition');
    const plainType = dataTransfer.getData('text/plain');
    return STUDIO_TRANSITIONS.some(item => item.type === customType)
      ? customType
      : STUDIO_TRANSITIONS.some(item => item.type === plainType)
        ? plainType
        : '';
  };

  useEffect(() => {
    if (baseCenteredRef.current) return;
    const node = scrollRef.current;
    const baseMetric = laneMetrics.find(metric => metric.layer === 0);
    if (!node || !baseMetric) return;
    baseCenteredRef.current = true;
    requestAnimationFrame(() => {
      const rulerHeight = 28;
      node.scrollTop = Math.max(0, rulerHeight + baseMetric.top + baseMetric.height / 2 - node.clientHeight / 2);
    });
  }, [laneMetrics]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const handleWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const now = performance.now();
      if (now - lastWheelZoomAt.current < 45) return;
      lastWheelZoomAt.current = now;
      if (event.deltaY < 0) zoomInRef.current();
      else if (event.deltaY > 0) zoomOutRef.current();
    };
    node.addEventListener('wheel', handleWheel, { passive: false });
    return () => node.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', close);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [contextMenu]);

  const playheadFromClientX = (clientX: number) => {
    if (!lanesRef.current) return;
    const rect = lanesRef.current.getBoundingClientRect();
    const x = clamp(clientX - rect.left, 0, width);
    onSetPlayhead((x / pxPerSecond) * 1000);
  };

  const laneFromClientY = (clientY: number) => {
    if (!lanesRef.current) return 0;
    const rect = lanesRef.current.getBoundingClientRect();
    const y = clamp(clientY - rect.top, 0, lanesHeight - 1);
    return laneMetrics.find(metric => y >= metric.top && y < metric.top + metric.height)?.layer ?? 0;
  };

  const clipTimelineRect = (clip: StudioTimelineClip) => {
    const laneMetric = laneMetrics.find(metric => metric.layer === clip.layer) || laneMetrics.find(metric => metric.layer === 0)!;
    return {
      left: (clip.startMs / 1000) * pxPerSecond,
      top: laneMetric.top + 5,
      width: Math.max(clip.kind === 'TEXT' || clip.kind === 'SCRIPT' ? 8 : 32, (clip.durationMs / 1000) * pxPerSecond),
      height: laneMetric.height - 10,
    };
  };

  const rectsOverlap = (
    a: { left: number; top: number; width: number; height: number },
    b: { left: number; top: number; width: number; height: number },
  ) => (
    a.left < b.left + b.width
    && b.left < a.left + a.width
    && a.top < b.top + b.height
    && b.top < a.top + a.height
  );

  const startScrub = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    playheadFromClientX(event.clientX);
    const move = (moveEvent: PointerEvent) => {
      playheadFromClientX(moveEvent.clientX);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const startEmptySelection = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !lanesRef.current) return;
    event.preventDefault();
    onSelectTransition(null);
    const lanesRect = lanesRef.current.getBoundingClientRect();
    const startX = clamp(event.clientX - lanesRect.left, 0, width);
    const startY = clamp(event.clientY - lanesRect.top, 0, lanesHeight);
    let moved = false;
    playheadFromClientX(event.clientX);

    const move = (moveEvent: PointerEvent) => {
      const currentX = clamp(moveEvent.clientX - lanesRect.left, 0, width);
      const currentY = clamp(moveEvent.clientY - lanesRect.top, 0, lanesHeight);
      if (!moved && Math.hypot(currentX - startX, currentY - startY) < 4) return;
      moved = true;
      const nextRect = {
        left: Math.min(startX, currentX),
        top: Math.min(startY, currentY),
        width: Math.abs(currentX - startX),
        height: Math.abs(currentY - startY),
      };
      setSelectionRect(nextRect);
      onSelectClips(clips.filter(clip => rectsOverlap(nextRect, clipTimelineRect(clip))).map(clip => clip.id));
    };

    const up = () => {
      if (!moved) onSelectClip(null);
      setSelectionRect(null);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const startDrag = (event: React.PointerEvent<HTMLDivElement>, clip: StudioTimelineClip) => {
    if (layerSetting(layers, clip.layer).locked) {
      event.preventDefault();
      event.stopPropagation();
      onSelectClip(clip.id);
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const movingClips = (selectedClipIds.includes(clip.id)
      ? clips.filter(item => selectedClipIds.includes(item.id))
      : [clip]
    ).filter(item => !layerSetting(layers, item.layer).locked);
    if (!selectedClipIds.includes(clip.id)) onSelectClip(clip.id);
    if (movingClips.length === 0) return;
    onSelectTransition(null);
    onBeginEdit();
    onDragStateChange(true);
    const startX = event.clientX;
    const movingIds = new Set(movingClips.map(item => item.id));
    const initialById = new Map(movingClips.map(item => [item.id, item]));
    const minimumStart = Math.min(...movingClips.map(item => item.startMs));
    const maximumEnd = Math.max(...movingClips.map(clipEnd));
    let droppedUpdates: ClipBatchUpdate[] = movingClips.map(item => ({
      clipId: item.id,
      patch: { startMs: item.startMs, layer: item.layer },
    }));

    const move = (moveEvent: PointerEvent) => {
      const rawDeltaMs = ((moveEvent.clientX - startX) / pxPerSecond) * 1000;
      const rawLayer = laneFromClientY(moveEvent.clientY);
      const anchorLayer = clip.kind === 'AUDIO'
        ? clamp(rawLayer, MIN_LAYER, -1)
        : clamp(rawLayer, 0, MAX_LAYER);
      const boundedDeltaMs = clamp(rawDeltaMs, -minimumStart, Math.max(0, displayDurationMs - maximumEnd));
      const rawAnchorStart = clip.startMs + boundedDeltaMs;
      const otherClips = clips.filter(item => !movingIds.has(item.id));
      const snappedAnchorStart = snapEnabled
        ? snapClipStart(otherClips, clip.id, clip.kind, rawAnchorStart, clip.durationMs, playheadMs, snapThresholdMs)
        : rawAnchorStart;
      const layerDelta = anchorLayer - clip.layer;
      let deltaMs = clamp(snappedAnchorStart - clip.startMs, -minimumStart, Math.max(0, displayDurationMs - maximumEnd));
      if (mainTrackMagnetEnabled && anchorLayer === 0 && clipLayerCategory(clip.kind) === 'visual') {
        const movingMainClips = movingClips.filter(item => (
          item.kind !== 'AUDIO'
          && clamp(item.layer + layerDelta, 0, MAX_LAYER) === 0
          && clipLayerCategory(item.kind) === 'visual'
        ));
        const otherMainClips = otherClips.filter(item => item.layer === 0 && clipLayerCategory(item.kind) === 'visual');
        if (movingMainClips.length > 0 && otherMainClips.length > 0) {
          const movingStart = Math.min(...movingMainClips.map(item => item.startMs));
          const movingEnd = Math.max(...movingMainClips.map(clipEnd));
          const magneticDeltas = otherMainClips.flatMap(item => [
            clipEnd(item) - movingStart,
            item.startMs - movingEnd,
          ]).filter(candidateDelta => {
            const boundedCandidate = clamp(candidateDelta, -minimumStart, Math.max(0, displayDurationMs - maximumEnd));
            if (boundedCandidate !== candidateDelta) return false;
            return movingMainClips.every(movingClip => !otherMainClips.some(otherClip => rangesOverlap(
              movingClip.startMs + candidateDelta,
              movingClip.durationMs,
              otherClip.startMs,
              otherClip.durationMs,
            )));
          });
          if (magneticDeltas.length > 0) {
            deltaMs = magneticDeltas.reduce((best, candidate) => (
              Math.abs(candidate - deltaMs) < Math.abs(best - deltaMs) ? candidate : best
            ));
          }
        }
      }
      const rigidDeltaMs = Math.round(deltaMs);
      droppedUpdates = movingClips.map(item => ({
        clipId: item.id,
        patch: {
          startMs: item.startMs + rigidDeltaMs,
          layer: item.kind === 'AUDIO'
            ? clamp(item.layer + layerDelta, MIN_LAYER, -1)
            : clamp(item.layer + layerDelta, 0, MAX_LAYER),
        },
      }));
      const nextAnchorStart = clip.startMs + rigidDeltaMs;
      const guidePoints = [playheadMs, ...otherClips.flatMap(item => [item.startMs, clipEnd(item)])];
      setSnapGuideMs(guidePoints.find(point => (
        Math.abs(point - nextAnchorStart) < 1
        || Math.abs(point - (nextAnchorStart + clip.durationMs)) < 1
      )) ?? null);
      const insertionTarget = anchorLayer >= 0
        && clip.kind !== 'AUDIO'
        && otherClips.some(item => (
          item.layer === anchorLayer
          && clipLayerCategory(item.kind) !== clipLayerCategory(clip.kind)
        ))
        ? { targetLayer: anchorLayer, movingIds: Array.from(movingIds) }
        : null;
      setLayerInsertionPreview(previous => (
        previous?.targetLayer === insertionTarget?.targetLayer
          && previous?.movingIds.length === insertionTarget?.movingIds.length
          ? previous
          : insertionTarget
      ));
      onUpdateClips(droppedUpdates, { recordHistory: false, allowCollision: true });
    };

    const up = () => {
      const desiredById = new Map(droppedUpdates.map(update => [update.clipId, update.patch]));
      const anchorTargetLayer = desiredById.get(clip.id)?.layer ?? clip.layer;
      const anchorCategory = clipLayerCategory(clip.kind);
      const highestVisualLayer = Math.max(0, ...clips.filter(item => item.kind !== 'AUDIO').map(item => item.layer));
      const shouldInsertLayer = anchorTargetLayer >= 0
        && highestVisualLayer < MAX_LAYER
        && clips.some(item => (
          !movingIds.has(item.id)
          && item.layer === anchorTargetLayer
          && clipLayerCategory(item.kind) !== anchorCategory
        ));
      const shiftedUpdates: ClipBatchUpdate[] = shouldInsertLayer
        ? clips
          .filter(item => !movingIds.has(item.id) && item.kind !== 'AUDIO' && item.layer >= anchorTargetLayer)
          .map(item => ({ clipId: item.id, patch: { layer: item.layer + 1 } }))
        : [];
      const shiftedById = new Map(shiftedUpdates.map(update => [update.clipId, update.patch.layer as number]));
      const effectiveClips = clips
        .filter(item => !movingIds.has(item.id))
        .map(item => shiftedById.has(item.id) ? { ...item, layer: shiftedById.get(item.id)! } : item);
      const effectiveLayers = shouldInsertLayer
        ? [
          ...layers
            .filter(setting => setting.layer !== anchorTargetLayer)
            .map(setting => setting.layer >= anchorTargetLayer && setting.layer >= 0
              ? { ...setting, layer: setting.layer + 1 }
              : setting),
          { layer: anchorTargetLayer, locked: false, hidden: false, muted: false },
        ]
        : layers;
      const resolvedUpdates: ClipBatchUpdate[] = [];
      const dropGroups = new Map<string, Array<{ clip: StudioTimelineClip; startMs: number; requestedLayer: number }>>();
      movingClips.forEach(item => {
        const desired = desiredById.get(item.id) || {};
        const startMs = desired.startMs ?? item.startMs;
        const requestedLayer = desired.layer ?? item.layer;
        const key = `${item.layer}:${requestedLayer}:${clipLayerCategory(item.kind)}`;
        const group = dropGroups.get(key) || [];
        group.push({ clip: item, startMs, requestedLayer });
        dropGroups.set(key, group);
      });

      dropGroups.forEach(group => {
        const resolvedLayer = findGroupDropLayer(
          effectiveClips,
          effectiveLayers,
          group,
          group[0].requestedLayer,
        );
        group.forEach(item => {
          resolvedUpdates.push({ clipId: item.clip.id, patch: { startMs: item.startMs, layer: resolvedLayer } });
          effectiveClips.push({ ...initialById.get(item.clip.id)!, startMs: item.startMs, layer: resolvedLayer });
        });
      });

      if (shouldInsertLayer) onInsertLayer(anchorTargetLayer);
      onUpdateClips([...shiftedUpdates, ...resolvedUpdates], {
        recordHistory: false,
        allowCollision: true,
        ignoreLayerLock: shouldInsertLayer,
      });
      setSnapGuideMs(null);
      setLayerInsertionPreview(null);
      onDragStateChange(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const startResize = (event: React.PointerEvent<HTMLDivElement>, clip: StudioTimelineClip, edge: 'start' | 'end') => {
    if (layerSetting(layers, clip.layer).locked) return;
    event.preventDefault();
    event.stopPropagation();
    onSelectClip(clip.id);
    onBeginEdit();
    const startX = event.clientX;
    const initialStart = clip.startMs;
    const initialDuration = clip.durationMs;
    const initialSourceStart = clip.sourceStartMs || 0;
    const sourceDuration = clipSourceDurationMs(clip);
    const speedMagnitude = playbackSpeedMagnitude(clip.speed);
    const isTextClip = clip.kind === 'TEXT' || clip.kind === 'SCRIPT';

    const move = (moveEvent: PointerEvent) => {
      const deltaMs = ((moveEvent.clientX - startX) / pxPerSecond) * 1000;
      if (edge === 'end') {
        const maxDuration = isTextClip
          ? MAX_STUDIO_TEXT_DURATION_MS
          : Math.max(500, (sourceDuration - initialSourceStart) / speedMagnitude);
        const rawEnd = initialStart + clamp(initialDuration + deltaMs, 500, maxDuration);
        const snapped = snapTimelinePoint(rawEnd, clip);
        const snappedEnd = snapped.value;
        setSnapGuideMs(snapped.guide);
        const nextDuration = clamp(snappedEnd - initialStart, 500, maxDuration);
        if (isTextClip || !hasLayerCollision(clips, clip.id, clip.layer, clip.startMs, nextDuration)) {
          onUpdateClip(clip.id, { durationMs: Math.round(nextDuration) }, { recordHistory: false, allowCollision: isTextClip });
        }
        return;
      }

      const maxLeftExtension = isTextClip ? initialStart : initialSourceStart / speedMagnitude;
      const minStart = Math.max(0, initialStart - maxLeftExtension);
      const maxStart = initialStart + initialDuration - 500;
      const rawStart = clamp(initialStart + deltaMs, minStart, maxStart);
      const snapped = snapTimelinePoint(rawStart, clip);
      const nextStart = snapped.value;
      setSnapGuideMs(snapped.guide);
      const diff = nextStart - initialStart;
      const nextDuration = initialDuration - diff;
      const nextSourceStart = clamp(initialSourceStart + diff * speedMagnitude, 0, Math.max(0, sourceDuration - 500));
      if (isTextClip || !hasLayerCollision(clips, clip.id, clip.layer, nextStart, nextDuration)) {
        onUpdateClip(clip.id, isTextClip ? {
          startMs: Math.round(nextStart),
          durationMs: Math.round(nextDuration),
        } : {
          startMs: Math.round(nextStart),
          durationMs: Math.round(nextDuration),
          sourceStartMs: Math.round(nextSourceStart),
          sourceDurationMs: sourceDuration,
        }, { recordHistory: false, allowCollision: isTextClip });
      }
    };

    const up = () => {
      setSnapGuideMs(null);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const contextPosition = (event: React.MouseEvent<HTMLDivElement>) => {
    const x = Math.min(event.clientX, window.innerWidth - 180);
    const y = Math.min(event.clientY, window.innerHeight - 96);
    return { x, y };
  };

  const timelinePointFromEvent = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!lanesRef.current) return { startMs: playheadMs, layer: 0 };
    const rect = lanesRef.current.getBoundingClientRect();
    const x = clamp(event.clientX - rect.left, 0, width);
    return {
      startMs: Math.round((x / pxPerSecond) * 1000),
      layer: laneFromClientY(event.clientY),
    };
  };

  const menuItems: StudioContextMenuItem[] = contextMenu
    ? contextMenu.type === 'clip'
      ? (() => {
        const contextClip = clips.find(clip => clip.id === contextMenu.clipId);
        return [
        {
          id: 'copy',
          label: 'Kopiuj',
          icon: <ClipboardCopy />,
          onClick: () => {
            if (contextMenu.clipId) onCopyClip(contextMenu.clipId);
            setContextMenu(null);
          },
        },
        {
          id: 'duplicate',
          label: 'Duplikuj',
          icon: <CopyPlus />,
          onClick: () => {
            if (contextMenu.clipId) onDuplicateClip(contextMenu.clipId);
            setContextMenu(null);
          },
        },
        ...(contextClip?.kind === 'VIDEO' && !contextClip.audioDetached ? [{
          id: 'detach-audio',
          label: 'Wydziel audio z wideo',
          icon: <AudioLines />,
          separatorBefore: true,
          onClick: () => {
            if (contextMenu.clipId) onDetachAudio(contextMenu.clipId);
            setContextMenu(null);
          },
        }] : []),
        {
          id: 'delete',
          label: 'Usuń',
          icon: <Trash2 />,
          destructive: true,
          separatorBefore: Boolean(contextClip?.kind === 'VIDEO' && !contextClip.audioDetached),
          onClick: () => {
            if (contextMenu.clipId) onDeleteClip(contextMenu.clipId);
            setContextMenu(null);
          },
        },
        ];
      })()
      : [{
        id: 'paste',
        label: 'Wklej',
        icon: <ClipboardPaste />,
        disabled: !copiedClip,
        onClick: () => {
          if (copiedClip) onPasteClip(contextMenu.startMs, contextMenu.layer);
          setContextMenu(null);
        },
      }]
    : [];

  return (
    <div className="h-full border-t border-border bg-surface-raised flex flex-col min-h-0">
      <div className="h-8 px-1.5 border-b border-border-subtle flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <StudioIconButton
            label={isPlaying ? 'Pauza' : 'Play'}
            icon={isPlaying ? <Pause /> : <Play />}
            onClick={onTogglePlay}
          />
          <StudioIconButton
            label="Tnij"
            icon={<Scissors />}
            disabled={!selectedClipId}
            onClick={onSplit}
          />
          <StudioIconButton
            label="Usuń zaznaczony element"
            icon={<Trash2 />}
            disabled={!selectedClipId}
            onClick={() => onDeleteSelectedClips(selectedClipIds)}
          />
          <StudioIconButton
            label="Cofnij zmianę"
            icon={<Undo2 />}
            disabled={!canUndo}
            onClick={onUndo}
          />
          <StudioIconButton
            label="Ponów zmianę"
            icon={<Redo2 />}
            disabled={!canRedo}
            onClick={onRedo}
          />
        </div>
        <div className="flex items-center gap-1">
          {timelineSaveError && (
            <span className="max-w-40 truncate px-1 text-[10px] text-danger" title={timelineSaveError}>
              Nie zapisano zmian
            </span>
          )}
          <div className="text-[10px] font-mono text-text-muted min-w-[88px] text-right px-1">
            {formatTime(playheadMs)} / {formatTime(durationMs)}
          </div>
          <StudioIconButton
            label={snapEnabled ? 'Wyłącz automatyczne dopasowanie' : 'Włącz automatyczne dopasowanie'}
            icon={<Focus />}
            active={snapEnabled}
            onClick={onToggleSnap}
          />
          <StudioIconButton
            label={mainTrackMagnetEnabled ? 'Wyłącz automatyczny magnes głównej ścieżki' : 'Włącz automatyczny magnes głównej ścieżki'}
            icon={<Magnet />}
            active={mainTrackMagnetEnabled}
            onClick={onToggleMainTrackMagnet}
            side="left"
          />
          <StudioIconButton
            label="Oddal oś czasu"
            icon={<ZoomOut />}
            onClick={onZoomOut}
          />
          <StudioIconButton
            label="Przybliż oś czasu"
            icon={<ZoomIn />}
            onClick={onZoomIn}
          />
        </div>
      </div>

      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-auto"
      >
        <div className="relative" style={{ width: width + TIMELINE_LABEL_WIDTH }}>
          <div className="sticky top-0 z-20 h-7 bg-surface-raised border-b border-border-subtle" style={{ marginLeft: TIMELINE_LABEL_WIDTH }}>
            <div
              className="relative h-full cursor-pointer"
              style={{ width }}
              onPointerDown={startScrub}
            >
              {ticks.map(tick => (
                <div
                  key={tick.value}
                  className={`absolute bottom-0 border-l ${tick.major ? 'h-full border-border-strong' : 'h-2.5 border-border-subtle'}`}
                  style={{ left: (tick.value / 1000) * pxPerSecond }}
                >
                  {tick.major && (
                    <span className="absolute top-0.5 left-1 text-[9px] text-text-muted font-mono whitespace-nowrap">
                      {formatTime(tick.value)}
                    </span>
                  )}
                </div>
              ))}
              <div
                className="absolute top-0 bottom-0 z-30 w-px -translate-x-1/2 bg-danger pointer-events-none"
                style={{ left: playheadLeft }}
              >
                <div className="absolute top-0 left-1/2 h-3.5 w-2.5 -translate-x-1/2 rounded-b-sm bg-danger" />
              </div>
            </div>
          </div>

          <div className="flex">
            <div
              className="sticky left-0 z-30 shrink-0 bg-surface-raised border-r border-border-subtle"
              style={{ width: TIMELINE_LABEL_WIDTH }}
            >
              {laneMetrics.map(({ layer, height }) => (
                <div
                  key={layer}
                  className="border-b border-border-subtle flex items-center px-1 text-[10px] text-text-muted"
                  style={{ height }}
                >
                  <button
                    type="button"
                    className="flex h-full w-5 shrink-0 items-center justify-center rounded text-center hover:bg-accent-primary/15 hover:text-accent-primary"
                    aria-label={`Zaznacz wszystkie elementy warstwy ${layer}`}
                    title="Zaznacz wszystkie elementy warstwy"
                    onClick={() => onSelectClips(clips.filter(clip => clip.layer === layer).map(clip => clip.id))}
                  >
                    {layer === 0 ? <House className="w-3 h-3" aria-hidden="true" /> : layer > 0 ? `+${layer}` : layer}
                  </button>
                  <div className="ml-auto flex h-full items-center gap-0.5">
                    <StudioIconButton
                      label={layerSetting(layers, layer).locked ? 'Odblokuj warstwę' : 'Zablokuj warstwę'}
                      icon={layerSetting(layers, layer).locked ? <Lock /> : <Unlock />}
                      active={layerSetting(layers, layer).locked}
                      onClick={() => onUpdateLayer(layer, { locked: !layerSetting(layers, layer).locked })}
                      side="top"
                      compact
                    />
                    <StudioIconButton
                      label={layerSetting(layers, layer).hidden ? 'Pokaż warstwę' : 'Ukryj warstwę'}
                      icon={layerSetting(layers, layer).hidden ? <EyeOff /> : <Eye />}
                      active={layerSetting(layers, layer).hidden}
                      onClick={() => onUpdateLayer(layer, { hidden: !layerSetting(layers, layer).hidden })}
                      side="top"
                      compact
                    />
                    <StudioIconButton
                      label={layerSetting(layers, layer).muted ? 'Włącz dźwięk warstwy' : 'Wycisz warstwę'}
                      icon={layerSetting(layers, layer).muted ? <VolumeX /> : <Volume2 />}
                      active={layerSetting(layers, layer).muted}
                      onClick={() => onUpdateLayer(layer, { muted: !layerSetting(layers, layer).muted })}
                      side="top"
                      compact
                    />
                  </div>
                </div>
              ))}
            </div>

            <div
              ref={lanesRef}
              className="relative"
              style={{ width, height: lanesHeight }}
              onPointerDown={startEmptySelection}
              onDragOver={(event) => {
                const types = Array.from(event.dataTransfer.types);
                if (types.includes('application/x-studio-transition') || types.includes('text/plain')) {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'copy';
                }
              }}
              onDrop={(event) => {
                const type = transitionTypeFromTransfer(event.dataTransfer);
                if (!type || !lanesRef.current || touchingPairs.length === 0) return;
                event.preventDefault();
                event.stopPropagation();
                const rect = lanesRef.current.getBoundingClientRect();
                const dropMs = ((event.clientX - rect.left) / pxPerSecond) * 1000;
                const nearestPair = touchingPairs.reduce((best, pair) => (
                  Math.abs(pair.boundaryMs - dropMs) < Math.abs(best.boundaryMs - dropMs) ? pair : best
                ));
                const toleranceMs = Math.max(500, (36 / pxPerSecond) * 1000);
                if (Math.abs(nearestPair.boundaryMs - dropMs) <= toleranceMs) {
                  onAddTransition(type, nearestPair.fromClip.id, nearestPair.toClip.id, nearestPair.boundaryMs);
                }
              }}
              onContextMenu={(event) => {
                event.preventDefault();
                const point = timelinePointFromEvent(event);
                setContextMenu({ ...contextPosition(event), type: 'empty', ...point });
              }}
            >
              {layerNumbers.map((layer, laneIndex) => (
                <div
                  key={layer}
                  className={`absolute left-0 right-0 border-b border-border-subtle ${layer === 0 ? 'bg-accent-primary/5' : 'bg-surface-base/30'}`}
                  style={{ top: laneMetrics[laneIndex].top, height: laneMetrics[laneIndex].height }}
                />
              ))}

              {displayClips.map(clip => {
                const clipRect = clipTimelineRect(clip);
                const selected = selectedClipIds.includes(clip.id);
                const settings = layerSetting(layers, clip.layer);
                const asset = assetMap.get(clip.assetId);
                const colorClass = clip.kind === 'TEXT' || clip.kind === 'SCRIPT'
                  ? selected
                    ? 'bg-orange-500/35 border-orange-300 text-orange-50'
                    : 'bg-orange-500/20 border-orange-400/70 text-orange-100 hover:bg-orange-500/30'
                  : clip.kind === 'AUDIO'
                    ? selected
                      ? 'bg-sky-500/25 border-sky-300 text-text-primary'
                      : 'bg-sky-950/80 border-sky-500/60 text-sky-100 hover:border-sky-400'
                    : selected
                      ? 'bg-accent-primary/25 border-accent-primary text-text-primary'
                      : 'bg-surface-hover border-border-strong text-text-secondary hover:text-text-primary';
                return (
                  <div
                    key={clip.id}
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelectTransition(null);
                      if (!selectedClipIds.includes(clip.id)) onSelectClip(clip.id);
                    }}
                    onPointerDown={(event) => startDrag(event, clip)}
                    onContextMenu={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onSelectClip(clip.id);
                      setContextMenu({
                        ...contextPosition(event),
                        type: 'clip',
                        clipId: clip.id,
                        startMs: clip.startMs,
                        layer: clip.layer,
                      });
                    }}
                  className={`absolute rounded-md border px-2 py-1 overflow-hidden ${settings.locked ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'} ${settings.hidden ? 'opacity-35' : ''} ${colorClass}`}
                    style={{
                      left: clipRect.left,
                      top: clipRect.top,
                      width: clipRect.width,
                      height: clipRect.height,
                    }}
                  >
                    <ClipThumbnail clip={clip} asset={asset} />
                    {(clip.kind === 'VIDEO' || clip.kind === 'IMAGE') && (
                      <div className="absolute inset-x-0 top-0 h-5 bg-gradient-to-b from-black/80 to-transparent pointer-events-none" />
                    )}
                    <div
                      className="absolute left-0 top-0 h-full w-2 cursor-ew-resize hover:bg-accent-primary/40"
                      onPointerDown={(event) => startResize(event, clip, 'start')}
                      aria-hidden="true"
                    />
                    <p className="relative z-[3] text-[10px] font-medium leading-tight truncate drop-shadow">{clip.name}</p>
                    {clipHasAudioControls(clip) && !(clip.kind === 'VIDEO' && clip.audioDetached) && (
                      <ClipWaveform clip={clip} asset={asset} selected={selected} pxPerSecond={pxPerSecond} />
                    )}
                    {clipRect.width >= 88 && (
                      <p className={`relative z-[3] text-[9px] truncate leading-tight drop-shadow ${clip.kind === 'VIDEO' || clip.kind === 'IMAGE' ? 'text-white/80' : 'opacity-75'}`}>{kindLabel(clip.kind)} - {formatTime(clip.durationMs)}</p>
                    )}
                    <div
                      className="absolute right-0 top-0 h-full w-2 cursor-ew-resize hover:bg-accent-primary/40"
                      onPointerDown={(event) => startResize(event, clip, 'end')}
                      aria-hidden="true"
                    />
                  </div>
                );
              })}
              {touchingPairs.map(pair => {
                const transition = transitions.find(item => item.fromClipId === pair.fromClip.id && item.toClipId === pair.toClip.id);
                const laneMetric = laneMetrics.find(metric => metric.layer === pair.layer)!;
                const definition = transition ? STUDIO_TRANSITIONS.find(item => item.type === transition.type) : null;
                const Icon = definition?.icon || Wand2;
                const markerWidth = transition
                  ? Math.max(30, (transition.durationMs / 1000) * pxPerSecond)
                  : 18;
                return (
                  <div
                    key={`${pair.fromClip.id}-${pair.toClip.id}`}
                    role={transition ? 'button' : undefined}
                    tabIndex={transition ? 0 : -1}
                    title={transition ? `${transition.name} - ${formatTime(transition.durationMs)}` : 'Upuść tutaj przejście'}
                    onPointerDown={event => event.stopPropagation()}
                    onClick={event => {
                      event.stopPropagation();
                      if (transition) {
                        onSelectClip(null);
                        onSelectTransition(transition.id);
                      }
                    }}
                    onDragOver={event => {
                      const types = Array.from(event.dataTransfer.types);
                      if (types.includes('application/x-studio-transition') || types.includes('text/plain')) {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'copy';
                      }
                    }}
                    onDrop={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      const type = transitionTypeFromTransfer(event.dataTransfer);
                      if (type) {
                        onAddTransition(type, pair.fromClip.id, pair.toClip.id, pair.boundaryMs);
                      }
                    }}
                    className={transition
                      ? `absolute z-20 flex items-center justify-center rounded-md border text-white shadow-lg cursor-pointer transition-colors ${selectedTransitionId === transition.id ? 'border-white bg-violet-500 ring-2 ring-violet-300/60' : 'border-violet-300/80 bg-violet-600/90 hover:bg-violet-500'}`
                      : 'absolute z-10 rounded border border-dashed border-violet-400/0 bg-violet-500/0 hover:border-violet-400/80 hover:bg-violet-500/20 transition-colors'}
                    style={{
                      left: (pair.boundaryMs / 1000) * pxPerSecond - markerWidth / 2,
                      top: laneMetric.top + 4,
                      width: markerWidth,
                      height: laneMetric.height - 8,
                    }}
                  >
                    {transition && <Icon className="h-3.5 w-3.5" />}
                  </div>
                );
              })}
              {selectionRect && (
                <div
                  className="absolute z-30 border border-accent-primary bg-accent-primary/10 pointer-events-none"
                  style={selectionRect}
                />
              )}
            </div>
          </div>

          <div
            className="absolute top-0 bottom-0 z-40 w-px -translate-x-1/2 bg-danger pointer-events-none"
            style={{ left: TIMELINE_LABEL_WIDTH + playheadLeft }}
          />
          {snapGuideMs !== null && Math.abs(snapGuideMs - playheadMs) > 1 && (
            <div
              className="absolute top-0 bottom-0 z-40 w-px -translate-x-1/2 bg-accent-primary pointer-events-none shadow-[0_0_8px_rgba(59,130,246,0.9)]"
              style={{ left: TIMELINE_LABEL_WIDTH + (snapGuideMs / 1000) * pxPerSecond }}
            />
          )}
        </div>
      </div>
      {contextMenu && createPortal(
        <div
          role="menu"
          className="fixed z-[12000] min-w-[8rem] rounded-lg bg-surface-raised border border-border shadow-glass backdrop-blur-xl py-1 animate-scale-in origin-top-left"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          {menuItems.map(item => (
            <div key={item.id}>
              {item.separatorBefore && <div className="my-1 border-t border-border-subtle" />}
              <button
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                if (!item.disabled) item.onClick();
              }}
              className={[
                'w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left transition-colors',
                item.disabled
                  ? 'opacity-50 cursor-not-allowed text-text-disabled'
                  : item.destructive
                    ? 'text-danger hover:bg-danger/10'
                    : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover',
              ].join(' ')}
              >
                <span className="inline-flex items-center justify-center w-3.5 h-3.5 shrink-0 [&>svg]:w-full [&>svg]:h-full" aria-hidden="true">
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

export function StudioEditorPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  const [activePanel, setActivePanel] = useState<StudioPanelKey>('media');
  const [clips, setClips] = useState<StudioTimelineClip[]>([]);
  const [layers, setLayers] = useState<StudioTimelineLayer[]>(() => layerSettingsFrom(null));
  const [transitions, setTransitions] = useState<StudioTimelineTransition[]>([]);
  const [durationMs, setDurationMs] = useState(BASE_TIMELINE_DURATION_MS);
  const [playheadMs, setPlayheadMs] = useState(0);
  const [timelineZoom, setTimelineZoom] = useState(DEFAULT_PX_PER_SECOND);
  const [timelineHeight, setTimelineHeight] = useState(330);
  const [projectSettings, setProjectSettings] = useState<StudioProjectSettings>(DEFAULT_STUDIO_SETTINGS);
  const [copiedClip, setCopiedClip] = useState<StudioTimelineClip | null>(null);
  const [selectedClipIds, setSelectedClipIds] = useState<string[]>([]);
  const [selectedTransitionId, setSelectedTransitionId] = useState<string | null>(null);
  const [historyPast, setHistoryPast] = useState<TimelineHistorySnapshot[]>([]);
  const [historyFuture, setHistoryFuture] = useState<TimelineHistorySnapshot[]>([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlaybackPreparing, setIsPlaybackPreparing] = useState(false);
  const [isPlaybackBuffering, setIsPlaybackBuffering] = useState(false);
  const [isDraggingClip, setIsDraggingClip] = useState(false);
  const [timelineSaveError, setTimelineSaveError] = useState<string | null>(null);
  const [timelineSaving, setTimelineSaving] = useState(false);
  const [activeLabProductId, setActiveLabProductId] = useState('');
  const [previewAssetId, setPreviewAssetId] = useState<string | null>(null);
  const hydratedProjectId = useRef<string | null>(null);
  const playbackStartRequest = useRef(0);
  const lastFrame = useRef<number | null>(null);
  const lastSavedTimelineKey = useRef('');
  const timelineSaveQueue = useRef<Promise<void>>(Promise.resolve());
  const pendingTimelineSaves = useRef(0);
  const hydrationExpectedTimelineKey = useRef<string | null>(null);
  const latestTimeline = useRef<TimelineHistorySnapshot>({ durationMs: BASE_TIMELINE_DURATION_MS, clips: [], layers: layerSettingsFrom(null), transitions: [] });
  const latestProjectSettings = useRef<StudioProjectSettings>(DEFAULT_STUDIO_SETTINGS);

  const projectQuery = useQuery({
    queryKey: ['studio', 'project', projectId],
    queryFn: async () => (await studioApi.getProject(projectId!)).data.data,
    enabled: Boolean(projectId),
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: {
      timeline?: { durationMs: number; clips: StudioTimelineClip[]; layers: StudioTimelineLayer[]; transitions: StudioTimelineTransition[] };
      durationMs?: number;
      labProductId?: string | null;
      settings?: Record<string, unknown>;
    }) =>
      (await studioApi.updateProject(projectId!, payload)).data.data,
  });

  const enqueueTimelineSave = useCallback((snapshot: TimelineHistorySnapshot, key: string) => {
    if (!projectId) return Promise.resolve();
    const timeline = cloneTimelineSnapshot(snapshot);
    pendingTimelineSaves.current += 1;
    setTimelineSaving(true);
    const save = timelineSaveQueue.current
      .catch(() => undefined)
      .then(async () => {
        try {
          await studioApi.updateProject(projectId, {
            timeline,
            durationMs: timeline.durationMs,
          });
          lastSavedTimelineKey.current = key;
          const latest = latestTimeline.current;
          if (timelineKey(latest.durationMs, latest.clips, latest.layers, latest.transitions) === key) {
            removeTimelineDraft(projectId);
          }
          setTimelineSaveError(null);
        } catch (error) {
          writeTimelineDraft(projectId, timeline);
          setTimelineSaveError(errorMessage(error));
        }
      })
      .finally(() => {
        pendingTimelineSaves.current = Math.max(0, pendingTimelineSaves.current - 1);
        if (pendingTimelineSaves.current === 0) setTimelineSaving(false);
      });
    timelineSaveQueue.current = save;
    return save;
  }, [projectId]);

  const project = projectQuery.data;
  const snapEnabled = projectSettings.snapEnabled;
  const mainTrackMagnetEnabled = projectSettings.mainTrackMagnetEnabled;
  const aspectRatio = projectSettings.aspectRatio;
  const assets = project?.assets || [];
  const assetMap = useMemo(() => new Map(assets.map(asset => [asset.id, asset])), [assets]);
  const selectedClipId = selectedClipIds[0] || null;
  const selectedClips = selectedClipIds.flatMap(id => clips.find(clip => clip.id === id) || []);
  const contentEndMs = useMemo(() => clips.length > 0 ? Math.max(...clips.map(clipEnd)) : 0, [clips]);
  const selectedClip = selectedClipId ? clips.find(clip => clip.id === selectedClipId) || null : null;
  const selectedTransition = selectedTransitionId ? transitions.find(transition => transition.id === selectedTransitionId) || null : null;
  const activeClip = useMemo(() => {
    return clips
      .filter(clip => (
        clip.kind !== 'AUDIO'
        && !layerSetting(layers, clip.layer).hidden
        && playheadMs >= clip.startMs
        && playheadMs < clip.startMs + clip.durationMs
      ))
      .sort((a, b) => b.layer - a.layer)[0] || null;
  }, [clips, layers, playheadMs]);
  const activeAsset = activeClip ? assetMap.get(activeClip.assetId) || null : null;
  const activeBackgroundClip = useMemo(() => clips
    .filter(clip => (
      (clip.kind === 'VIDEO' || clip.kind === 'IMAGE')
      && !layerSetting(layers, clip.layer).hidden
      && playheadMs >= clip.startMs
      && playheadMs < clip.startMs + clip.durationMs
    ))
    .sort((a, b) => b.layer - a.layer)[0] || null, [clips, layers, playheadMs]);
  const activeBackgroundAsset = activeBackgroundClip ? assetMap.get(activeBackgroundClip.assetId) || null : null;
  const previewPlaybackClip = activeClip?.kind === 'VIDEO' ? activeClip : activeBackgroundClip?.kind === 'VIDEO' ? activeBackgroundClip : null;
  const previewPlaybackMuted = Boolean(!previewPlaybackClip
    || previewPlaybackClip.audioDetached
    || layerSetting(layers, previewPlaybackClip.layer).muted
    || layerSetting(layers, previewPlaybackClip.layer).hidden);
  const activeTimelineTransition = useMemo(() => transitions.find(transition => (
    playheadMs >= transition.boundaryMs - transition.durationMs / 2
    && playheadMs <= transition.boundaryMs + transition.durationMs / 2
  )) || null, [playheadMs, transitions]);
  const transitionFromClip = activeTimelineTransition ? clips.find(clip => clip.id === activeTimelineTransition.fromClipId) || null : null;
  const transitionToClip = activeTimelineTransition ? clips.find(clip => clip.id === activeTimelineTransition.toClipId) || null : null;
  const transitionFromAsset = transitionFromClip ? assetMap.get(transitionFromClip.assetId) || null : null;
  const transitionToAsset = transitionToClip ? assetMap.get(transitionToClip.assetId) || null : null;
  const previewAsset = previewAssetId ? assetMap.get(previewAssetId) || null : null;
  const videoPreloadCandidates = useMemo<StudioVideoPreloadCandidate[]>(() => clips.flatMap(clip => {
    if (clip.kind !== 'VIDEO') return [];
    const asset = assetMap.get(clip.assetId);
    if (!asset?.previewUrl) return [];
    return [{
      assetId: clip.assetId,
      url: asset.previewUrl,
      startMs: clip.startMs,
      endMs: clipEnd(clip),
      hidden: Boolean(layerSetting(layers, clip.layer).hidden),
    }];
  }), [assetMap, clips, layers]);
  const preloadVideoUrls = useMemo(() => selectStudioVideoPreloadUrls(
    videoPreloadCandidates,
    playheadMs,
  ), [playheadMs, videoPreloadCandidates]);
  const preloadVideoUrlsKey = JSON.stringify(preloadVideoUrls);

  useEffect(() => {
    const urls = JSON.parse(preloadVideoUrlsKey) as string[];
    void Promise.all(urls.map(url => warmStudioVideo(url)));
  }, [preloadVideoUrlsKey]);

  const togglePlayback = useCallback(async () => {
    const requestId = ++playbackStartRequest.current;
    if (isPlaying || isPlaybackPreparing) {
      setIsPlaybackPreparing(false);
      setIsPlaybackBuffering(false);
      setIsPlaying(false);
      return;
    }
    if (contentEndMs <= 0) return;
    const startMs = playheadMs >= contentEndMs - 1 ? 0 : playheadMs;
    if (startMs !== playheadMs) setPlayheadMs(startMs);
    const urls = selectStudioVideoPreloadUrls(videoPreloadCandidates, startMs, { horizonMs: 15_000, limit: 3 });
    if (urls.length > 0) {
      setIsPlaybackPreparing(true);
      await Promise.all(urls.map(url => warmStudioVideo(url)));
      if (playbackStartRequest.current !== requestId) return;
      setIsPlaybackPreparing(false);
    }
    if (playbackStartRequest.current !== requestId) return;
    setIsPlaying(true);
  }, [contentEndMs, isPlaybackPreparing, isPlaying, playheadMs, videoPreloadCandidates]);

  useEffect(() => {
    if (!previewAssetId) return;
    const closePreviewOutside = (event: PointerEvent) => {
      const target = event.target;
      if (
        target instanceof Element
        && target.closest('[data-studio-source-preview-content], [data-studio-asset-preview-trigger]')
      ) return;
      setPreviewAssetId(null);
    };
    document.addEventListener('pointerdown', closePreviewOutside);
    return () => document.removeEventListener('pointerdown', closePreviewOutside);
  }, [previewAssetId]);

  const startTimelineResize = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const startY = event.clientY;
    const initialHeight = timelineHeight;
    const move = (moveEvent: PointerEvent) => {
      setTimelineHeight(clamp(initialHeight + startY - moveEvent.clientY, 180, Math.round(window.innerHeight * 0.72)));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, [timelineHeight]);

  useEffect(() => {
    if (!project || hydratedProjectId.current === project.id) return;
    const serverClips = normalizeClipLayout(project.timeline?.clips || []);
    const serverDuration = Math.max(project.timeline?.durationMs || project.durationMs || BASE_TIMELINE_DURATION_MS, BASE_TIMELINE_DURATION_MS);
    const serverLayers = layerSettingsFrom(project.timeline);
    const serverTransitions = transitionsFrom(project.timeline, serverClips);
    const serverSnapshot: TimelineHistorySnapshot = {
      durationMs: serverDuration,
      clips: serverClips,
      layers: serverLayers,
      transitions: serverTransitions,
    };
    const localDraft = readTimelineDraft(project.id);
    const serverTimelineKey = timelineKey(serverDuration, serverClips, serverLayers, serverTransitions);
    const localTimelineKey = localDraft
      ? timelineKey(
        localDraft.timeline.durationMs,
        localDraft.timeline.clips,
        localDraft.timeline.layers,
        localDraft.timeline.transitions,
      )
      : null;
    const hasUnsavedLocalDraft = Boolean(localDraft && localTimelineKey !== serverTimelineKey);
    if (localDraft && !hasUnsavedLocalDraft) removeTimelineDraft(project.id);
    const restoredSnapshot = hasUnsavedLocalDraft && localDraft
      ? cloneTimelineSnapshot(localDraft.timeline)
      : serverSnapshot;
    const nextClips = normalizeClipLayout(restoredSnapshot.clips);
    const nextDuration = Math.max(restoredSnapshot.durationMs, BASE_TIMELINE_DURATION_MS);
    const nextSettings = studioSettingsFrom(project.settings);
    const nextLayers = layerSettingsFrom({ layers: restoredSnapshot.layers });
    const nextTransitions = transitionsFrom({ transitions: restoredSnapshot.transitions }, nextClips);
    hydrationExpectedTimelineKey.current = timelineKey(nextDuration, nextClips, nextLayers, nextTransitions);
    hydratedProjectId.current = project.id;
    lastSavedTimelineKey.current = serverTimelineKey;
    latestTimeline.current = { durationMs: nextDuration, clips: nextClips, layers: nextLayers, transitions: nextTransitions };
    latestProjectSettings.current = nextSettings;
    setClips(nextClips);
    setLayers(nextLayers);
    setTransitions(nextTransitions);
    setDurationMs(nextDuration);
    setProjectSettings(nextSettings);
    setActiveLabProductId(project.labProductId || '');
    setPlayheadMs(0);
    setSelectedClipIds([]);
    setSelectedTransitionId(null);
    setTimelineSaveError(null);
    setHistoryPast([]);
    setHistoryFuture([]);
  }, [project]);

  useEffect(() => {
    if (!projectId || hydratedProjectId.current !== projectId) return;
    const snapshot = { durationMs, clips, layers, transitions };
    const key = timelineKey(durationMs, clips, layers, transitions);
    const expectedHydrationKey = hydrationExpectedTimelineKey.current;
    if (expectedHydrationKey) {
      if (key !== expectedHydrationKey) return;
      hydrationExpectedTimelineKey.current = null;
    }
    latestTimeline.current = snapshot;
    if (key === lastSavedTimelineKey.current) return;
    if (isDraggingClip) return;
    writeTimelineDraft(projectId, snapshot);
    const timeout = window.setTimeout(() => {
      void enqueueTimelineSave(snapshot, key);
    }, 500);
    return () => window.clearTimeout(timeout);
  }, [clips, durationMs, enqueueTimelineSave, isDraggingClip, layers, projectId, transitions]);

  useEffect(() => {
    setTransitions(previous => {
      const next = reconcileTransitions(previous, clips);
      return JSON.stringify(next) === JSON.stringify(previous) ? previous : next;
    });
  }, [clips]);

  useEffect(() => {
    const highestLayer = Math.max(4, ...clips.filter(clip => clip.kind !== 'AUDIO').map(clip => clip.layer));
    const requiredLayers = [
      ...Array.from({ length: Math.min(MAX_LAYER, highestLayer) + 1 }, (_, layer) => layer),
      ...AUDIO_LAYERS,
    ];
    setLayers(previous => {
      const byLayer = new Map(previous.map(item => [item.layer, item]));
      const next = requiredLayers
        .map(layer => byLayer.get(layer) || { layer, locked: false, hidden: false, muted: false })
        .sort((a, b) => b.layer - a.layer);
      return JSON.stringify(next) === JSON.stringify(previous) ? previous : next;
    });
  }, [clips]);

  useEffect(() => {
    if (selectedTransitionId && !transitions.some(transition => transition.id === selectedTransitionId)) {
      setSelectedTransitionId(null);
    }
  }, [selectedTransitionId, transitions]);

  const persistTimelineBeforeExit = useCallback(() => {
    if (!projectId || hydratedProjectId.current !== projectId) return;
    const latest = latestTimeline.current;
    const key = timelineKey(latest.durationMs, latest.clips, latest.layers, latest.transitions);
    if (key === lastSavedTimelineKey.current) return;
    writeTimelineDraft(projectId, latest);
    void studioApi.updateProjectKeepalive(projectId, {
      timeline: latest,
      durationMs: latest.durationMs,
    }).catch(() => {});
  }, [projectId]);

  const saveTimelineNow = useCallback(() => {
    if (!projectId || hydratedProjectId.current !== projectId) return;
    const snapshot = cloneTimelineSnapshot(latestTimeline.current);
    const key = timelineKey(snapshot.durationMs, snapshot.clips, snapshot.layers, snapshot.transitions);
    writeTimelineDraft(projectId, snapshot);
    void enqueueTimelineSave(snapshot, key);
  }, [enqueueTimelineSave, projectId]);

  useEffect(() => {
    window.addEventListener('pagehide', persistTimelineBeforeExit);
    window.addEventListener('beforeunload', persistTimelineBeforeExit);
    window.addEventListener('popstate', persistTimelineBeforeExit);
    const saveWhenHidden = () => {
      if (document.visibilityState === 'hidden') persistTimelineBeforeExit();
    };
    document.addEventListener('visibilitychange', saveWhenHidden);
    return () => {
      window.removeEventListener('pagehide', persistTimelineBeforeExit);
      window.removeEventListener('beforeunload', persistTimelineBeforeExit);
      window.removeEventListener('popstate', persistTimelineBeforeExit);
      document.removeEventListener('visibilitychange', saveWhenHidden);
      if (!projectId || hydratedProjectId.current !== projectId) return;
      const latest = latestTimeline.current;
      const key = timelineKey(latest.durationMs, latest.clips, latest.layers, latest.transitions);
      if (key === lastSavedTimelineKey.current) return;
      writeTimelineDraft(projectId, latest);
      void enqueueTimelineSave(latest, key);
    };
  }, [enqueueTimelineSave, persistTimelineBeforeExit, projectId]);

  useEffect(() => {
    if (!isPlaying || isPlaybackBuffering || isPlaybackPreparing) {
      lastFrame.current = null;
      return;
    }
    let frameId = 0;
    const tick = (timestamp: number) => {
      if (lastFrame.current == null) lastFrame.current = timestamp;
      const delta = timestamp - lastFrame.current;
      if (delta >= 1000 / 30) {
        lastFrame.current = timestamp;
        setPlayheadMs(prev => {
          const next = prev + delta;
          if (next >= contentEndMs) {
            setIsPlaying(false);
            return contentEndMs;
          }
          return next;
        });
      }
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [contentEndMs, isPlaybackBuffering, isPlaybackPreparing, isPlaying]);

  const copyClip = useCallback((clipId: string) => {
    const clip = clips.find(item => item.id === clipId);
    if (clip) setCopiedClip(clip);
  }, [clips]);

  const recordTimelineHistory = useCallback(() => {
    const snapshot = cloneTimelineSnapshot(latestTimeline.current);
    const snapshotKey = timelineKey(snapshot.durationMs, snapshot.clips, snapshot.layers, snapshot.transitions);
    setHistoryPast(prev => {
      const last = prev[prev.length - 1];
      if (last && timelineKey(last.durationMs, last.clips, last.layers, last.transitions) === snapshotKey) return prev;
      return [...prev.slice(-49), snapshot];
    });
    setHistoryFuture([]);
  }, []);

  const applyTimelineSnapshot = useCallback((snapshot: TimelineHistorySnapshot) => {
    const next = cloneTimelineSnapshot(snapshot);
    latestTimeline.current = next;
    setClips(next.clips);
    setLayers(next.layers);
    setTransitions(next.transitions);
    setDurationMs(next.durationMs);
    setPlayheadMs(prev => clamp(prev, 0, next.durationMs));
    setSelectedClipIds([]);
    setSelectedTransitionId(null);
  }, []);

  const undoTimelineChange = useCallback(() => {
    const target = historyPast[historyPast.length - 1];
    if (!target) return;
    const current = cloneTimelineSnapshot(latestTimeline.current);
    setHistoryPast(prev => prev.slice(0, -1));
    setHistoryFuture(prev => [current, ...prev].slice(0, 50));
    applyTimelineSnapshot(target);
  }, [applyTimelineSnapshot, historyPast]);

  const redoTimelineChange = useCallback(() => {
    const target = historyFuture[0];
    if (!target) return;
    const current = cloneTimelineSnapshot(latestTimeline.current);
    setHistoryFuture(prev => prev.slice(1));
    setHistoryPast(prev => [...prev.slice(-49), current]);
    applyTimelineSnapshot(target);
  }, [applyTimelineSnapshot, historyFuture]);

  const pasteClip = useCallback((startMs: number, layer: number) => {
    if (!copiedClip) return;
    recordTimelineHistory();
    const nextClip = duplicateClip(copiedClip, Math.max(0, startMs), layer);
    const requestedLayer = nextClip.kind === 'AUDIO'
      ? clamp(nextClip.layer, MIN_LAYER, -1)
      : clamp(nextClip.layer, 0, MAX_LAYER);
    const openLayer = !layerAcceptsKind(clips, requestedLayer, nextClip.kind)
      || hasLayerCollision(clips, null, requestedLayer, nextClip.startMs, nextClip.durationMs)
      ? nextClip.kind === 'AUDIO'
        ? findOpenAudioLayer(clips, nextClip.startMs, nextClip.durationMs)
        : findOpenLayer(clips, nextClip.startMs, nextClip.durationMs, nextClip.kind)
      : requestedLayer;
    const placedClip = hasLayerCollision(clips, null, openLayer, nextClip.startMs, nextClip.durationMs)
      ? { ...nextClip, layer: nextClip.kind === 'AUDIO' ? -1 : 0, startMs: nextNonOverlappingStart(clips, nextClip.kind === 'AUDIO' ? -1 : 0) }
      : { ...nextClip, layer: openLayer };
    const nextClips = sortClips([...clips, placedClip]);
    const nextDuration = Math.max(durationMs, placedClip.startMs + placedClip.durationMs + 1000, BASE_TIMELINE_DURATION_MS);
    latestTimeline.current = { durationMs: nextDuration, clips: nextClips, layers, transitions };
    setClips(nextClips);
    setDurationMs(nextDuration);
    setSelectedClipIds([placedClip.id]);
  }, [clips, copiedClip, durationMs, layers, recordTimelineHistory, transitions]);

  const duplicateTimelineClip = useCallback((clipId: string) => {
    const sourceClip = clips.find(clip => clip.id === clipId);
    if (!sourceClip) return;
    recordTimelineHistory();
    const startMs = clipEnd(sourceClip);
    const duplicate = duplicateClip(sourceClip, startMs, sourceClip.layer);
    const targetLayer = findDropLayer(
      clips,
      layers,
      duplicate.id,
      duplicate.layer,
      duplicate.startMs,
      duplicate.durationMs,
      duplicate.kind,
    );
    const placedClip = { ...duplicate, layer: targetLayer };
    const nextClips = sortClips([...clips, placedClip]);
    const nextDuration = Math.max(durationMs, clipEnd(placedClip) + 1000, BASE_TIMELINE_DURATION_MS);
    latestTimeline.current = { durationMs: nextDuration, clips: nextClips, layers, transitions };
    setClips(nextClips);
    setDurationMs(nextDuration);
    setSelectedClipIds([placedClip.id]);
  }, [clips, durationMs, layers, recordTimelineHistory, transitions]);

  const updateProjectSettings = useCallback((patch: Partial<StudioProjectSettings>) => {
    const nextSettings = studioSettingsFrom({
      ...latestProjectSettings.current,
      ...patch,
    });
    latestProjectSettings.current = nextSettings;
    setProjectSettings(nextSettings);
    if (!projectId) return;
    updateMutation.mutate({ settings: nextSettings }, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['studio', 'project', projectId] });
      },
    });
  }, [projectId, queryClient, updateMutation]);

  const updateClip = useCallback((clipId: string, patch: Partial<StudioTimelineClip>, options?: ClipUpdateOptions) => {
    if (options?.recordHistory !== false) recordTimelineHistory();
    const currentClip = latestTimeline.current.clips.find(clip => clip.id === clipId);
    const nextTimelineDuration = currentClip && (patch.startMs !== undefined || patch.durationMs !== undefined)
      ? Math.max(
        durationMs,
        (patch.startMs ?? currentClip.startMs) + (patch.durationMs ?? currentClip.durationMs) + 1000,
        BASE_TIMELINE_DURATION_MS,
      )
      : durationMs;
    if (nextTimelineDuration > durationMs) setDurationMs(nextTimelineDuration);
    setClips(prev => {
      const nextClips = sortClips(prev.map(clip => {
        if (clip.id !== clipId) return clip;
        if (layerSetting(layers, clip.layer).locked) return clip;
        const nextClip: StudioTimelineClip = {
          ...clip,
          ...patch,
          position: patch.position ? patch.position : clip.position,
          layer: patch.layer !== undefined
            ? clip.kind === 'AUDIO'
              ? clamp(Math.round(patch.layer), MIN_LAYER, -1)
              : clamp(Math.round(patch.layer), 0, MAX_LAYER)
            : clip.layer,
          startMs: patch.startMs !== undefined ? Math.max(0, patch.startMs) : clip.startMs,
          durationMs: patch.durationMs !== undefined ? Math.max(500, patch.durationMs) : clip.durationMs,
          speed: patch.speed !== undefined ? normalizePlaybackSpeed(patch.speed, normalizePlaybackSpeed(clip.speed)) : clip.speed,
          scaleX: patch.scaleX !== undefined ? clamp(patch.scaleX, 0.1, 8) : clip.scaleX,
          scaleY: patch.scaleY !== undefined ? clamp(patch.scaleY, 0.1, 8) : clip.scaleY,
          volumeDb: patch.volumeDb !== undefined ? clamp(patch.volumeDb, -60, 12) : clip.volumeDb,
          fadeInMs: patch.fadeInMs !== undefined ? Math.max(0, patch.fadeInMs) : clip.fadeInMs,
          fadeOutMs: patch.fadeOutMs !== undefined ? Math.max(0, patch.fadeOutMs) : clip.fadeOutMs,
        };
        const timelineChanged = patch.layer !== undefined || patch.startMs !== undefined || patch.durationMs !== undefined;
        if (
          timelineChanged
          && !options?.allowCollision
          && hasLayerCollision(prev, clip.id, nextClip.layer, nextClip.startMs, nextClip.durationMs)
        ) {
          return clip;
        }
        return nextClip;
      }));
      latestTimeline.current = { durationMs: nextTimelineDuration, clips: nextClips, layers, transitions };
      return nextClips;
    });
  }, [durationMs, layers, recordTimelineHistory, transitions]);

  const updateClips = useCallback((updates: ClipBatchUpdate[], options?: ClipUpdateOptions) => {
    if (updates.length === 0) return;
    if (options?.recordHistory !== false) recordTimelineHistory();
    const patches = new Map(updates.map(update => [update.clipId, update.patch]));
    setClips(prev => {
      const nextClips = sortClips(prev.map(clip => {
        const patch = patches.get(clip.id);
        if (!patch || (layerSetting(layers, clip.layer).locked && !options?.ignoreLayerLock)) return clip;
        const nextClip: StudioTimelineClip = {
          ...clip,
          ...patch,
          position: patch.position ? patch.position : clip.position,
          layer: patch.layer !== undefined
            ? clip.kind === 'AUDIO'
              ? clamp(Math.round(patch.layer), MIN_LAYER, -1)
              : clamp(Math.round(patch.layer), 0, MAX_LAYER)
            : clip.layer,
          startMs: patch.startMs !== undefined ? Math.max(0, patch.startMs) : clip.startMs,
          durationMs: patch.durationMs !== undefined ? Math.max(500, patch.durationMs) : clip.durationMs,
          speed: patch.speed !== undefined ? normalizePlaybackSpeed(patch.speed, normalizePlaybackSpeed(clip.speed)) : clip.speed,
          scaleX: patch.scaleX !== undefined ? clamp(patch.scaleX, 0.1, 8) : clip.scaleX,
          scaleY: patch.scaleY !== undefined ? clamp(patch.scaleY, 0.1, 8) : clip.scaleY,
          volumeDb: patch.volumeDb !== undefined ? clamp(patch.volumeDb, -60, 12) : clip.volumeDb,
          fadeInMs: patch.fadeInMs !== undefined ? Math.max(0, patch.fadeInMs) : clip.fadeInMs,
          fadeOutMs: patch.fadeOutMs !== undefined ? Math.max(0, patch.fadeOutMs) : clip.fadeOutMs,
        };
        const timelineChanged = patch.layer !== undefined || patch.startMs !== undefined || patch.durationMs !== undefined;
        if (
          timelineChanged
          && !options?.allowCollision
          && hasLayerCollision(prev, clip.id, nextClip.layer, nextClip.startMs, nextClip.durationMs)
        ) {
          return clip;
        }
        return nextClip;
      }));
      latestTimeline.current = { durationMs, clips: nextClips, layers, transitions };
      return nextClips;
    });
  }, [durationMs, layers, recordTimelineHistory, transitions]);

  const updateSelectedClips = useCallback((updater: (clip: StudioTimelineClip) => Partial<StudioTimelineClip>) => {
    if (selectedClips.length === 0) return;
    recordTimelineHistory();
    selectedClips.forEach(clip => updateClip(clip.id, updater(clip), { recordHistory: false }));
  }, [recordTimelineHistory, selectedClips, updateClip]);

  const updateTextClip = useCallback((
    patch: Partial<StudioTimelineClip>,
    options?: { applyAll?: boolean; relativeFields?: StudioTextNumericField[] },
  ) => {
    if (!selectedClip || (selectedClip.kind !== 'TEXT' && selectedClip.kind !== 'SCRIPT')) return;
    const targetIds = options?.applyAll
      ? new Set(clips.filter(clip => clip.kind === 'TEXT' || clip.kind === 'SCRIPT').map(clip => clip.id))
      : new Set([selectedClip.id]);
    const numericDefaults: Record<StudioTextNumericField, number> = {
      fontSize: 48,
      textStrokeWidth: 0,
      textBackgroundOpacity: 70,
      textBorderRadius: 16,
      textPadding: 16,
      letterSpacing: 0,
      lineHeight: 1.15,
    };
    const deltas = new Map((options?.relativeFields || []).map(field => {
      const requested = patch[field];
      const current = selectedClip[field];
      return [field, Number(requested ?? numericDefaults[field]) - Number(current ?? numericDefaults[field])];
    }));
    recordTimelineHistory();
    setClips(previous => {
      const nextClips = sortClips(previous.map(clip => {
        if (!targetIds.has(clip.id) || layerSetting(layers, clip.layer).locked) return clip;
        const nextPatch: Partial<StudioTimelineClip> = { ...patch };
        deltas.forEach((delta, field) => {
          (nextPatch as Record<string, unknown>)[field] = Number(clip[field] ?? numericDefaults[field]) + delta;
        });
        return { ...clip, ...nextPatch };
      }));
      latestTimeline.current = { durationMs, clips: nextClips, layers, transitions };
      return nextClips;
    });
  }, [clips, durationMs, layers, recordTimelineHistory, selectedClip, transitions]);

  const updateLayer = useCallback((layer: number, patch: Partial<StudioTimelineLayer>) => {
    recordTimelineHistory();
    setLayers(prev => {
      const nextLayers = prev.map(item => item.layer === layer ? { ...item, ...patch, layer } : item);
      latestTimeline.current = { durationMs, clips, layers: nextLayers, transitions };
      return nextLayers;
    });
  }, [clips, durationMs, recordTimelineHistory, transitions]);

  const insertLayer = useCallback((layer: number) => {
    setLayers(prev => {
      const nextLayers = [
        ...prev
          .filter(setting => setting.layer !== layer)
          .map(setting => setting.layer >= layer && setting.layer >= 0
            ? { ...setting, layer: Math.min(MAX_LAYER, setting.layer + 1) }
            : setting),
        { layer, locked: false, hidden: false, muted: false },
      ]
        .filter((setting, index, all) => all.findIndex(item => item.layer === setting.layer) === index)
        .sort((a, b) => b.layer - a.layer);
      latestTimeline.current = { durationMs, clips, layers: nextLayers, transitions };
      return nextLayers;
    });
  }, [clips, durationMs, transitions]);

  const addTransition = useCallback((type: string, fromClipId: string, toClipId: string, boundaryMs: number) => {
    const definition = STUDIO_TRANSITIONS.find(item => item.type === type);
    if (!definition) return;
    recordTimelineHistory();
    const existing = transitions.find(item => item.fromClipId === fromClipId && item.toClipId === toClipId);
    const nextTransition: StudioTimelineTransition = {
      id: existing?.id || newClipId(),
      type,
      name: definition.name,
      fromClipId,
      toClipId,
      boundaryMs,
      durationMs: existing?.durationMs || 500,
    };
    const next = existing
      ? transitions.map(item => item.id === existing.id ? nextTransition : item)
      : [...transitions, nextTransition];
    latestTimeline.current = { durationMs, clips, layers, transitions: next };
    setTransitions(next);
    setSelectedClipIds([]);
    setSelectedTransitionId(nextTransition.id);
  }, [clips, durationMs, layers, recordTimelineHistory, transitions]);

  const updateTransition = useCallback((transitionId: string, patch: Partial<StudioTimelineTransition>) => {
    recordTimelineHistory();
    setTransitions(previous => {
      const next = previous.map(transition => transition.id === transitionId ? {
        ...transition,
        ...patch,
        id: transition.id,
        durationMs: patch.durationMs !== undefined ? clamp(patch.durationMs, 100, 2000) : transition.durationMs,
      } : transition);
      latestTimeline.current = { durationMs, clips, layers, transitions: next };
      return next;
    });
  }, [clips, durationMs, layers, recordTimelineHistory, transitions]);

  const deleteTransition = useCallback((transitionId: string) => {
    recordTimelineHistory();
    setTransitions(previous => {
      const next = previous.filter(transition => transition.id !== transitionId);
      latestTimeline.current = { durationMs, clips, layers, transitions: next };
      return next;
    });
    setSelectedTransitionId(current => current === transitionId ? null : current);
  }, [clips, durationMs, layers, recordTimelineHistory, transitions]);

  const deleteClips = useCallback((clipIds: string[]) => {
    const requestedIds = new Set(clipIds);
    const deletableIds = new Set(clips
      .filter(clip => requestedIds.has(clip.id) && !layerSetting(layers, clip.layer).locked)
      .map(clip => clip.id));
    if (deletableIds.size === 0) return;
    recordTimelineHistory();
    const deletedClips = clips.filter(clip => deletableIds.has(clip.id));
    const remainingClips = clips.filter(clip => !deletableIds.has(clip.id));
    const magnetizedClips = mainTrackMagnetEnabled
      ? closeMainTrackAfterDeletion(remainingClips, deletedClips)
      : remainingClips;
    const compactedTimeline = compactPositiveLayers(magnetizedClips, layers);
    const nextClips = compactedTimeline.clips;
    const nextLayers = compactedTimeline.layers;
    const nextTransitions = transitions.filter(transition => (
      !deletableIds.has(transition.fromClipId) && !deletableIds.has(transition.toClipId)
    ));
    latestTimeline.current = { durationMs, clips: nextClips, layers: nextLayers, transitions: nextTransitions };
    setClips(nextClips);
    setLayers(nextLayers);
    setTransitions(nextTransitions);
    setSelectedClipIds(previous => previous.filter(id => !deletableIds.has(id)));
  }, [clips, durationMs, layers, mainTrackMagnetEnabled, recordTimelineHistory, transitions]);

  const deleteClip = useCallback((clipId: string) => {
    deleteClips([clipId]);
  }, [deleteClips]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target)) return;
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedClipIds.length > 0) {
        event.preventDefault();
        deleteClips(selectedClipIds);
        return;
      }
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedTransitionId) {
        event.preventDefault();
        deleteTransition(selectedTransitionId);
        return;
      }
      if (event.code === 'Space') {
        event.preventDefault();
        togglePlayback();
        return;
      }
      const modifier = event.metaKey || event.ctrlKey;
      if (!modifier) return;
      const key = event.key.toLowerCase();
      if (key === 'c' && selectedClipId) {
        event.preventDefault();
        copyClip(selectedClipId);
      }
      if (key === 'v' && copiedClip) {
        event.preventDefault();
        pasteClip(playheadMs, copiedClip.layer);
      }
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undoTimelineChange();
      }
      if (key === 'y' || (key === 'z' && event.shiftKey)) {
        event.preventDefault();
        redoTimelineChange();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedClipId, selectedClipIds, selectedTransitionId, copiedClip, copyClip, deleteClips, deleteTransition, pasteClip, playheadMs, redoTimelineChange, togglePlayback, undoTimelineChange]);

  const addAssetToTimeline = useCallback(async (asset: StudioAsset) => {
    const mediaDurationMs = await resolvedAssetDurationMs(asset);
    recordTimelineHistory();
    const targetLayer = asset.kind === 'AUDIO' ? -1 : 0;
    const startMs = nextNonOverlappingStart(clips, targetLayer);
    const placedClip = createClipFromAsset({ ...asset, durationMs: mediaDurationMs }, startMs, targetLayer);
    const nextClips = sortClips([...clips, placedClip]);
    const nextDuration = Math.max(durationMs, placedClip.startMs + placedClip.durationMs + 1000, BASE_TIMELINE_DURATION_MS);
    latestTimeline.current = { durationMs: nextDuration, clips: nextClips, layers, transitions };
    setClips(nextClips);
    setSelectedClipIds([placedClip.id]);
    setDurationMs(nextDuration);
  }, [clips, durationMs, layers, recordTimelineHistory, transitions]);

  const addTextToTimeline = useCallback(() => {
    recordTimelineHistory();
    const textClip: StudioTimelineClip = {
      id: newClipId(),
      assetId: '',
      name: 'Tekst',
      kind: 'TEXT',
      startMs: playheadMs,
      durationMs: 5000,
      sourceStartMs: 0,
      sourceDurationMs: 5000,
      layer: findOpenLayer(clips, playheadMs, 5000, 'TEXT'),
      speed: 1,
      position: { x: 0, y: 0 },
      positionSpace: 'layout',
      scaleX: 1,
      scaleY: 1,
      rotation: 0,
      content: 'Twój tekst',
      fontFamily: 'Inter',
      fontSize: 48,
      fontWeight: 700,
      fontStyle: 'normal',
      textDecoration: 'none',
      textAlign: 'center',
      textColor: '#ffffff',
      textBackgroundColor: '#000000',
      textBackgroundOpacity: 70,
      textBorderRadius: 16,
      textPadding: 16,
      letterSpacing: 0,
      lineHeight: 1.15,
      textShadow: true,
    };
    const nextClips = sortClips([...clips, textClip]);
    const nextDuration = Math.max(durationMs, clipEnd(textClip) + 1000);
    latestTimeline.current = { durationMs: nextDuration, clips: nextClips, layers, transitions };
    setClips(nextClips);
    setDurationMs(nextDuration);
    setSelectedClipIds([textClip.id]);
  }, [clips, durationMs, layers, playheadMs, recordTimelineHistory, transitions]);

  const addTranscriptionsToTimeline = useCallback((results: Array<{
    sourceClip: StudioTimelineClip;
    segments: Array<{ startMs: number; endMs: number; text: string; words?: Array<{ startMs: number; endMs: number; text: string }> }>;
    dynamic: boolean;
    fillToClip: boolean;
  }>) => {
    if (results.length === 0) return;
    recordTimelineHistory();
    const sourceClipIds = new Set(results.map(result => result.sourceClip.id));
    const sourceAssetIds = new Set(results.map(result => result.sourceClip.assetId));
    const sourceCaptionPrefixes = results.map(result => `Napisy ${result.sourceClip.name} `);
    const baseClips = clips.filter(clip => !(
      clip.kind === 'TEXT'
      && clip.captionGroupId
      && (
        (clip.captionSourceClipId && sourceClipIds.has(clip.captionSourceClipId))
        || (clip.captionSourceAssetId && sourceAssetIds.has(clip.captionSourceAssetId) && results.some(result => (
          result.sourceClip.assetId === clip.captionSourceAssetId
          && rangesOverlap(clip.startMs, clip.durationMs, result.sourceClip.startMs, result.sourceClip.durationMs)
        )))
        || (!clip.captionSourceAssetId && sourceCaptionPrefixes.some(prefix => clip.name.startsWith(prefix)))
      )
    ));
    const newClips: StudioTimelineClip[] = [];
    results.forEach(({ sourceClip, segments, dynamic, fillToClip }) => {
      const sourceStart = sourceClip.sourceStartMs || 0;
      const sourceEnd = sourceStart + sourceClip.durationMs * playbackSpeedMagnitude(sourceClip.speed);
      const speed = playbackSpeedMagnitude(sourceClip.speed);
      const usableSegments = segments
        .filter(segment => segment.endMs > sourceStart && segment.startMs < sourceEnd && segment.text.trim())
        .sort((a, b) => a.startMs - b.startMs);
      const mappedSegments = usableSegments.map((segment, index) => {
        const segmentStart = Math.max(sourceStart, segment.startMs);
        const nextSegmentStart = usableSegments[index + 1]?.startMs ?? sourceEnd;
        const segmentEnd = Math.min(sourceEnd, segment.endMs, nextSegmentStart);
        const startMs = sourceClip.startMs + (segmentStart - sourceStart) / speed;
        const segmentDuration = (segmentEnd - segmentStart) / speed;
        return { segment, index, startMs, segmentDuration, segmentStart, segmentEnd };
      }).filter(item => item.segmentDuration > 20);
      const uniqueMappedSegments = mappedSegments.filter(item => {
        const roundedStartMs = Math.round(item.startMs);
        const roundedDurationMs = Math.max(20, Math.floor(item.startMs + item.segmentDuration) - roundedStartMs);
        return !newClips.some(caption => (
          caption.captionSourceAssetId === sourceClip.assetId
          && rangesOverlap(roundedStartMs, roundedDurationMs, caption.startMs, caption.durationMs)
        ));
      });
      if (uniqueMappedSegments.length === 0) return;
      const timelineSegments = uniqueMappedSegments.map((item, index, all) => {
        if (!fillToClip) return item;
        const startMs = index === 0 ? sourceClip.startMs : item.startMs;
        const endMs = all[index + 1]?.startMs ?? clipEnd(sourceClip);
        return {
          ...item,
          startMs,
          segmentDuration: Math.max(20, endMs - startMs),
        };
      });
      const highestLayer = Math.max(4, ...baseClips.filter(clip => clip.kind !== 'AUDIO').map(clip => clip.layer));
      const captionLayer = Array.from({ length: Math.min(MAX_LAYER, highestLayer + 1) + 1 }, (_, layer) => layer)
        .find(layer => (
          layerAcceptsKind([...baseClips, ...newClips], layer, 'TEXT')
          && timelineSegments.every(item => !hasLayerCollision([...baseClips, ...newClips], null, layer, item.startMs, item.segmentDuration))
        )) ?? Math.min(MAX_LAYER, highestLayer + 1);
      const captionGroupId = newClipId();
      timelineSegments.forEach(({ segment, index, startMs, segmentDuration, segmentStart, segmentEnd }) => {
        const roundedStartMs = Math.round(startMs);
        const roundedDurationMs = Math.max(20, Math.floor(startMs + segmentDuration) - roundedStartMs);
        const clippedWords = segment.words?.filter(word => word.endMs > segmentStart && word.startMs < segmentEnd);
        const cueSegment = clippedWords?.length ? {
          ...segment,
          startMs: segmentStart,
          endMs: segmentEnd,
          text: clippedWords.map(word => word.text).join(' ').replace(/\s+([,.!?;:])/g, '$1'),
          words: clippedWords,
        } : {
          ...segment,
          startMs: segmentStart,
          endMs: segmentEnd,
        };
        const captionCues = captionCuesFromSegment(cueSegment).flatMap(cue => {
          const cueSourceStart = Math.max(segmentStart, cueSegment.startMs + cue.startOffsetMs);
          const cueSourceEnd = Math.min(segmentEnd, cueSegment.startMs + cue.endOffsetMs);
          if (cueSourceEnd <= cueSourceStart) return [];
          const cueTimelineStart = sourceClip.startMs + (cueSourceStart - sourceStart) / speed;
          const cueTimelineEnd = sourceClip.startMs + (cueSourceEnd - sourceStart) / speed;
          return [{
            startOffsetMs: Math.max(0, Math.round(cueTimelineStart - roundedStartMs)),
            endOffsetMs: Math.min(roundedDurationMs, Math.round(cueTimelineEnd - roundedStartMs)),
            text: cue.text,
          }];
        }).filter(cue => cue.endOffsetMs > cue.startOffsetMs);
        const continuousCaptionCues = fillToClip ? captionCues.map((cue, cueIndex) => ({
          ...cue,
          startOffsetMs: cueIndex === 0 ? 0 : cue.startOffsetMs,
          endOffsetMs: captionCues[cueIndex + 1]?.startOffsetMs ?? roundedDurationMs,
        })) : captionCues;
        const captionContent = captionCues.length > 0
          ? captionCues.map(cue => cue.text).join(' ').replace(/\s+([,.!?;:])/g, '$1')
          : segment.text.trim();
        newClips.push({
          id: newClipId(),
          assetId: '',
          name: `Napisy ${sourceClip.name} ${index + 1}`,
          kind: 'TEXT' as const,
          startMs: roundedStartMs,
          durationMs: roundedDurationMs,
          sourceStartMs: 0,
          sourceDurationMs: roundedDurationMs,
          layer: captionLayer,
          speed: 1,
          position: { x: 0, y: 220 },
          positionSpace: 'layout',
          scaleX: 1,
          scaleY: 1,
          rotation: 0,
          content: captionContent,
          captionGroupId,
          captionSourceClipId: sourceClip.id,
          captionSourceAssetId: sourceClip.assetId,
          captionCues: continuousCaptionCues,
          dynamicCaption: dynamic && continuousCaptionCues.length > 0,
          fontFamily: 'Montserrat',
          fontSize: 42,
          fontWeight: 800,
          textAlign: 'center' as const,
          textColor: '#ffffff',
          textBackgroundColor: '#000000',
          textBackgroundOpacity: 68,
          textBorderRadius: 12,
          textPadding: 12,
          lineHeight: 1.1,
          textShadow: true,
        });
      });
    });
    if (newClips.length === 0) return;
    ensureGoogleFont('Montserrat');
    const nextClips = sortClips([...baseClips, ...newClips]);
    latestTimeline.current = { durationMs, clips: nextClips, layers, transitions };
    setClips(nextClips);
    setSelectedClipIds([newClips[0].id]);
  }, [clips, durationMs, layers, recordTimelineHistory, transitions]);

  const detachAudio = useCallback((clipId: string) => {
    const sourceClip = clips.find(clip => clip.id === clipId && clip.kind === 'VIDEO');
    if (!sourceClip || sourceClip.audioDetached) return;
    const audioLayer = [-1, -2, -3, -4].find(layer => !hasLayerCollision(clips, null, layer, sourceClip.startMs, sourceClip.durationMs));
    if (audioLayer === undefined) return;
    recordTimelineHistory();
    const audioClip: StudioTimelineClip = {
      ...sourceClip,
      id: newClipId(),
      name: `${sourceClip.name} - audio`,
      kind: 'AUDIO',
      layer: audioLayer,
      audioDetached: false,
      detachedFromClipId: sourceClip.id,
      position: { x: 0, y: 0 },
      positionSpace: 'layout',
      scaleX: 1,
      scaleY: 1,
      rotation: 0,
    };
    const nextClips = sortClips(clips.map(clip => clip.id === clipId ? { ...clip, audioDetached: true } : clip).concat(audioClip));
    latestTimeline.current = { durationMs, clips: nextClips, layers, transitions };
    setClips(nextClips);
    setSelectedClipIds([audioClip.id]);
  }, [clips, durationMs, layers, recordTimelineHistory, transitions]);

  const removeSilence = useCallback(async (clipIds: string[], sensitivity: SilenceSensitivity = 'balanced') => {
    const preset = SILENCE_PRESETS[sensitivity];
    const requestedIds = new Set(clipIds);
    const targets = clips.filter(clip => (
      requestedIds.has(clip.id)
      && clipHasAudioControls(clip)
      && !(clip.kind === 'VIDEO' && clip.audioDetached)
      && !layerSetting(layers, clip.layer).locked
    ));
    const analyzed = await Promise.all(targets.map(async sourceClip => {
      const asset = assetMap.get(sourceClip.assetId);
      if (!asset?.previewUrl) return null;
      const sourceDuration = Math.max(1, clipSourceDurationMs(sourceClip));
      const analysisBucketCount = Math.ceil(sourceDuration / 20);
      const peaks = await loadWaveform(asset.previewUrl, analysisBucketCount);
      const startIndex = Math.floor(clamp((sourceClip.sourceStartMs || 0) / sourceDuration, 0, 1) * peaks.length);
      const endIndex = Math.max(startIndex + 1, Math.ceil(clamp(((sourceClip.sourceStartMs || 0) + sourceClip.durationMs * playbackSpeedMagnitude(sourceClip.speed)) / sourceDuration, 0, 1) * peaks.length));
      const relevant = peaks.slice(startIndex, endIndex);
      if (relevant.length < 3) return null;
      const smoothedPeaks = relevant.map((_, index) => {
        const from = Math.max(0, index - 1);
        const to = Math.min(relevant.length, index + 2);
        return relevant.slice(from, to).reduce((sum, peak) => sum + peak, 0) / (to - from);
      });
      const speechProtectedPeaks = smoothedPeaks.map((_, index) => Math.max(
        ...smoothedPeaks.slice(
          Math.max(0, index - preset.dilationBuckets),
          Math.min(smoothedPeaks.length, index + preset.dilationBuckets + 1),
        ),
      ));
      const sortedPeaks = [...smoothedPeaks].sort((a, b) => a - b);
      const quietLevel = sortedPeaks[Math.floor(sortedPeaks.length * 0.15)] || 0;
      const speechLevel = sortedPeaks[Math.floor(sortedPeaks.length * 0.85)] || Math.max(...smoothedPeaks);
      const threshold = clamp(
        quietLevel + (speechLevel - quietLevel) * preset.thresholdRatio,
        preset.minimumThreshold,
        preset.maximumThreshold,
      );
      const bucketMs = sourceClip.durationMs / relevant.length;
      const minimumPauseBuckets = Math.max(2, Math.ceil(preset.minimumPauseMs / bucketMs));
      const guardAfterSpeechBuckets = Math.max(1, Math.ceil(preset.guardAfterSpeechMs / bucketMs));
      const guardBeforeSpeechBuckets = Math.max(1, Math.ceil(preset.guardBeforeSpeechMs / bucketMs));
      const minimumCutBuckets = Math.max(1, Math.ceil(SILENCE_MINIMUM_CUT_MS / bucketMs));
      const cuts: Array<{ start: number; end: number }> = [];
      let silenceStart = -1;
      speechProtectedPeaks.forEach((peak, index) => {
        if (peak <= threshold && silenceStart < 0) silenceStart = index;
        if ((peak > threshold || index === relevant.length - 1) && silenceStart >= 0) {
          const silenceEnd = peak > threshold ? index : index + 1;
          if (silenceEnd - silenceStart >= minimumPauseBuckets) {
            const guardedStart = silenceStart === 0
              ? 0
              : Math.min(silenceEnd, silenceStart + guardAfterSpeechBuckets);
            const guardedEnd = silenceEnd === relevant.length
              ? relevant.length
              : Math.max(guardedStart, silenceEnd - guardBeforeSpeechBuckets);
            if (guardedEnd - guardedStart >= minimumCutBuckets) {
              cuts.push({ start: guardedStart, end: guardedEnd });
            }
          }
          silenceStart = -1;
        }
      });
      if (cuts.length === 0) return null;
      const speechRanges: Array<{ start: number; end: number }> = [];
      let cursor = 0;
      cuts.forEach(cut => {
        if (cut.start > cursor) speechRanges.push({ start: cursor, end: cut.start });
        cursor = cut.end;
      });
      if (cursor < relevant.length) speechRanges.push({ start: cursor, end: relevant.length });
      const usableRanges = speechRanges.filter(range => range.end > range.start);
      if (usableRanges.length === 0) return null;
      let timelineCursor = Math.round(sourceClip.startMs);
      const sourceSpeed = playbackSpeedMagnitude(sourceClip.speed);
      const pieces = usableRanges.map((range, index) => {
        const sourceOffsetMs = range.start * bucketMs * sourceSpeed;
        const pieceDuration = Math.max(1, Math.round((range.end - range.start) * bucketMs));
        const piece: StudioTimelineClip = {
          ...sourceClip,
          id: index === 0 ? sourceClip.id : newClipId(),
          name: usableRanges.length > 1 ? `${sourceClip.name} ${index + 1}` : sourceClip.name,
          startMs: timelineCursor,
          durationMs: pieceDuration,
          sourceStartMs: Math.round((sourceClip.sourceStartMs || 0) + sourceOffsetMs),
          fadeInMs: index === 0 ? sourceClip.fadeInMs : 0,
          fadeOutMs: index === usableRanges.length - 1 ? sourceClip.fadeOutMs : 0,
        };
        timelineCursor += pieceDuration;
        return piece;
      });
      return { sourceClip, clipId: sourceClip.id, pieces };
    }));
    const changes = analyzed.filter((item): item is {
      sourceClip: StudioTimelineClip;
      clipId: string;
      pieces: StudioTimelineClip[];
    } => Boolean(item));
    if (changes.length === 0) return 0;
    recordTimelineHistory();
    const byClipId = new Map(changes.map(change => [change.clipId, change.pieces]));
    const cutClips = sortClips(clips.flatMap(clip => byClipId.get(clip.id) || [clip]));
    const nextClips = mainTrackMagnetEnabled
      ? closeMainTrackAfterReplacement(cutClips, changes)
      : cutClips;
    const changedIds = new Set(changes.flatMap(change => change.pieces.map(piece => piece.id)));
    latestTimeline.current = { durationMs, clips: nextClips, layers, transitions };
    setClips(nextClips);
    setSelectedClipIds(nextClips.filter(clip => changedIds.has(clip.id)).map(clip => clip.id));
    return changes.length;
  }, [assetMap, clips, durationMs, layers, mainTrackMagnetEnabled, recordTimelineHistory, transitions]);

  const splitSelectedClip = () => {
    if (!selectedClip) return;
    if (layerSetting(layers, selectedClip.layer).locked) return;
    const offset = playheadMs - selectedClip.startMs;
    if (offset < 500 || offset > selectedClip.durationMs - 500) return;
    recordTimelineHistory();
    const first: StudioTimelineClip = { ...selectedClip, durationMs: Math.round(offset) };
    const second: StudioTimelineClip = {
      ...selectedClip,
      id: newClipId(),
      name: `${selectedClip.name} 2`,
      startMs: Math.round(playheadMs),
      durationMs: Math.round(selectedClip.durationMs - offset),
      sourceStartMs: Math.round((selectedClip.sourceStartMs || 0) + offset * playbackSpeedMagnitude(selectedClip.speed)),
      sourceDurationMs: selectedClip.sourceDurationMs || selectedClip.durationMs,
    };
    const nextClips = sortClips(clips.flatMap(clip => clip.id === selectedClip.id ? [first, second] : [clip]));
    latestTimeline.current = { durationMs, clips: nextClips, layers, transitions };
    setClips(nextClips);
    setSelectedClipIds([second.id]);
  };

  const handleLabProductChange = (value: string) => {
    setActiveLabProductId(value);
    if (!projectId) return;
    updateMutation.mutate({ labProductId: value || null }, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['studio', 'project', projectId] });
      },
    });
  };

  if (projectQuery.isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-surface-base">
        <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
      </div>
    );
  }

  if (projectQuery.isError || !project || !projectId) {
    return (
      <div className="h-screen w-screen bg-surface-base p-8">
        <div className="rounded-card border border-border bg-surface-raised">
          <EmptyState
            icon={Film}
            title="Nie udało się otworzyć projektu"
            description={projectQuery.isError ? errorMessage(projectQuery.error) : 'Projekt nie istnieje albo nie masz do niego dostępu.'}
            buttonLabel="Wróć do Studio"
            buttonTo="/studio"
            buttonIcon={ArrowLeft}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-screen overflow-hidden bg-surface-base grid grid-cols-[56px_300px_minmax(0,1fr)_300px]"
      style={{ gridTemplateRows: `48px minmax(0, 1fr) ${timelineHeight}px` }}
    >
      <div className="border-r border-b border-border-subtle bg-surface-raised/70">
        <StudioLogoMark />
      </div>

      <div className="col-span-3 h-12 px-3 border-b border-border-subtle bg-surface-raised/70 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to="/studio"
            onClick={persistTimelineBeforeExit}
            className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-hover border border-border-subtle"
            aria-label="Wróć do Studio"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="h-8 min-w-0 flex items-center gap-2">
            <Cloud className="w-4 h-4 text-accent-primary shrink-0" />
            <div className="min-w-0 leading-tight">
              <div className="flex items-center gap-1.5 min-w-0">
                <h1 className="text-sm font-semibold text-text-primary truncate">{project.name}</h1>
                {project.driveFolderUrl && (
                  <a
                    href={project.driveFolderUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex w-5 h-5 items-center justify-center rounded text-text-muted hover:text-accent-primary hover:bg-surface-hover shrink-0"
                    aria-label="Otwórz folder projektu na Dysku Google"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
              <p className="text-[10px] text-text-muted truncate">
                {project.driveFolderUrl ? 'Dysk Google aktywny' : 'Brak folderu Drive'}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <span className="text-xs text-text-muted">
              {timelineSaveError
                ? 'Nie zapisano zmian'
                : timelineSaving || updateMutation.isPending
                  ? 'Zapisywanie...'
                  : 'Zapisano automatycznie'}
            </span>
            <StudioIconButton
              label="Zapisz teraz"
              icon={<Save />}
              active={timelineSaving}
              onClick={saveTimelineNow}
              side="bottom"
            />
          </div>
          <Button size="sm" onClick={() => setExportOpen(true)} leftIcon={<FileDown />}>
            Eksportuj
          </Button>
        </div>
      </div>

      <div className="min-h-0 border-r border-border-subtle bg-surface-raised/70 py-2 flex flex-col gap-1">
        {PANEL_ITEMS.map(item => (
          <button
            key={item.key}
            type="button"
            onClick={() => setActivePanel(item.key)}
            className={`mx-1 px-1 py-2 rounded-lg text-[9px] leading-tight flex flex-col items-center gap-1 transition-colors ${
              activePanel === item.key
                ? 'bg-accent-primary/15 text-accent-primary border border-accent-primary/30'
                : 'text-text-muted hover:text-text-primary hover:bg-surface-hover border border-transparent'
            }`}
          >
            <item.icon className="w-4 h-4" />
            <span>{item.label}</span>
          </button>
        ))}
      </div>

      <div className="min-h-0 border-r border-border-subtle bg-surface-raised/40 p-3">
        {activePanel === 'media' && (
          <MediaPanel
            projectId={project.id}
            assets={assets}
            labProductId={activeLabProductId}
            onLabProductChange={handleLabProductChange}
            onAddAsset={addAssetToTimeline}
            onPreviewAsset={(asset) => {
              setIsPlaying(false);
              setPreviewAssetId(asset.id);
            }}
            previewAssetId={previewAssetId}
            onUploadDone={() => {}}
          />
        )}
        {activePanel === 'audio' && (
          <AudioPanel assets={assets} onAddAsset={addAssetToTimeline} />
        )}
        {activePanel === 'text' && (
          <TextPanel
            projectId={project.id}
            clips={clips}
            layers={layers}
            selectedClip={selectedClip}
            onAddText={addTextToTimeline}
            onUpdateClip={updateClip}
            onAddTranscriptions={addTranscriptionsToTimeline}
          />
        )}
        {activePanel === 'transitions' && (
          <TransitionsPanel />
        )}
        {activePanel === 'ai' && (
          <AiPanel
            projectId={project.id}
            assets={assets}
            labProductId={activeLabProductId}
            onLabProductChange={handleLabProductChange}
          />
        )}
      </div>

      <div className="min-h-0 bg-surface-base">
        {previewAsset ? (
          <SourceAssetPreview asset={previewAsset} onClose={() => setPreviewAssetId(null)} />
        ) : <PreviewPanel
          clip={activeClip}
          asset={activeAsset}
          playheadMs={playheadMs}
          isPlaying={isPlaying && !activeTimelineTransition}
          aspectRatio={aspectRatio}
          onAspectRatioChange={(value) => updateProjectSettings({ aspectRatio: value })}
          selectedClipId={selectedClipId}
          onSelectClip={(clipId) => {
            setSelectedTransitionId(null);
            setSelectedClipIds([clipId]);
          }}
          onClearSelection={() => setSelectedClipIds([])}
          onBeginEdit={recordTimelineHistory}
          onUpdateClip={updateClip}
          onTogglePlay={togglePlayback}
          muted={previewPlaybackMuted}
          backgroundClip={activeBackgroundClip && activeBackgroundClip.id !== activeClip?.id ? activeBackgroundClip : null}
          backgroundAsset={activeBackgroundClip && activeBackgroundClip.id !== activeClip?.id ? activeBackgroundAsset : null}
          activeTransition={activeTimelineTransition}
          transitionFromClip={transitionFromClip}
          transitionToClip={transitionToClip}
          transitionFromAsset={transitionFromAsset}
          transitionToAsset={transitionToAsset}
          playbackBuffering={isPlaybackPreparing || isPlaybackBuffering}
          onPlaybackBufferingChange={setIsPlaybackBuffering}
        />}
        {!previewAsset && clips
          .filter(clip => clip.kind === 'AUDIO' || (clip.kind === 'VIDEO' && !clip.audioDetached))
          .filter(clip => activeTimelineTransition || clip.kind === 'AUDIO' || clip.id !== previewPlaybackClip?.id)
          .map(clip => {
          const asset = assetMap.get(clip.assetId);
          if (!asset?.previewUrl) return null;
          const settings = layerSetting(layers, clip.layer);
          return (
            <AudioPlaybackTrack
              key={clip.id}
              clip={clip}
              asset={asset}
              playheadMs={playheadMs}
              isPlaying={isPlaying}
              muted={Boolean(settings.muted || settings.hidden)}
            />
          );
        })}
      </div>

      <div className="min-h-0 border-l border-border-subtle bg-surface-raised/40 p-3">
        <InspectorPanel
          selectedClip={selectedClip}
          selectedClips={selectedClips}
          selectedTransition={selectedTransition}
          onUpdateSelectedClips={updateSelectedClips}
          onUpdateTransition={updateTransition}
          onDeleteTransition={deleteTransition}
          onDetachAudio={detachAudio}
          onRemoveSilence={removeSilence}
          onUpdateTextClip={updateTextClip}
        />
      </div>

      <div className="relative col-span-4 min-h-0 bg-surface-base">
        <div
          role="separator"
          aria-label="Zmień wysokość osi czasu"
          aria-orientation="horizontal"
          onPointerDown={startTimelineResize}
          onDoubleClick={() => setTimelineHeight(330)}
          className="absolute inset-x-0 -top-1 z-50 h-2 cursor-ns-resize bg-transparent hover:bg-accent-primary/60"
        />
        <TimelinePanel
          key={project.id}
          clips={clips}
          assets={assets}
          layers={layers}
          transitions={transitions}
          selectedTransitionId={selectedTransitionId}
          selectedClipId={selectedClipId}
          selectedClipIds={selectedClipIds}
          copiedClip={copiedClip}
          playheadMs={playheadMs}
          durationMs={durationMs}
          pxPerSecond={timelineZoom}
          snapEnabled={snapEnabled}
          mainTrackMagnetEnabled={mainTrackMagnetEnabled}
          timelineSaveError={timelineSaveError}
          isPlaying={isPlaying}
          onSelectClip={(clipId) => {
            setPreviewAssetId(null);
            setSelectedClipIds(clipId ? [clipId] : []);
          }}
          onSelectClips={(clipIds) => setSelectedClipIds(Array.from(new Set(clipIds)))}
          onUpdateClip={updateClip}
          onUpdateClips={updateClips}
          onSetPlayhead={(value) => setPlayheadMs(clamp(value, 0, durationMs))}
          onTogglePlay={togglePlayback}
          onUndo={undoTimelineChange}
          onRedo={redoTimelineChange}
          onSplit={splitSelectedClip}
          onZoomIn={() => setTimelineZoom(value => clamp(value * 1.25, MIN_PX_PER_SECOND, MAX_PX_PER_SECOND))}
          onZoomOut={() => setTimelineZoom(value => clamp(value / 1.25, MIN_PX_PER_SECOND, MAX_PX_PER_SECOND))}
          onCopyClip={copyClip}
          onDuplicateClip={duplicateTimelineClip}
          onDetachAudio={detachAudio}
          onPasteClip={pasteClip}
          onDeleteClip={deleteClip}
          onDeleteSelectedClips={deleteClips}
          onUpdateLayer={updateLayer}
          onInsertLayer={insertLayer}
          onSelectTransition={setSelectedTransitionId}
          onAddTransition={addTransition}
          onDragStateChange={setIsDraggingClip}
          onToggleSnap={() => updateProjectSettings({ snapEnabled: !snapEnabled })}
          onToggleMainTrackMagnet={() => updateProjectSettings({ mainTrackMagnetEnabled: !mainTrackMagnetEnabled })}
          onBeginEdit={recordTimelineHistory}
          canUndo={historyPast.length > 0}
          canRedo={historyFuture.length > 0}
        />
      </div>
      <ExportProjectModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        project={{ id: project.id, name: project.name }}
        clips={clips}
        layers={layers}
        transitions={transitions}
        assets={assets}
        aspectRatio={aspectRatio}
      />
    </div>
  );
}
